import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useAuth } from "../lib/auth";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail, formatPrice, productImage } from "../lib/api";
import { toast } from "sonner";
import { BadgeCheck, Trash2, Edit3, Plus, Upload, Save, Palette, Check, Activity, TrendingUp, Users, ShoppingBag, Eye, AlertTriangle, Bell, Package, FileDown, GripVertical, Truck, MapPin, Mail, Receipt, Send, Sparkles, X, ShieldCheck, Image as ImageIcon, Search, Calendar, Filter, Download, Printer, RefreshCw, Layers, CheckSquare, Square, ExternalLink, MessageSquare, Star, ThumbsUp, ThumbsDown, Globe, Rocket, Wrench, Lock, Unlock, Clock, User, Languages, Handshake, Bot, Store, Tag } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useTheme } from "../lib/theme";
import { THEMES } from "../lib/themes";
import { CATEGORIES } from "../lib/categories";
import { generateReceipt, SAMPLE_RECEIPT_ORDER } from "../lib/receipt";
import { statusLabel, paymentStatusLabel, paymentMethodLabel, verificationLabel, roleLabel, ORDER_STATUS_LABELS, PAYMENT_STATUS_LABELS } from "../lib/labels";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import C807SampleLabelModal from "../components/C807SampleLabelModal";
import SociosAdmin from "../components/admin/SociosAdmin";
import VendorsBannerAdmin from "../components/admin/VendorsBannerAdmin";
import InventorySyncAdmin from "../components/admin/InventorySyncAdmin";
import CatalogoDiscordAdmin from "../components/admin/CatalogoDiscordAdmin";
import { HideByIdTool } from "../components/admin/HideByIdTool";
import VendorApplicationsAdmin from "../components/admin/VendorApplicationsAdmin";

export default function Admin() {
    const { user, loading } = useAuth();
    const nav = useNavigate();
    const [tab, setTab] = useState("dashboard");
    const [unread, setUnread] = useState(0);

    useEffect(() => {
        if (!loading && (!user || user.role !== "admin")) nav("/");
    }, [user, loading, nav]);

    useEffect(() => {
        if (!user || user.role !== "admin") return;
        const load = () => api.get("/notifications/unread-count").then(({ data }) => setUnread(data.count || 0)).catch(() => {});
        load();
        const iv = setInterval(load, 20000);
        return () => clearInterval(iv);
    }, [user, tab]);

    if (!user || user.role !== "admin") return null;

    const tabs = [
        { id: "dashboard", label: "Dashboard" },
        { id: "notificaciones", label: "Notificaciones" },
        { id: "inventario-especial", label: "Inventario Especial" },
        { id: "usuarios", label: "Usuarios" },
        { id: "ordenes", label: "Pedidos en tienda (SV)" },
        { id: "c807", label: "Envíos C807" },
        { id: "herramientas", label: "Herramientas" },
        { id: "ajustes", label: "Ajustes" },
    ];

    return (
        <div className="max-w-7xl mx-auto px-6 py-10 fade-in">
            <h1 className="font-display text-4xl mb-2">Panel Administrativo</h1>
            <p className="text-[var(--tb-text-2)] mb-8">Gestión completa de Terra Boutique</p>
            <div className="border-b border-[var(--tb-border)] flex gap-6 mb-8 overflow-x-auto">
                {tabs.map((t) => (
                    <button key={t.id} onClick={() => setTab(t.id)}
                        className={`pb-3 text-sm whitespace-nowrap flex items-center gap-2 ${tab === t.id ? "border-b-2 border-[var(--tb-gold)] text-[var(--tb-gold)]" : "text-[var(--tb-text-2)]"}`}
                        data-testid={`admin-tab-${t.id}`}>
                        {t.label}
                        {t.id === "notificaciones" && unread > 0 && (
                            <span className="bg-red-500 text-white text-xs rounded-full px-1.5 min-w-[18px] text-center font-bold" data-testid="notif-unread-badge">{unread}</span>
                        )}
                    </button>
                ))}
            </div>
            {tab === "dashboard" && <Dashboard />}
            {tab === "notificaciones" && <Notificaciones onChange={() => setUnread(0)} />}
            {tab === "inventario-especial" && <InventarioEspecial />}
            {tab === "usuarios" && <Usuarios />}
            {tab === "ordenes" && <OrdersKanban />}
            {tab === "c807" && <EnviosC807 />}
            {tab === "herramientas" && <Herramientas />}
            {tab === "ajustes" && <Ajustes />}
        </div>
    );
}

function ThemePicker({ label, flag, active, onPick, testid }) {
    return (
        <div data-testid={`theme-picker-${testid}`}>
            <div className="flex items-center gap-3 mb-3">
                <span className="text-2xl leading-none">{flag}</span>
                <div>
                    <h4 className="font-display text-xl">{label}</h4>
                    <p className="text-xs text-[var(--tb-text-2)]">Activo: <span className="text-[var(--tb-gold)]">{THEMES[active]?.name || "—"}</span></p>
                </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {Object.entries(THEMES).map(([key, t]) => (
                    <button key={key} onClick={() => onPick(key)}
                        className={`tb-card text-left p-4 transition ${active === key ? "ring-2 ring-[var(--tb-gold)]" : ""}`}
                        data-testid={`theme-${testid}-${key}`}>
                        <div className="flex items-center justify-between mb-2">
                            <div className="font-display text-base">{t.name}</div>
                            {active === key && <Check className="w-4 h-4 text-[var(--tb-gold)]" />}
                        </div>
                        <p className="text-[10px] text-[var(--tb-text-2)] mb-3 min-h-[2rem]">{t.description}</p>
                        <div className="flex gap-1.5">
                            {t.preview.map((c, i) => (
                                <div key={`p-${testid}-${c}-${i}`} className="flex-1 h-8 rounded-sm border border-black/10" style={{ background: c }} />
                            ))}
                        </div>
                    </button>
                ))}
            </div>
        </div>
    );
}

function Tema() {
    const { themeSv, setThemeSv, themeUs, setThemeUs } = useTheme();
    const saveSv = async (key) => {
        try { await setThemeSv(key); toast.success(`Tema SV: "${THEMES[key].name}" aplicado`); }
        catch (e) { toast.error("Error al guardar el tema SV"); }
    };
    const saveUs = async (key) => {
        try { await setThemeUs(key); toast.success(`Tema USA: "${THEMES[key].name}" aplicado`); }
        catch (e) { toast.error("Error al guardar el tema USA"); }
    };
    return (
        <div className="space-y-10">
            <div className="flex items-center gap-3">
                <Palette className="w-6 h-6 text-[var(--tb-gold)]" />
                <div>
                    <h3 className="font-display text-2xl">Paletas por país</h3>
                    <p className="text-sm text-[var(--tb-text-2)]">Define una paleta independiente para El Salvador y para Estados Unidos. La paleta cambia automáticamente al cambiar el país desde la barra superior.</p>
                </div>
            </div>
            <ThemePicker label="Tema · El Salvador" flag="🇸🇻" active={themeSv} onPick={saveSv} testid="sv" />
            <div className="border-t border-[var(--tb-border)]" />
            <ThemePicker label="Tema · Estados Unidos" flag="🇺🇸" active={themeUs} onPick={saveUs} testid="us" />
        </div>
    );
}

function Analytics() {
    const [d, setD] = useState(null);
    const [days, setDays] = useState(30);
    useEffect(() => { api.get(`/analytics?days=${days}`).then(({ data }) => setD(data)); }, [days]);
    if (!d) return <div>Cargando analítica...</div>;
    const maxDaily = Math.max(...d.daily.map(x => x.revenue), 1);
    return (
        <div className="space-y-8">
            <div className="flex items-center gap-3">
                <Activity className="w-6 h-6 text-[var(--tb-gold)]" />
                <h3 className="font-display text-2xl">Analítica del Sitio</h3>
                <div className="ml-auto flex gap-2">
                    {[7, 30, 90, 365].map((n) => (
                        <button key={n} onClick={() => setDays(n)}
                            className={`tb-badge cursor-pointer ${days === n ? "tb-badge-gold" : "tb-badge-muted"}`}>{n}d</button>
                    ))}
                </div>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                    { l: "Ingresos totales", v: formatPrice(d.revenue), icon: TrendingUp },
                    { l: "Ticket promedio", v: formatPrice(d.orders.avg_ticket), icon: ShoppingBag },
                    { l: "Pedidos pagados", v: d.orders.paid, icon: ShoppingBag },
                    { l: "Tasa conversión", v: d.funnel.conversion_rate + "%", icon: Activity },
                    { l: "Usuarios totales", v: d.users.total, icon: Users },
                    { l: "Nuevos en periodo", v: d.users.new_period, icon: Users },
                    { l: "Verificados", v: d.users.verified, icon: BadgeCheck },
                    { l: "Vistas totales", v: d.funnel.views, icon: Eye },
                ].map((k) => (
                    <div key={k.l} className="tb-card p-5">
                        <k.icon className="w-4 h-4 text-[var(--tb-gold)] mb-2" />
                        <div className="tb-overline text-[var(--tb-muted)]">{k.l}</div>
                        <div className="text-2xl font-display text-[var(--tb-text)] mt-1">{k.v}</div>
                    </div>
                ))}
            </div>

            {/* Daily chart */}
            <div className="tb-card p-5">
                <div className="tb-overline text-[var(--tb-gold)] mb-3">Ingresos por día (últimos {days}d)</div>
                {d.daily.length === 0 ? <div className="text-[var(--tb-muted)] text-sm">Sin pedidos en el periodo.</div> : (
                    <div className="flex items-end gap-1 h-40">
                        {d.daily.map((day) => (
                            <div key={day.date} className="flex-1 flex flex-col items-center gap-1" title={`${day.date}: $${day.revenue} (${day.orders} pedidos)`}>
                                <div className="w-full bg-[var(--tb-gold)] rounded-t" style={{ height: `${(day.revenue / maxDaily) * 100}%`, minHeight: day.orders > 0 ? "4px" : "0" }} />
                                <div className="text-[10px] text-[var(--tb-muted)] rotate-45 origin-left translate-y-2 truncate w-8">{day.date.slice(5)}</div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="grid md:grid-cols-2 gap-6">
                {/* Top viewed */}
                <div className="tb-card p-5">
                    <div className="tb-overline text-[var(--tb-gold)] mb-3 flex items-center gap-2"><Eye className="w-4 h-4" /> Productos más vistos</div>
                    <div className="space-y-2">
                        {d.top_viewed.slice(0, 6).map((p) => (
                            <div key={p.sku} className="flex justify-between text-sm border-b border-[var(--tb-border)] pb-1">
                                <span className="truncate">{p.nombre} <span className="text-[var(--tb-muted)]">· {p.sku}</span></span>
                                <span className="text-[var(--tb-gold)]">{p.views} vistas</span>
                            </div>
                        ))}
                    </div>
                </div>
                {/* Top sold */}
                <div className="tb-card p-5">
                    <div className="tb-overline text-[var(--tb-gold)] mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Más vendidos</div>
                    {d.top_sold.length === 0 ? <div className="text-[var(--tb-muted)] text-sm">Aún no hay ventas.</div> :
                        <div className="space-y-2">
                            {d.top_sold.slice(0, 6).map((p) => (
                                <div key={p.sku} className="flex justify-between text-sm border-b border-[var(--tb-border)] pb-1">
                                    <span className="truncate">{p.nombre}</span>
                                    <span className="text-[var(--tb-gold)]">{p.qty_sold} uds</span>
                                </div>
                            ))}
                        </div>
                    }
                </div>
                {/* Payment methods */}
                <div className="tb-card p-5">
                    <div className="tb-overline text-[var(--tb-gold)] mb-3">Métodos de pago</div>
                    <div className="space-y-2">
                        {d.payment_methods.map((p) => (
                            <div key={p.method} className="flex justify-between text-sm">
                                <span className="capitalize">{p.method}</span>
                                <span><span className="text-[var(--tb-text-2)] mr-3">{p.count} pedidos</span><span className="text-[var(--tb-gold)]">{formatPrice(p.revenue)}</span></span>
                            </div>
                        ))}
                    </div>
                </div>
                {/* Inventory health */}
                <div className="tb-card p-5">
                    <div className="tb-overline text-[var(--tb-gold)] mb-3">Inventario</div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                        <div><div className="text-2xl font-display text-[var(--tb-text)]">{d.inventory.available}</div><div className="text-xs text-[var(--tb-muted)]">Disponibles</div></div>
                        <div><div className="text-2xl font-display text-[var(--tb-text)]">{d.inventory.sold}</div><div className="text-xs text-[var(--tb-muted)]">Vendidos</div></div>
                        <div><div className="text-2xl font-display text-orange-400">{d.inventory.low_stock}</div><div className="text-xs text-[var(--tb-muted)]">Stock bajo (≤2)</div></div>
                        <div><div className="text-2xl font-display text-[var(--tb-text)]">{d.tickets.open + d.tickets.in_process}</div><div className="text-xs text-[var(--tb-muted)]">Tickets activos</div></div>
                    </div>
                </div>
            </div>

            {/* Activity feed */}
            <div className="tb-card p-5">
                <div className="tb-overline text-[var(--tb-gold)] mb-3">Actividad reciente</div>
                <div className="space-y-2 max-h-96 overflow-auto">
                    {d.activity.map((a, i) => (
                        <div key={`${a.at || ""}-${a.type}-${i}`} className="text-sm flex gap-3 py-1.5 border-b border-[var(--tb-border)]">
                            <span className={`tb-badge ${a.type === "order" ? "tb-badge-gold" : a.type === "user" ? "tb-badge-success" : "tb-badge-warm"} text-[10px]`}>{a.type}</span>
                            <span className="text-[var(--tb-text)] flex-1">{a.text}</span>
                            <span className="text-xs text-[var(--tb-muted)] whitespace-nowrap">{a.at ? new Date(a.at).toLocaleString("es-SV", { dateStyle: "short", timeStyle: "short" }) : ""}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function Notificaciones({ onChange }) {
    const [list, setList] = useState([]);
    const [filter, setFilter] = useState("all");
    const [selected, setSelected] = useState(null);

    const load = async () => {
        const { data } = await api.get("/notifications");
        setList(data);
    };
    useEffect(() => { load(); }, []);

    const markRead = async (nid) => {
        await api.post(`/notifications/${nid}/read`);
        load();
    };
    const markAll = async () => {
        await api.post("/notifications/mark-all-read");
        load(); onChange?.();
        toast.success("Todas marcadas como leídas");
    };
    const updateStatus = async (orderId, status) => {
        await api.put(`/orders/${orderId}/status`, { status });
        toast.success(`Pedido marcado: ${status}`);
        if (selected) setSelected({ ...selected, _orderStatus: status });
        load();
    };

    // When selecting an order notification, fetch order details
    useEffect(() => {
        if (!selected || !selected.order_id) return;
        api.get(`/orders/${selected.order_id}`).then(({ data }) => {
            setSelected((cur) => cur ? {
                ...cur,
                _paymentConfirmed: !!data.payment_confirmed,
                _orderStatus: data.status,
                _c807_guia: data.c807_guia || cur.c807_guia || "",
                _canCreateGuide: !!cur.can_create_guide && !data.c807_guia,
                _transferTicketId: data.transfer_ticket_id || "",
            } : cur);
        }).catch(() => {});
    }, [selected?._id]);

    const createGuide = async () => {
        if (!selected?.order_id) return;
        try {
            const { data } = await api.post(`/admin/orders/${selected.order_id}/create-guide`, {});
            toast.success(`Guía creada: ${data.guia_numero} · email enviado al cliente`);
            setSelected((cur) => cur ? { ...cur, _c807_guia: data.guia_numero, _canCreateGuide: false, has_guide: true, c807_guia: data.guia_numero } : cur);
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error creando la guía");
        }
    };

    const visible = list.filter((n) => filter === "all" || (filter === "unread" && !n.read));
    const typeLabel = {
        order_apartado: "APARTADO", order_shipping: "PEDIDO + ENVÍO", order_created: "PEDIDO",
        product_request: "SOLICITUD", incoming_reservation: "APARTADO EN CAMINO",
        verify_request: "VERIFICACIÓN", ticket: "TICKET",
    };
    const typeColor = {
        order_apartado: "bg-amber-500/20 text-amber-300",
        order_shipping: "bg-blue-500/20 text-blue-300",
        order_created: "bg-emerald-500/20 text-emerald-300",
        product_request: "bg-purple-500/20 text-purple-300",
        incoming_reservation: "bg-pink-500/20 text-pink-300",
        verify_request: "bg-cyan-500/20 text-cyan-300",
        ticket: "bg-gray-500/20 text-gray-300",
    };

    const isOrderNotif = selected && !!selected.order_id && Array.isArray(selected.items);

    return (
        <div className="grid md:grid-cols-3 gap-6">
            <div className="md:col-span-1 space-y-3">
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2"><Bell className="w-5 h-5 text-[var(--tb-gold)]" /><span className="font-display text-xl">Bandeja</span></div>
                    <button onClick={markAll} className="text-xs tb-link" data-testid="notif-mark-all">Marcar todas</button>
                </div>
                <div className="flex gap-2 mb-2">
                    {["all", "unread"].map((f) => (
                        <button key={f} onClick={() => setFilter(f)} className={`tb-badge cursor-pointer ${filter === f ? "tb-badge-gold" : "tb-badge-muted"}`}>{f === "all" ? "Todas" : "No leídas"}</button>
                    ))}
                </div>
                <div className="space-y-2 max-h-[70vh] overflow-auto pr-1">
                    {visible.length === 0 ? <div className="tb-card p-4 text-center text-sm text-[var(--tb-muted)]">Sin notificaciones.</div> : visible.map((n) => (
                        <button key={n._id} onClick={() => { setSelected(n); if (!n.read) markRead(n._id); }}
                            className={`tb-card w-full text-left p-4 transition relative ${selected?._id === n._id ? "!border-[var(--tb-gold)]" : ""} ${
                                n.read
                                    ? "opacity-60 bg-transparent"
                                    : "bg-[var(--tb-border-gold)]/8 border-l-4 !border-l-[var(--tb-gold)]"
                            }`}
                            data-testid={`notif-${n._id}`}>
                            <div className="flex justify-between items-start gap-2 mb-1">
                                <span className={`text-[10px] px-2 py-0.5 rounded font-medium ${typeColor[n.type] || "bg-gray-500/20 text-gray-300"}`}>{typeLabel[n.type] || n.type?.toUpperCase()}</span>
                                {!n.read && <span className="w-2 h-2 bg-[var(--tb-gold)] rounded-full mt-1.5" />}
                            </div>
                            <div className={`font-medium text-sm ${n.read ? "text-[var(--tb-text-2)]" : "text-[var(--tb-text)]"}`}>
                                {n.customer_name || n.message || "—"}
                            </div>
                            {n.items_summary && <div className="text-xs text-[var(--tb-text-2)] truncate">{n.items_summary}</div>}
                            <div className="flex justify-between mt-2 text-xs">
                                <span className="text-[var(--tb-muted)]">{n.created_at ? new Date(n.created_at).toLocaleString("es-SV", { dateStyle: "short", timeStyle: "short" }) : ""}</span>
                                {n.total !== undefined && <span className="text-[var(--tb-gold)] font-semibold">{formatPrice(n.total)}</span>}
                            </div>
                        </button>
                    ))}
                </div>
            </div>

            <div className="md:col-span-2">
                {!selected ? (
                    <div className="tb-card p-12 text-center text-[var(--tb-muted)]">Selecciona una notificación para ver el detalle completo.</div>
                ) : isOrderNotif ? (
                    <div className="tb-card p-6 sticky top-24" data-testid="notif-detail">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-display text-2xl">Detalle del Pedido</h3>
                            <span className={`text-xs px-2 py-1 rounded font-medium ${typeColor[selected.type] || ""}`}>{typeLabel[selected.type] || selected.type}</span>
                        </div>
                        <div className="space-y-3 mb-5">
                            <div>
                                <div className="tb-overline text-[var(--tb-muted)]">ID del pedido</div>
                                <div className="font-mono text-lg text-[var(--tb-gold)]" data-testid="notif-order-id">{selected.order_id}</div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div><div className="tb-overline text-[var(--tb-muted)]">Cliente</div><div>{selected.customer_name}</div></div>
                                <div><div className="tb-overline text-[var(--tb-muted)]">Correo</div><div className="text-sm">{selected.user_email}</div></div>
                                <div><div className="tb-overline text-[var(--tb-muted)]">Teléfono</div><div>{selected.customer_phone || "—"}</div></div>
                                <div><div className="tb-overline text-[var(--tb-muted)]">Método de pago</div><div className="capitalize">{selected.payment_method}</div></div>
                            </div>
                            <div>
                                <div className="tb-overline text-[var(--tb-muted)]">{selected.needs_shipping ? "Dirección de envío" : "Retiro en tienda"}</div>
                                <div className="text-sm">{selected.needs_shipping ? (selected.shipping_address || "No especificada") : "El cliente recoge en la boutique"}</div>
                            </div>
                            <div>
                                <div className="tb-overline text-[var(--tb-muted)]">Productos</div>
                                <div className="space-y-1 mt-1">
                                    {(selected.items || []).map((it, i) => (
                                        <div key={`${it.sku}-${i}`} className="flex justify-between text-sm border-b border-[var(--tb-border)] py-1">
                                            <span>{it.nombre} <span className="text-[var(--tb-muted)] font-mono">{it.sku}</span> × {it.quantity}</span>
                                            <span>{formatPrice(it.subtotal)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            {selected.notes && (
                                <div><div className="tb-overline text-[var(--tb-muted)]">Notas del cliente</div><div className="text-sm">{selected.notes}</div></div>
                            )}
                            <div className="flex justify-between text-xl pt-2 border-t border-[var(--tb-border)]">
                                <span>Total</span><span className="text-[var(--tb-gold)] font-semibold">{formatPrice(selected.total)}</span>
                            </div>
                        </div>
                        {/* Estado visual claro (read-only). El admin gestiona el ciclo del pedido
                            desde "Pedidos en tienda", "Envíos C807" o "Etiquetas USA" — no desde aquí. */}
                        <div className="mb-4 p-3 border rounded flex items-center gap-3 flex-wrap"
                             style={{
                                 borderColor: selected._paymentConfirmed || selected.payment_method === "apartado"
                                     ? "rgba(16,185,129,0.5)" : "rgba(251,191,36,0.5)",
                                 background: selected._paymentConfirmed || selected.payment_method === "apartado"
                                     ? "rgba(16,185,129,0.08)" : "rgba(251,191,36,0.08)",
                             }}>
                            {selected.payment_method === "apartado" ? (
                                <>
                                    <span className="text-2xl">💰</span>
                                    <div>
                                        <div className="font-semibold text-amber-300 text-sm">APARTADO — pago en EFECTIVO al recoger</div>
                                        <div className="text-[11px] text-[var(--tb-text-2)]">El cliente reservó este producto. Cobra al momento del retiro.</div>
                                    </div>
                                </>
                            ) : selected._paymentConfirmed ? (
                                <>
                                    <span className="text-2xl">✅</span>
                                    <div>
                                        <div className="font-semibold text-emerald-300 text-sm">PAGO EXITOSO · {String(selected.payment_method || "").toUpperCase()}</div>
                                        <div className="text-[11px] text-[var(--tb-text-2)]">El pago fue procesado correctamente. Procede al envío/entrega según el panel correspondiente.</div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <span className="text-2xl">⏳</span>
                                    <div>
                                        <div className="font-semibold text-amber-300 text-sm">Pendiente de confirmación de pago</div>
                                        <div className="text-[11px] text-[var(--tb-text-2)]">Verifica el pago manualmente antes de proceder.</div>
                                    </div>
                                </>
                            )}
                            {selected._orderStatus && (
                                <span className="ml-auto text-[10px] px-2 py-1 rounded bg-[var(--tb-bg-2)]/60 uppercase tracking-wider font-mono">
                                    {selected._orderStatus}
                                </span>
                            )}
                        </div>

                        <div className="flex gap-2 flex-wrap">
                            <button onClick={async () => {
                                try {
                                    const [order, settings] = await Promise.all([
                                        api.get(`/orders/${selected.order_id}`).then(r => r.data),
                                        api.get("/settings").then(r => r.data),
                                    ]);
                                    await generateReceipt(order, settings);
                                    toast.success("Recibo descargado");
                                } catch (e) { toast.error("Error al generar recibo"); }
                            }} className="tb-btn-primary text-xs inline-flex items-center gap-1.5" data-testid="action-receipt">
                                <FileDown className="w-3.5 h-3.5" /> Imprimir / Descargar recibo (PDF)
                            </button>

                            {/* Create guide button — appears once payment is confirmed on SV shipping orders */}
                            {selected._canCreateGuide && selected._paymentConfirmed && (
                                <button onClick={createGuide}
                                    className="tb-btn-secondary text-xs inline-flex items-center gap-1.5 !text-emerald-300 !border-emerald-500/50 hover:!bg-emerald-500/10"
                                    data-testid="action-create-guide"
                                    title="Genera la guía C807 con los datos ya provistos por el cliente y envía el número por correo automáticamente">
                                    <Printer className="w-3.5 h-3.5" /> Crear guía C807
                                </button>
                            )}

                            {selected._c807_guia && (
                                <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 text-xs">
                                    <span className="opacity-70">Guía:</span>
                                    <span className="font-mono font-semibold">{selected._c807_guia}</span>
                                </div>
                            )}
                        </div>
                        <p className="text-xs text-[var(--tb-muted)] mt-3">
                            El estado del pedido se gestiona desde su panel dedicado:
                            <span className="text-[var(--tb-gold)]"> Pedidos en tienda</span> (retiros SV),
                            <span className="text-[var(--tb-gold)]"> Envíos C807</span> (envíos SV),
                            <span className="text-[var(--tb-gold)]"> Etiquetas USA</span> (envíos US).
                        </p>
                    </div>
                ) : (
                    /* Generic notification detail (product_request, incoming_reservation, verify_request, etc.) */
                    <div className="tb-card p-6" data-testid="notif-detail-generic">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-display text-2xl">Detalle</h3>
                            <span className={`text-xs px-2 py-1 rounded font-medium ${typeColor[selected.type] || ""}`}>{typeLabel[selected.type] || selected.type}</span>
                        </div>
                        <p className="text-base mb-4">{selected.message}</p>
                        <div className="text-xs text-[var(--tb-muted)] mb-4">
                            {selected.created_at && new Date(selected.created_at).toLocaleString("es-SV")}
                        </div>
                        {selected.data && (
                            <div className="bg-[var(--tb-bg-2)]/40 p-3 rounded text-xs space-y-1 mb-4">
                                {Object.entries(selected.data).map(([k, v]) => (
                                    <div key={k}><span className="text-[var(--tb-muted)]">{k}:</span> <span className="font-mono">{String(v)}</span></div>
                                ))}
                            </div>
                        )}
                        <div className="flex gap-2 flex-wrap">
                            {selected.type === "product_request" && selected.data?.request_id && (
                                <a href="#" onClick={(e) => { e.preventDefault(); window.location.hash = ""; const u = new URL(window.location); u.searchParams.set("tab", "solicitudes"); window.location.assign(u); }} className="tb-btn-primary text-xs">Ver en Solicitudes</a>
                            )}
                            {selected.type === "incoming_reservation" && (
                                <a href="#" onClick={(e) => { e.preventDefault(); const u = new URL(window.location); u.searchParams.set("tab", "incoming"); window.location.assign(u); }} className="tb-btn-primary text-xs">Ver en Productos en Camino</a>
                            )}
                            {selected.type === "verify_request" && (
                                <a href="#" onClick={(e) => { e.preventDefault(); const u = new URL(window.location); u.searchParams.set("tab", "usuarios"); window.location.assign(u); }} className="tb-btn-primary text-xs">Ver usuario</a>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function Dashboard() {
    const [s, setS] = useState(null);
    useEffect(() => { api.get("/stats").then(({ data }) => setS(data)); }, []);
    if (!s) return <div>Cargando estadísticas...</div>;
    const cards = [
        ["Ingresos", formatPrice(s.revenue)], ["Pedidos", s.total_orders],
        ["Pagados", s.paid_orders], ["Apartados", s.apartados],
        ["Productos", s.total_products], ["Vendidos", s.sold],
        ["Usuarios", s.total_users], ["Tickets abiertos", s.open_tickets],
    ];
    return (
        <div className="space-y-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {cards.map(([l, v]) => (
                    <div key={l} className="tb-card p-5">
                        <div className="tb-overline text-[var(--tb-muted)]">{l}</div>
                        <div className="text-2xl font-display text-[var(--tb-gold)] mt-2">{v}</div>
                    </div>
                ))}
            </div>
            <div>
                <div className="tb-overline text-[var(--tb-gold)] mb-3">Productos más vistos</div>
                <div className="grid md:grid-cols-5 gap-3">
                    {s.top_viewed.map((p) => (
                        <div key={p.sku} className="tb-card p-3 text-sm">
                            <div className="truncate font-medium">{p.nombre}</div>
                            <div className="text-xs text-[var(--tb-text-2)]">{p.views} vistas</div>
                        </div>
                    ))}
                </div>
            </div>
            <div>
                <div className="tb-overline text-[var(--tb-gold)] mb-3">Actividad reciente</div>
                <div className="space-y-2">
                    {s.recent_orders.map((o) => (
                        <div key={o._id} className="tb-card p-3 flex justify-between text-sm">
                            <span>{o.user_email}</span><span className="text-[var(--tb-text-2)]">{o.status}</span><span className="text-[var(--tb-gold)]">{formatPrice(o.total)}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function Productos() {
    const [list, setList] = useState([]);
    const [editing, setEditing] = useState(null);
    const [showClear, setShowClear] = useState(false);
    const [clearForm, setClearForm] = useState({ password: "", confirm: "" });
    const [search, setSearch] = useState("");
    // Server-side pagination — the list can grow to hundreds of products;
    // loading them all at once would spike admin panel memory + wire cost.
    const [offset, setOffset] = useState(0);
    const [total, setTotal] = useState(0);
    const PAGE_SIZE = 24;
    const blank = { nombre: "", descripcion: "", precio_compra: 0, precio_venta: 0, foto: "", estado: "Disponible", condicion: "Nuevo", categoria: "General", cantidad_disponible: 0, cantidad_sv: 1, cantidad_us: 0, availability: ["SV"], weight_oz: 8, length_in: 6, width_in: 4, height_in: 2 };

    const onPickPhoto = (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) { toast.error("Solo imágenes"); return; }
        if (file.size > 5 * 1024 * 1024) { toast.error("La imagen supera 5MB"); return; }
        const img = new Image();
        const reader = new FileReader();
        reader.onload = (ev) => {
            img.onload = () => {
                const canvas = document.createElement("canvas");
                const max = 1200; // product photo — keep more detail
                const scale = Math.min(1, max / Math.max(img.width, img.height));
                canvas.width = Math.round(img.width * scale);
                canvas.height = Math.round(img.height * scale);
                canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
                setEditing((cur) => ({ ...cur, foto: canvas.toDataURL("image/jpeg", 0.85), foto_url: "" }));
            };
            img.src = ev.target.result;
        };
        reader.readAsDataURL(file);
    };
    // Load current page. `q` piggybacks on the list endpoint's server-side
    // search so we don't have to fetch every product to filter client-side.
    const load = useCallback(async (nextOffset = offset, q = search) => {
        const params = { limit: PAGE_SIZE, offset: nextOffset };
        const term = (q || "").trim();
        if (term) params.q = term;
        try {
            const res = await api.get("/products", { params });
            setList(res.data);
            const totalHeader = res.headers?.["x-total-count"] || res.headers?.["X-Total-Count"];
            setTotal(totalHeader ? parseInt(totalHeader, 10) : res.data.length);
            setOffset(nextOffset);
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error"); }
    }, [offset, search]);
    // Initial load
    useEffect(() => { load(0, ""); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
    // Debounced re-fetch when the search term changes → always resets to page 0.
    useEffect(() => {
        const timer = setTimeout(() => { load(0, search); }, 300);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);
    const page = Math.floor(offset / PAGE_SIZE) + 1;
    const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const goToPage = (n) => {
        const clamped = Math.max(1, Math.min(pageCount, n));
        load((clamped - 1) * PAGE_SIZE, search);
    };
    const save = async () => {
        try {
            const payload = { ...editing };
            payload.precio_minimo = payload.precio_venta; // final price; no min markdown
            if (editing.sku && list.find((p) => p.sku === editing.sku)) {
                await api.put(`/products/${editing.sku}`, payload);
            } else {
                await api.post("/products", payload);
            }
            toast.success("Guardado");
            setEditing(null); load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    const del = async (sku) => {
        if (!window.confirm(`¿Eliminar ${sku}?`)) return;
        await api.delete(`/products/${sku}`); toast.success("Eliminado"); load();
    };
    const clearAll = async () => {
        try {
            const { data } = await api.post("/admin/clear-inventory", clearForm);
            toast.success(`Inventario limpiado: ${data.deleted} productos eliminados`);
            setShowClear(false); setClearForm({ password: "", confirm: "" });
            load(0, search);
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    return (
        <div>
            <div className="flex flex-wrap gap-3 mb-4">
                <button onClick={() => setEditing({ ...blank })} className="tb-btn-primary inline-flex items-center gap-2" data-testid="admin-new-product">
                    <Plus className="w-4 h-4" /> Nuevo Producto
                </button>
                <button onClick={() => setShowClear(true)} className="tb-btn-secondary !text-red-400 !border-red-400 hover:!bg-red-500/10 inline-flex items-center gap-2" data-testid="admin-clear-inventory">
                    <AlertTriangle className="w-4 h-4" /> Limpiar inventario
                </button>
                <div className="ml-auto text-sm text-[var(--tb-text-2)] self-center" data-testid="admin-products-count">
                    {total} producto{total === 1 ? "" : "s"}
                    {pageCount > 1 && (
                        <span className="ml-2 text-[var(--tb-muted)]">· Pág {page} / {pageCount}</span>
                    )}
                </div>
            </div>
            <div className="mb-3 relative max-w-md">
                <Search className="w-4 h-4 text-[var(--tb-muted)] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Buscar por nombre o ID (SKU)..."
                    className="tb-input !pl-9 w-full"
                    data-testid="admin-products-search"
                />
                {search && (
                    <button
                        onClick={() => setSearch("")}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--tb-muted)] hover:text-[var(--tb-gold)] text-xs px-2"
                        data-testid="admin-products-search-clear"
                        title="Limpiar"
                    >
                        ×
                    </button>
                )}
            </div>
            <div className="overflow-x-auto tb-card">
                <table className="w-full text-sm">
                    <thead className="border-b border-[var(--tb-border)] text-left text-[var(--tb-text-2)]">
                        <tr><th className="p-3">SKU</th><th>Nombre</th><th>Precio venta</th><th>Estado</th><th>Stock por país</th><th>Disponible en</th><th></th></tr>
                    </thead>
                    <tbody>
                        {list.length === 0 && (
                            <tr><td colSpan={7} className="p-6 text-center text-[var(--tb-muted)] text-sm" data-testid="admin-products-empty">
                                {search ? "Sin coincidencias para tu búsqueda" : "Sin productos"}
                            </td></tr>
                        )}
                        {list.map((p) => (
                            <tr key={p.sku} className="border-b border-[var(--tb-border)]">
                                <td className="p-3 font-mono">{p.sku}</td>
                                <td>{p.nombre}</td>
                                <td className="text-[var(--tb-gold)]">{formatPrice(p.precio_venta)}</td>
                                <td>{p.estado}</td>
                                <td>
                                    <div className="text-xs">
                                        {(p.availability || ["SV"]).includes("SV") && (
                                            <div title="Stock El Salvador">🇸🇻 <span className="font-semibold">{p.cantidad_sv ?? 0}</span></div>
                                        )}
                                        {(p.availability || ["SV"]).includes("US") && (
                                            <div title="Stock Estados Unidos">🇺🇸 <span className="font-semibold">{p.cantidad_us ?? 0}</span></div>
                                        )}
                                        <div className="text-[10px] text-[var(--tb-muted)]">Σ {p.cantidad_disponible}</div>
                                    </div>
                                </td>
                                <td className="text-xs">
                                    {(p.availability || ["SV"]).map((c) => (
                                        <span key={c} className="inline-block mr-1" title={c === "SV" ? "El Salvador" : "Estados Unidos"}>
                                            {c === "SV" ? "🇸🇻" : "🇺🇸"}
                                        </span>
                                    ))}
                                </td>
                                <td className="p-3 flex gap-2">
                                    <button onClick={async () => {
                                        // The list endpoint returns a lightweight projection (no `descripcion`,
                                        // images, videos, etc.) for perf. Fetch the full document before opening
                                        // the edit modal so those fields aren't wiped on save.
                                        try {
                                            const { data } = await api.get(`/products/${p.sku}`);
                                            setEditing(data);
                                        } catch (e) {
                                            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo cargar el producto");
                                        }
                                    }} className="tb-link" data-testid={`edit-${p.sku}`}><Edit3 className="w-4 h-4" /></button>
                                    <button onClick={() => del(p.sku)} className="tb-link" data-testid={`del-${p.sku}`}><Trash2 className="w-4 h-4" /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {pageCount > 1 && (
                <div className="flex items-center justify-center gap-2 mt-4" data-testid="admin-products-pagination">
                    <button
                        onClick={() => goToPage(page - 1)}
                        disabled={page <= 1}
                        className="tb-btn-secondary !text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid="admin-products-page-prev">
                        ← Anterior
                    </button>
                    <span className="text-xs text-[var(--tb-text-2)] px-3">Página {page} de {pageCount}</span>
                    <button
                        onClick={() => goToPage(page + 1)}
                        disabled={page >= pageCount}
                        className="tb-btn-secondary !text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid="admin-products-page-next">
                        Siguiente →
                    </button>
                </div>
            )}

            {editing && (
                <div className="tb-modal-backdrop" onClick={(e) => e.target === e.currentTarget && setEditing(null)}>
                    <div className="tb-modal-content">
                        <h3 className="font-display text-2xl mb-1">{editing.sku && list.find((x) => x.sku === editing.sku) ? `Editar ${editing.sku}` : "Nuevo Producto"}</h3>
                        <p className="text-xs text-[var(--tb-text-2)] mb-5">Completa los datos. Los campos con * son requeridos. Todos los precios en USD ($).</p>
                        <div className="grid grid-cols-2 gap-3">
                            <label className="col-span-2">
                                <span className="tb-overline text-[var(--tb-gold)]">ID / SKU del producto</span>
                                <div className="mt-1 flex gap-2 items-start">
                                    <input className="tb-input font-mono flex-1" placeholder="Ej. T-0150 — déjalo vacío y se asignará automáticamente"
                                        value={editing.sku || ""}
                                        disabled={!!(editing.sku && list.find((x) => x.sku === editing.sku))}
                                        onChange={(e) => setEditing({ ...editing, sku: e.target.value.toUpperCase().trim() })}
                                        data-testid="prod-sku" />
                                    {editing.sku && list.find((x) => x.sku === editing.sku) && (
                                        <button type="button"
                                            onClick={async () => {
                                                const nuevo = window.prompt(
                                                    `Cambiar SKU de "${editing.sku}"\n\nNuevo SKU (mismo formato T-XXXX, en mayúsculas):`,
                                                    editing.sku
                                                );
                                                if (!nuevo || nuevo.trim().toUpperCase() === editing.sku) return;
                                                const newSku = nuevo.trim().toUpperCase();
                                                if (!/^[A-Z0-9\-]{2,32}$/.test(newSku)) {
                                                    toast.error("SKU inválido. Usa solo letras, números y guiones (máx 32 caracteres). Ej: T-0100");
                                                    return;
                                                }
                                                if (!window.confirm(
                                                    `¿Cambiar "${editing.sku}" → "${newSku}"?\n\n` +
                                                    `Esto actualizará el SKU en TODAS las colecciones (producto, pedidos, carritos, reseñas, cajas).\n\n` +
                                                    `⚠️ IMPORTANTE: si tienes el mismo producto en tu bot de Discord, tendrás que renombrarlo allí también (manualmente) para que la sincronización siga funcionando.`
                                                )) return;
                                                try {
                                                    const { data } = await api.post(`/admin/products/${editing.sku}/rename`, { new_sku: newSku });
                                                    toast.success(`SKU cambiado a ${data.new_sku}. Actualizado en: productos (${data.cascaded.products}), pedidos (${data.cascaded.orders}), carritos (${data.cascaded.cart_items}), reseñas (${data.cascaded.reviews})`);
                                                    // Refresh the edit modal + list
                                                    const { data: fresh } = await api.get(`/products/${data.new_sku}`);
                                                    setEditing(fresh);
                                                    load();
                                                } catch (e) {
                                                    toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error renombrando SKU");
                                                }
                                            }}
                                            className="tb-btn-secondary !text-xs whitespace-nowrap inline-flex items-center gap-1"
                                            data-testid="prod-rename-sku"
                                            title="Cambiar el SKU de este producto">
                                            <RefreshCw className="w-3.5 h-3.5" /> Renombrar
                                        </button>
                                    )}
                                </div>
                                <span className="text-xs text-[var(--tb-muted)] mt-1 block">
                                    {editing.sku && list.find((x) => x.sku === editing.sku)
                                        ? "Usa el botón Renombrar para cambiar el SKU (cascada automática a pedidos, carritos y reseñas)."
                                        : "Si tienes un inventario externo con IDs propios, escríbelo aquí. Si lo dejas vacío, el sistema asigna automáticamente (T-XXXX)."}
                                </span>
                            </label>
                            <label className="col-span-2"><span className="tb-overline text-[var(--tb-gold)]">Nombre del producto *</span><input className="tb-input mt-1" placeholder="Ej: Bolso Negro Premium" value={editing.nombre} onChange={(e) => setEditing({ ...editing, nombre: e.target.value })} data-testid="prod-nombre" /></label>
                            <label className="col-span-2"><span className="tb-overline text-[var(--tb-gold)]">Descripción</span><textarea className="tb-input mt-1" placeholder="Detalles, materiales, talla..." value={editing.descripcion} onChange={(e) => setEditing({ ...editing, descripcion: e.target.value })} data-testid="prod-desc" /></label>
                            <div className="col-span-2">
                                <span className="tb-overline text-[var(--tb-gold)]">Foto del producto</span>
                                <div className="mt-1 flex items-start gap-4">
                                    <div className="w-28 h-28 rounded border border-[var(--tb-border)] bg-[var(--tb-bg-2)]/30 flex items-center justify-center overflow-hidden flex-shrink-0">
                                        {(() => { const s = productImage(editing); return s ? (
                                            <img src={s} alt="" className="w-full h-full object-cover" />
                                        ) : (
                                            <ImageIcon className="w-8 h-8 text-[var(--tb-muted)]" />
                                        ); })()}
                                    </div>
                                    <div className="flex-1 space-y-2">
                                        <label className="inline-flex items-center gap-2 tb-btn-secondary !text-xs cursor-pointer" data-testid="prod-foto-label">
                                            <Upload className="w-4 h-4" /> {productImage(editing) ? "Cambiar foto" : "Subir foto"}
                                            <input type="file" accept="image/*" className="hidden"
                                                onChange={(e) => onPickPhoto(e.target.files?.[0])} data-testid="prod-foto-file" />
                                        </label>
                                        {productImage(editing) && (
                                            <button type="button" onClick={() => setEditing({ ...editing, foto: "", foto_url: "" })}
                                                className="tb-link text-xs ml-2" data-testid="prod-foto-clear">Quitar foto</button>
                                        )}
                                        <p className="text-[10px] text-[var(--tb-muted)]">JPEG/PNG/WebP · máx 5 MB · se redimensiona a 1200px automáticamente.</p>
                                    </div>
                                </div>
                            </div>
                            <ProductGalleryEditor editing={editing} setEditing={setEditing} />
                            <ProductVideosEditor editing={editing} setEditing={setEditing} />
                            <label><span className="tb-overline text-[var(--tb-gold)]">Categoría</span>
                                <select className="tb-input mt-1" value={editing.categoria || ""} onChange={(e) => setEditing({ ...editing, categoria: e.target.value })} data-testid="prod-categoria">
                                    <option value="">— Selecciona —</option>
                                    {editing.categoria && !CATEGORIES.includes(editing.categoria) && (
                                        <option value={editing.categoria}>{editing.categoria} (categoría antigua)</option>
                                    )}
                                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </label>
                            <label><span className="tb-overline text-[var(--tb-gold)]">Stock total (auto)</span><input type="number" min="0" className="tb-input mt-1 opacity-60 cursor-not-allowed" value={(parseInt(editing.cantidad_sv || 0) + parseInt(editing.cantidad_us || 0))} readOnly title="Suma automática de SV + US" /><span className="text-xs text-[var(--tb-muted)] mt-1 block">Se calcula sumando SV + US abajo.</span></label>
                            <label><span className="tb-overline text-[var(--tb-gold)]">Costo de compra (USD)</span><div className="relative mt-1"><span className="absolute left-3 top-2.5 text-[var(--tb-text-2)] pointer-events-none">$</span><input type="number" step="0.01" min="0" className="tb-input pl-8" placeholder="0.00" value={editing.precio_compra || ""} onChange={(e) => setEditing({ ...editing, precio_compra: parseFloat(e.target.value || 0) })} /></div><span className="text-xs text-[var(--tb-muted)] mt-1 block">Lo que pagaste por el producto (interno)</span></label>
                            <label><span className="tb-overline text-[var(--tb-gold)]">Precio de venta final (USD) *</span><div className="relative mt-1"><span className="absolute left-3 top-2.5 text-[var(--tb-text-2)] pointer-events-none">$</span><input type="number" step="0.01" min="0" className="tb-input pl-8" placeholder="0.00" value={editing.precio_venta || ""} onChange={(e) => setEditing({ ...editing, precio_venta: parseFloat(e.target.value || 0) })} data-testid="prod-precio" /></div><span className="text-xs text-[var(--tb-muted)] mt-1 block">Precio que verá el cliente</span></label>
                            <label><span className="tb-overline text-[var(--tb-gold)]">Estado</span>
                                <select className="tb-input mt-1" value={editing.estado} onChange={(e) => setEditing({ ...editing, estado: e.target.value })}>
                                    <option>Disponible</option><option>Vendido</option><option>Apartado</option><option>Transferido</option>
                                </select>
                            </label>
                            <label><span className="tb-overline text-[var(--tb-gold)]">Condición</span>
                                <select className="tb-input mt-1" value={editing.condicion} onChange={(e) => setEditing({ ...editing, condicion: e.target.value })}>
                                    <option value="Nuevo">Nuevo</option>
                                    <option value="Como nuevo">Usado / Como nuevo</option>
                                    <option value="Usado">Usado</option>
                                </select>
                            </label>

                            {/* Disponibilidad por país */}
                            <div className="col-span-2 mt-3 p-3 border border-[var(--tb-gold)]/30 rounded">
                                <div className="tb-overline text-[var(--tb-gold)] mb-2">Dónde se vende este producto</div>
                                <div className="flex gap-4 flex-wrap">
                                    {[
                                        { code: "SV", label: "🇸🇻 El Salvador" },
                                        { code: "US", label: "🇺🇸 Estados Unidos" },
                                    ].map((c) => {
                                        const arr = editing.availability || ["SV"];
                                        const checked = arr.includes(c.code);
                                        return (
                                            <label key={c.code} className="flex items-center gap-2 cursor-pointer" data-testid={`prod-avail-${c.code}`}>
                                                <input type="checkbox" checked={checked} onChange={(e) => {
                                                    const next = e.target.checked
                                                        ? Array.from(new Set([...arr, c.code]))
                                                        : arr.filter((x) => x !== c.code);
                                                    setEditing({ ...editing, availability: next.length ? next : ["SV"] });
                                                }} />
                                                <span className="text-sm">{c.label}</span>
                                            </label>
                                        );
                                    })}
                                </div>
                                <div className="text-[10px] text-[var(--tb-text-2)] mt-2">El producto solo aparecerá a clientes del país marcado.</div>

                                {/* Per-country stock inputs */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                                    {(editing.availability || ["SV"]).includes("SV") && (
                                        <label className="block">
                                            <span className="tb-overline text-[var(--tb-gold)] flex items-center gap-1">🇸🇻 Unidades disponibles en El Salvador</span>
                                            <input
                                                type="number"
                                                min="0"
                                                className="tb-input mt-1"
                                                value={editing.cantidad_sv ?? 0}
                                                onChange={(e) => setEditing({ ...editing, cantidad_sv: parseInt(e.target.value || 0) })}
                                                data-testid="prod-cantidad-sv"
                                            />
                                        </label>
                                    )}
                                    {(editing.availability || ["SV"]).includes("US") && (
                                        <label className="block">
                                            <span className="tb-overline text-[var(--tb-gold)] flex items-center gap-1">🇺🇸 Unidades disponibles en Estados Unidos</span>
                                            <input
                                                type="number"
                                                min="0"
                                                className="tb-input mt-1"
                                                value={editing.cantidad_us ?? 0}
                                                onChange={(e) => setEditing({ ...editing, cantidad_us: parseInt(e.target.value || 0) })}
                                                data-testid="prod-cantidad-us"
                                            />
                                        </label>
                                    )}
                                </div>
                                <div className="text-[10px] text-[var(--tb-text-2)] mt-2">Cada país maneja su stock por separado. El descuento al vender solo afecta al país del pedido.</div>
                            </div>

                            {/* Dimensiones para envío (US shipstation) */}
                            {(editing.availability || ["SV"]).includes("US") && (
                                <div className="col-span-2 p-3 border border-[var(--tb-border)] rounded space-y-3">
                                    <div className="tb-overline text-[var(--tb-gold)]">Datos de envío (Estados Unidos)</div>
                                    <p className="text-[10px] text-[var(--tb-text-2)]">Usado por ShipStation para calcular el costo de envío con USPS. Si dejas los valores por defecto, se usarán al estimar.</p>
                                    <div className="grid grid-cols-4 gap-2">
                                        <label className="text-xs">Peso (oz)
                                            <input type="number" step="0.1" min="0.1" className="tb-input mt-1" value={editing.weight_oz ?? 8} onChange={(e) => setEditing({ ...editing, weight_oz: parseFloat(e.target.value || 8) })} data-testid="prod-weight" />
                                        </label>
                                        <label className="text-xs">Largo (in)
                                            <input type="number" step="0.1" min="1" className="tb-input mt-1" value={editing.length_in ?? 6} onChange={(e) => setEditing({ ...editing, length_in: parseFloat(e.target.value || 6) })} />
                                        </label>
                                        <label className="text-xs">Ancho (in)
                                            <input type="number" step="0.1" min="1" className="tb-input mt-1" value={editing.width_in ?? 4} onChange={(e) => setEditing({ ...editing, width_in: parseFloat(e.target.value || 4) })} />
                                        </label>
                                        <label className="text-xs">Alto (in)
                                            <input type="number" step="0.1" min="1" className="tb-input mt-1" value={editing.height_in ?? 2} onChange={(e) => setEditing({ ...editing, height_in: parseFloat(e.target.value || 2) })} />
                                        </label>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="flex gap-3 mt-6">
                            <button onClick={save} className="tb-btn-primary" data-testid="admin-save-product">Guardar</button>
                            <button onClick={() => setEditing(null)} className="tb-btn-secondary">Cancelar</button>
                        </div>
                    </div>
                </div>
            )}

            {showClear && (
                <div className="tb-modal-backdrop" onClick={(e) => e.target === e.currentTarget && setShowClear(false)}>
                    <div className="tb-modal-content max-w-md">
                        <div className="flex items-center gap-3 mb-3">
                            <AlertTriangle className="w-8 h-8 text-red-400" />
                            <h3 className="font-display text-2xl">¿Limpiar todo el inventario?</h3>
                        </div>
                        <p className="text-sm text-[var(--tb-text-2)] mb-5">Esta acción <strong className="text-red-400">eliminará TODOS los productos</strong> de Terra Boutique de forma permanente. No se puede deshacer.</p>
                        <div className="space-y-3">
                            <label><span className="tb-overline">Escribe <span className="text-red-400">LIMPIAR</span> para confirmar</span>
                                <input className="tb-input mt-1" placeholder="LIMPIAR" value={clearForm.confirm} onChange={(e) => setClearForm({ ...clearForm, confirm: e.target.value })} data-testid="clear-confirm" />
                            </label>
                            <label><span className="tb-overline">Tu contraseña de administrador</span>
                                <input type="password" className="tb-input mt-1" placeholder="••••••••" value={clearForm.password} onChange={(e) => setClearForm({ ...clearForm, password: e.target.value })} data-testid="clear-password" />
                            </label>
                        </div>
                        <div className="flex gap-3 mt-6">
                            <button onClick={clearAll}
                                disabled={clearForm.confirm !== "LIMPIAR" || !clearForm.password}
                                className="tb-btn-primary !bg-red-500 !border-red-500 hover:!bg-red-600 disabled:opacity-40 disabled:cursor-not-allowed"
                                data-testid="clear-submit">
                                Sí, eliminar todo
                            </button>
                            <button onClick={() => { setShowClear(false); setClearForm({ password: "", confirm: "" }); }} className="tb-btn-secondary">Cancelar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function JsonImport() {
    const [text, setText] = useState('{\n  "ultimo_contador": 150,\n  "productos": {\n    "T-0100": { "nombre": "Ejemplo", "descripcion": "Desc.", "precio_compra": 50, "precio_venta": 99.99, "precio_minimo": 80, "foto": "", "estado": "Disponible", "condicion": "Nuevo", "categoria": "General", "cantidad_disponible": 2 }\n  }\n}');
    const [fileName, setFileName] = useState("");

    const submit = async () => {
        try {
            const parsed = JSON.parse(text);
            const { data } = await api.post("/products/import-json", parsed);
            toast.success(`Importados: ${data.imported}`);
        } catch (e) { toast.error(e.message || formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const handleFile = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setFileName(f.name);
        const reader = new FileReader();
        reader.onload = async () => {
            try {
                const json = JSON.parse(reader.result);
                setText(JSON.stringify(json, null, 2));
                const { data } = await api.post("/products/import-json", json);
                toast.success(`Importados ${data.imported} productos desde ${f.name}`);
            } catch (err) {
                toast.error("Archivo JSON inválido: " + err.message);
            }
        };
        reader.readAsText(f);
    };

    return (
        <div className="space-y-6">
            <div className="tb-card p-6">
                <h3 className="font-display text-2xl mb-2">Subir archivo JSON</h3>
                <p className="text-sm text-[var(--tb-text-2)] mb-4">Selecciona un archivo .json con el inventario. Se auto-poblará la tienda. Los SKUs existentes se actualizarán.</p>
                <label className="tb-btn-primary inline-flex items-center gap-2 cursor-pointer" data-testid="json-file-label">
                    <Upload className="w-4 h-4" /> Elegir archivo .json
                    <input type="file" accept=".json,application/json" onChange={handleFile} className="hidden" data-testid="json-file-input" />
                </label>
                {fileName && <div className="text-xs text-[var(--tb-gold)] mt-3">Cargado: {fileName}</div>}
            </div>

            <div className="tb-card p-6">
                <h3 className="font-display text-2xl mb-2">O pega el JSON manualmente</h3>
                <p className="text-sm text-[var(--tb-text-2)] mb-3">Estructura esperada: {`{ ultimo_contador, productos: { "T-XXXX": {...} } }`}.</p>
                <textarea className="tb-input font-mono text-xs min-h-[300px]" value={text} onChange={(e) => setText(e.target.value)} data-testid="json-input" />
                <button onClick={submit} className="tb-btn-primary mt-4 inline-flex items-center gap-2" data-testid="json-import">
                    <Upload className="w-4 h-4" /> Importar JSON pegado
                </button>
            </div>

            <TranslateAllPanel />
        </div>
    );
}

function TranslateAllPanel() {
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [force, setForce] = useState(false);
    const run = async () => {
        if (!window.confirm(force ? "Esto RE-traducirá TODOS los productos (aunque ya tengan traducción). ¿Continuar?" : "Traducirá al inglés los productos que aún no tienen versión EN. ¿Continuar?")) return;
        setBusy(true); setResult(null);
        try {
            const { data } = await api.post(`/admin/products/translate-all?force=${force ? "true" : "false"}`, {}, { timeout: 300000 });
            setResult(data);
            toast.success(`Traducidos: ${data.translated}/${data.total}${data.failed ? ` (${data.failed} fallaron)` : ""}`);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
        } finally { setBusy(false); }
    };
    return (
        <div className="tb-card p-6" data-testid="translate-all-panel">
            <h3 className="font-display text-2xl mb-2">Traducción al inglés (USA)</h3>
            <p className="text-sm text-[var(--tb-text-2)] mb-3">
                Traduce automáticamente los nombres y descripciones de productos al inglés con IA (Claude Sonnet). Los usuarios con el idioma inglés activado en su navegador verán los productos ya traducidos.
                Nuevos productos y ediciones se traducen automáticamente al guardarlos.
            </p>
            <label className="inline-flex items-center gap-2 text-xs mb-3">
                <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} data-testid="translate-all-force" />
                Re-traducir también los que ya tienen versión EN (usar solo si cambió el estilo o los prompts)
            </label>
            <div>
                <button onClick={run} disabled={busy} className="tb-btn-primary inline-flex items-center gap-2" data-testid="translate-all-run">
                    <Globe className="w-4 h-4" /> {busy ? "Traduciendo..." : "Traducir productos al inglés"}
                </button>
            </div>
            {result && (
                <div className="text-xs text-[var(--tb-text-2)] mt-3">
                    Total analizados: <strong>{result.total}</strong> · Traducidos: <strong className="text-[var(--tb-gold)]">{result.translated}</strong>
                    {result.failed > 0 && <> · Fallaron: <strong className="text-rose-400">{result.failed}</strong></>}
                </div>
            )}
        </div>
    );
}

function Usuarios() {
    const [list, setList] = useState([]);
    const [stats, setStats] = useState(null);
    const [filter, setFilter] = useState("all"); // all | pending | verified
    const [reviewing, setReviewing] = useState(null);
    const [deleting, setDeleting] = useState(null); // user object pending deletion
    const [deletePw, setDeletePw] = useState("");
    const [deleteBusy, setDeleteBusy] = useState(false);
    const load = async () => {
        const [{ data: users }, { data: s }] = await Promise.all([
            api.get("/users"),
            api.get("/admin/users/stats").catch(() => ({ data: null })),
        ]);
        setList(users);
        if (s) setStats(s);
    };
    useEffect(() => {
        load();
        // Live refresh every 20s
        const t = setInterval(load, 20000);
        return () => clearInterval(t);
    }, []);
    const updRole = async (id, role) => { await api.put(`/users/${id}/role`, { role }); load(); };
    const verif = async (id, verified) => {
        await api.put(`/users/${id}/verify`, { verified, verification_status: verified ? "approved" : "rejected" });
        setReviewing(null);
        load();
        toast.success(verified ? "Usuario verificado" : "Verificación rechazada");
    };
    const confirmDelete = async () => {
        if (!deletePw) { toast.error("Ingresa tu contraseña"); return; }
        setDeleteBusy(true);
        try {
            await api.delete(`/users/${deleting.id}`, { data: { password: deletePw } });
            toast.success(`Usuario ${deleting.email} eliminado`);
            setDeleting(null);
            setDeletePw("");
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err) || "Error al eliminar");
        } finally {
            setDeleteBusy(false);
        }
    };

    const filtered = list.filter((u) => {
        if (filter === "pending") return u.verification_status === "pending";
        if (filter === "verified") return u.verified === true;
        return true;
    });
    const pendingCount = list.filter((u) => u.verification_status === "pending").length;

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-3 flex-wrap">
                <h2 className="font-display text-2xl flex-1">Usuarios</h2>
                <div className="flex gap-1 border border-[var(--tb-border)] rounded p-0.5">
                    {[
                        { id: "all", label: `Todos (${list.length})` },
                        { id: "pending", label: `Pendientes (${pendingCount})` },
                        { id: "verified", label: "Verificados" },
                    ].map((f) => (
                        <button key={f.id} onClick={() => setFilter(f.id)}
                            data-testid={`users-filter-${f.id}`}
                            className={`text-xs px-3 py-1.5 rounded ${filter === f.id ? "bg-[var(--tb-gold)] text-black font-medium" : "text-[var(--tb-text-2)]"}`}>
                            {f.label}
                            {f.id === "pending" && pendingCount > 0 && filter !== "pending" && (
                                <span className="ml-1 bg-red-500 text-white text-[9px] rounded-full px-1.5">{pendingCount}</span>
                            )}
                        </button>
                    ))}
                </div>
            </div>

            {/* Live stats summary */}
            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="users-stats">
                    {[
                        { k: "total", label: "Total", value: stats.total, color: "text-[var(--tb-gold)]" },
                        { k: "verified", label: "Verificados", value: stats.verified, color: "text-blue-400" },
                        { k: "pending", label: "Pendientes", value: stats.pending, color: "text-amber-400" },
                        { k: "admins", label: "Admins", value: stats.admins, color: "text-purple-400" },
                        { k: "sellers", label: "Vendedores", value: stats.sellers, color: "text-emerald-400" },
                        { k: "clients", label: "Clientes", value: stats.clients, color: "text-[var(--tb-text)]" },
                        { k: "new_24h", label: "Nuevos 24h", value: stats.new_24h, color: "text-rose-400" },
                    ].map((s) => (
                        <div key={s.k} className="tb-card p-3" data-testid={`users-stat-${s.k}`}>
                            <div className="tb-overline text-[10px] text-[var(--tb-muted)]">{s.label}</div>
                            <div className={`text-2xl font-display ${s.color}`}>{s.value}</div>
                        </div>
                    ))}
                    <div className="col-span-2 md:col-span-4 lg:col-span-7 text-[10px] text-[var(--tb-muted)] italic flex items-center gap-1">
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                        Actualización automática cada 20 segundos
                    </div>
                </div>
            )}

            <div className="tb-card overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="border-b border-[var(--tb-border)] text-left text-[var(--tb-text-2)]">
                        <tr><th className="p-3">Usuario</th><th>Email</th><th>Rol</th><th>Estado Verif.</th><th>Acciones</th></tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 && (
                            <tr><td colSpan="5" className="p-6 text-center text-[var(--tb-text-2)] text-sm">No hay usuarios para mostrar</td></tr>
                        )}
                        {filtered.map((u) => (
                            <tr key={u.id} className="border-b border-[var(--tb-border)]">
                                <td className="p-3 flex items-center gap-2">
                                    {u.photo ? <img src={u.photo} alt="" className="w-8 h-8 rounded-full object-cover" /> : <div className="w-8 h-8 rounded-full bg-[var(--tb-bg-2)]" />}
                                    <span>{u.name}</span>
                                    {u.verified && <BadgeCheck className="w-4 h-4 text-[#2196F3]" />}
                                </td>
                                <td className="text-xs">{u.email}</td>
                                <td>
                                    <select className="tb-input py-1 text-xs" value={u.role} onChange={(e) => updRole(u.id, e.target.value)} data-testid={`role-${u.email}`}>
                                        <option value="cliente">{roleLabel("cliente")}</option>
                                        <option value="vendedor">{roleLabel("vendedor")}</option>
                                        <option value="admin">{roleLabel("admin")}</option>
                                    </select>
                                </td>
                                <td>
                                    {u.verification_status === "pending" ? (
                                        <span className="tb-badge bg-amber-500/20 text-amber-300 border-amber-500/40">⏳ Pendiente</span>
                                    ) : u.verified ? (
                                        <span className="tb-badge tb-badge-success">✓ Aprobado</span>
                                    ) : u.verification_status === "rejected" ? (
                                        <span className="tb-badge bg-red-500/20 text-red-300">✗ Rechazado</span>
                                    ) : (
                                        <span className="tb-badge tb-badge-muted">— Sin solicitud</span>
                                    )}
                                </td>
                                <td className="space-x-2 whitespace-nowrap">
                                    {(u.verification_status === "pending" || u.verification_document || u.verification_document_file) ? (
                                        <button onClick={() => setReviewing(u)} className="tb-btn-secondary !text-xs inline-flex items-center gap-1" data-testid={`review-${u.email}`}>
                                            <ShieldCheck className="w-3.5 h-3.5" /> Revisar
                                        </button>
                                    ) : null}
                                    <button onClick={() => verif(u.id, !u.verified)} className="tb-btn-secondary !text-xs" data-testid={`verify-${u.email}`}>
                                        {u.verified ? "Quitar" : "Verificar"}
                                    </button>
                                    <button
                                        onClick={() => { setDeleting(u); setDeletePw(""); }}
                                        className="tb-btn-secondary !text-xs !text-red-400 !border-red-400/40 hover:!bg-red-500/10 inline-flex items-center gap-1"
                                        data-testid={`delete-user-${u.email}`}
                                    >
                                        <Trash2 className="w-3.5 h-3.5" /> Eliminar
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Verification review modal */}
            {reviewing && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-start md:items-center justify-center p-4 overflow-y-auto" onClick={() => setReviewing(null)}>
                    <div className="bg-[var(--tb-bg)] border border-[var(--tb-border)] max-w-2xl w-full my-4" onClick={(e) => e.stopPropagation()} data-testid="verify-review-modal">
                        <div className="p-6 border-b border-[var(--tb-border)] flex items-start justify-between gap-3">
                            <div>
                                <h3 className="font-display text-xl flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-[var(--tb-gold)]" /> Revisión de Verificación</h3>
                                <p className="text-xs text-[var(--tb-text-2)] mt-1">{reviewing.name} · {reviewing.email}</p>
                            </div>
                            <button onClick={() => setReviewing(null)} className="text-[var(--tb-text-2)] hover:text-[var(--tb-gold)]"><X className="w-5 h-5" /></button>
                        </div>

                        <div className="p-6 space-y-4">
                            {reviewing.verification_document && (
                                <div>
                                    <label className="tb-overline">Texto / Número de documento</label>
                                    <div className="mt-1 p-3 bg-[var(--tb-bg-2)] border border-[var(--tb-border)] rounded font-mono text-sm">{reviewing.verification_document}</div>
                                </div>
                            )}
                            {reviewing.verification_selfie ? (
                                <div>
                                    <div className="flex items-center justify-between mb-1">
                                        <label className="tb-overline">Selfie del usuario</label>
                                        <a
                                            href={reviewing.verification_selfie}
                                            download={`verificacion-selfie-${(reviewing.email || reviewing.id || "user").replace(/[^a-z0-9._-]+/gi, "_")}.${(reviewing.verification_selfie.match(/^data:image\/(\w+)/) || ["", "jpg"])[1]}`}
                                            className="tb-btn-secondary !text-xs inline-flex items-center gap-1.5"
                                            data-testid="download-verify-selfie">
                                            <Download className="w-3.5 h-3.5" /> Descargar
                                        </a>
                                    </div>
                                    <div className="border border-[var(--tb-border)] rounded overflow-hidden">
                                        {reviewing.verification_selfie.startsWith("data:image") ? (
                                            <img src={reviewing.verification_selfie} alt="selfie" className="w-full max-h-[500px] object-contain bg-black" />
                                        ) : (
                                            <a href={reviewing.verification_selfie} target="_blank" rel="noreferrer" className="block p-4 text-[var(--tb-gold)] underline">Ver archivo en nueva pestaña</a>
                                        )}
                                    </div>
                                </div>
                            ) : null}
                            {reviewing.verification_document_file ? (
                                <div>
                                    <div className="flex items-center justify-between mb-1">
                                        <label className="tb-overline">Documento de identidad</label>
                                        <a
                                            href={reviewing.verification_document_file}
                                            download={`verificacion-documento-${(reviewing.email || reviewing.id || "user").replace(/[^a-z0-9._-]+/gi, "_")}.${reviewing.verification_document_file.startsWith("data:application/pdf") ? "pdf" : (reviewing.verification_document_file.match(/^data:image\/(\w+)/) || ["", "jpg"])[1]}`}
                                            className="tb-btn-secondary !text-xs inline-flex items-center gap-1.5"
                                            data-testid="download-verify-document">
                                            <Download className="w-3.5 h-3.5" /> Descargar
                                        </a>
                                    </div>
                                    <div className="border border-[var(--tb-border)] rounded overflow-hidden">
                                        {reviewing.verification_document_file.startsWith("data:image") ? (
                                            <img src={reviewing.verification_document_file} alt="documento" className="w-full max-h-[500px] object-contain bg-black" />
                                        ) : reviewing.verification_document_file.startsWith("data:application/pdf") ? (
                                            <iframe title="doc-pdf" src={reviewing.verification_document_file} className="w-full h-[500px] bg-white" />
                                        ) : (
                                            <a href={reviewing.verification_document_file} target="_blank" rel="noreferrer" className="block p-4 text-[var(--tb-gold)] underline">Ver archivo en nueva pestaña</a>
                                        )}
                                    </div>
                                </div>
                            ) : (!reviewing.verification_selfie && !reviewing.verification_document) && (
                                <p className="text-[var(--tb-text-2)] text-sm">El usuario aún no ha subido ningún documento.</p>
                            )}

                            <div className="pt-4 border-t border-[var(--tb-border)] flex gap-2 justify-end">
                                <button onClick={() => verif(reviewing.id, false)} className="px-4 py-2 text-sm border border-red-500/40 text-red-300 hover:bg-red-500/10 rounded" data-testid="verify-reject">
                                    Rechazar
                                </button>
                                <button onClick={() => verif(reviewing.id, true)} className="tb-btn-primary !text-sm inline-flex items-center gap-2" data-testid="verify-approve">
                                    <Check className="w-4 h-4" /> Aprobar verificación
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete user modal with password confirmation */}
            {deleting && (
                <div className="fixed inset-0 bg-black/80 z-50 flex items-start md:items-center justify-center p-4 overflow-y-auto" onClick={() => !deleteBusy && setDeleting(null)} data-testid="delete-user-modal">
                    <div className="tb-card max-w-md w-full p-6 space-y-4 my-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-start gap-3">
                            <div className="bg-red-500/20 border border-red-500/40 rounded-full p-2.5 flex-shrink-0">
                                <AlertTriangle className="w-5 h-5 text-red-400" />
                            </div>
                            <div>
                                <h3 className="font-display text-xl text-red-400">Eliminar usuario</h3>
                                <p className="text-xs text-[var(--tb-text-2)] mt-1">Esta acción es permanente y no se puede deshacer.</p>
                            </div>
                        </div>
                        <div className="bg-[var(--tb-bg-2)] border border-[var(--tb-border)] p-3 text-sm">
                            <div className="flex items-center gap-2">
                                {deleting.photo ? <img src={deleting.photo} alt="" className="w-8 h-8 rounded-full object-cover" /> : <div className="w-8 h-8 rounded-full bg-[var(--tb-bg)]" />}
                                <div>
                                    <div className="font-medium">{deleting.name}</div>
                                    <div className="text-xs text-[var(--tb-muted)]">{deleting.email}</div>
                                </div>
                            </div>
                            <ul className="text-[11px] text-[var(--tb-text-2)] mt-3 space-y-1">
                                <li>• Se eliminará el perfil y todos sus datos personales.</li>
                                <li>• Se eliminarán sus reseñas de productos y del sitio.</li>
                                <li>• Se eliminarán sus tickets de soporte y carrito.</li>
                                <li>• Los pedidos históricos se conservan para registro contable.</li>
                            </ul>
                        </div>
                        <div>
                            <label className="tb-overline text-[var(--tb-text-2)]">Confirma con tu contraseña de administrador</label>
                            <input
                                type="password"
                                value={deletePw}
                                onChange={(e) => setDeletePw(e.target.value)}
                                placeholder="Tu contraseña"
                                className="tb-input mt-1"
                                autoFocus
                                onKeyDown={(e) => e.key === "Enter" && confirmDelete()}
                                data-testid="delete-user-pw"
                            />
                        </div>
                        <div className="flex gap-2 justify-end">
                            <button onClick={() => setDeleting(null)} disabled={deleteBusy} className="tb-btn-secondary !text-sm" data-testid="delete-user-cancel">Cancelar</button>
                            <button onClick={confirmDelete} disabled={deleteBusy || !deletePw} className="tb-btn-primary !text-sm !bg-red-500 !border-red-500 hover:!bg-red-600" data-testid="delete-user-confirm">
                                {deleteBusy ? "Eliminando..." : "Eliminar definitivamente"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function Ordenes() {
    const [list, setList] = useState([]);
    const load = () => api.get("/orders").then(({ data }) => setList(data));
    useEffect(() => { load(); }, []);
    const updStatus = async (id, status) => { await api.put(`/orders/${id}/status`, { status }); load(); toast.success("Actualizado"); };
    return (
        <div className="space-y-3">
            {list.map((o) => (
                <div key={o._id} className="tb-card p-5">
                    <div className="flex justify-between flex-wrap gap-2">
                        <div><div className="font-mono text-xs text-[var(--tb-muted)]">{o._id}</div><div>{o.user_email}</div></div>
                        <div className="text-[var(--tb-gold)] font-semibold">{formatPrice(o.total)}</div>
                    </div>
                    <div className="text-xs text-[var(--tb-text-2)] mt-1">{o.items.map(i => `${i.nombre} ×${i.quantity}`).join(", ")}</div>
                    <div className="text-xs mt-2">Método: {o.payment_method} · Pago: {o.payment_status}</div>
                    <div className="flex gap-2 mt-3 flex-wrap">
                        {["pending", "apartado", "pagado", "completado", "cancelado"].map((s) => (
                            <button key={s} onClick={() => updStatus(o._id, s)}
                                className={`tb-badge ${o.status === s ? "tb-badge-gold" : "tb-badge-muted"} cursor-pointer`}>{s}</button>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

function Tickets() {
    const [list, setList] = useState([]);
    const [reply, setReply] = useState({});
    const load = () => api.get("/tickets").then(({ data }) => setList(data));
    useEffect(() => {
        load();
        const iv = setInterval(() => { load().catch(() => {}); }, 5000);
        const onVis = () => { if (document.visibilityState === "visible") load().catch(() => {}); };
        document.addEventListener("visibilitychange", onVis);
        return () => {
            clearInterval(iv);
            document.removeEventListener("visibilitychange", onVis);
        };
    }, []);
    const send = async (tid, status) => {
        await api.post(`/tickets/${tid}/reply`, { message: reply[tid] || "", status });
        setReply({ ...reply, [tid]: "" }); load(); toast.success("Enviado");
    };
    return (
        <div className="space-y-4">
            {list.map((t) => (
                <div key={t._id} className="tb-card p-5">
                    <div className="flex justify-between mb-2">
                        <div><div className="font-mono text-xs text-[var(--tb-muted)]">{t._id}</div><div className="font-medium">{t.subject}</div><div className="text-xs text-[var(--tb-text-2)]">{t.user_email}</div></div>
                        <span className="tb-badge tb-badge-gold h-fit">{t.status}</span>
                    </div>
                    <div className="space-y-1 my-3 max-h-40 overflow-auto">
                        {t.messages.map((m, i) => <div key={`${t._id}-${i}-${m.at || ""}`} className="text-sm"><span className="text-[var(--tb-gold)]">{m.from}:</span> {m.text}</div>)}
                    </div>
                    <input className="tb-input mb-2" placeholder="Respuesta..." value={reply[t._id] || ""} onChange={(e) => setReply({ ...reply, [t._id]: e.target.value })} />
                    <div className="flex gap-2">
                        <button onClick={() => send(t._id, "in_process")} className="tb-btn-secondary text-xs">Responder · En proceso</button>
                        <button onClick={() => send(t._id, "closed")} className="tb-btn-primary text-xs">Cerrar</button>
                    </div>
                </div>
            ))}
        </div>
    );
}

function Anuncios() {
    const [list, setList] = useState([]);
    const [draft, setDraft] = useState({ title: "", body: "", image: "", active: true });
    const load = () => api.get("/announcements?active_only=false").then(({ data }) => setList(data));
    useEffect(() => { load(); }, []);
    const create = async () => { await api.post("/announcements", draft); setDraft({ title: "", body: "", image: "", active: true }); load(); toast.success("Anuncio creado"); };
    const upd = async (a, v) => { await api.put(`/announcements/${a._id}`, { ...a, active: v }); load(); };
    const del = async (id) => { await api.delete(`/announcements/${id}`); load(); };
    return (
        <div className="grid md:grid-cols-2 gap-6">
            <div className="tb-card p-5">
                <h3 className="font-display text-xl mb-3">Crear anuncio</h3>
                <input className="tb-input mb-2" placeholder="Título" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
                <textarea className="tb-input mb-2 min-h-[80px]" placeholder="Mensaje" value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} />
                <input className="tb-input mb-2" placeholder="Imagen URL (opcional)" value={draft.image} onChange={(e) => setDraft({ ...draft, image: e.target.value })} />
                <button onClick={create} className="tb-btn-primary">Publicar</button>
            </div>
            <div className="space-y-3">
                {list.map((a) => (
                    <div key={a._id} className="tb-card p-4">
                        <div className="flex justify-between items-start">
                            <div><div className="font-medium">{a.title}</div><div className="text-sm text-[var(--tb-text-2)]">{a.body}</div></div>
                            <div className="flex flex-col gap-2">
                                <button onClick={() => upd(a, !a.active)} className={`tb-badge cursor-pointer ${a.active ? "tb-badge-success" : "tb-badge-muted"}`}>{a.active ? "ON" : "OFF"}</button>
                                <button onClick={() => del(a._id)} className="tb-link"><Trash2 className="w-4 h-4" /></button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function Ajustes() {
    const blankBank = { bank_name: "", account_holder: "", account_number: "", account_type: "Ahorro", notes: "" };
    const [s, setS] = useState({ logo: "", banner: "", social_links: {}, bank_info: "", bank_accounts: [], paypal_email: "", paypal_client_id: "", paypal_secret: "", paypal_mode: "sandbox", stripe_publishable_key: "", stripe_secret_key: "", stripe_mode: "test", address: "", address_sv: "", address_us: "", shipping_price: 0, free_shipping_threshold: 0, us_shipping_price: 0, us_free_shipping_threshold: 0, shipping_partner: {}, receipt_config: {}, smtp: {}, payment_methods: {}, shipstation: {} });
    const [original, setOriginal] = useState(null);
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        api.get("/admin/settings")
            .then(({ data }) => {
                const merged = {
                    logo: "", banner: "", address: "", address_sv: "", address_us: "",
                    shipping_price: 0, free_shipping_threshold: 0, us_shipping_price: 0, us_free_shipping_threshold: 0,
                    paypal_email: "", paypal_client_id: "", paypal_secret: "", paypal_mode: "sandbox",
                    stripe_publishable_key: "", stripe_secret_key: "", stripe_mode: "test",
                    bank_info: "",
                    ...data,
                    social_links: data.social_links || {},
                    bank_accounts: data.bank_accounts || [],
                    shipping_partner: data.shipping_partner || {},
                    receipt_config: data.receipt_config || {},
                    smtp: data.smtp || {},
                    payment_methods: data.payment_methods || {},
                    shipstation: data.shipstation || {},
                    social_items: data.social_items || [],
                };
                setS(merged);
                setOriginal(JSON.stringify(merged));
            })
            .catch((e) => {
                toast.error("No se pudieron cargar los ajustes — recarga la página");
                console.error("GET /settings failed", e);
                // Mark snapshot so the bar at least renders (not permanently locked)
                setOriginal("__error__");
            });
    }, []);
    const isDirty = original !== null && original !== "__error__" && JSON.stringify(s) !== original;
    const save = async () => {
        setSaving(true);
        try {
            const { data: serverState } = await api.put("/settings", s);
            // Re-merge to keep client-only defaults but anchor the snapshot to server truth
            const merged = { ...s, ...serverState };
            setS(merged);
            setOriginal(JSON.stringify(merged));
            toast.success("Todos los cambios fueron guardados");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al guardar");
        } finally { setSaving(false); }
    };
    const addBank = () => setS({ ...s, bank_accounts: [...(s.bank_accounts || []), { ...blankBank }] });
    const updBank = (i, k, v) => {
        const arr = [...(s.bank_accounts || [])];
        arr[i] = { ...arr[i], [k]: v };
        setS({ ...s, bank_accounts: arr });
    };
    const delBank = (i) => setS({ ...s, bank_accounts: (s.bank_accounts || []).filter((_, idx) => idx !== i) });

    // File upload helper for logo/banner — compresses to data URL for storage in settings
    const onPickBrandImage = (file, field, maxDim) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) { toast.error("Solo imágenes"); return; }
        if (file.size > 5 * 1024 * 1024) { toast.error("La imagen supera 5MB"); return; }
        const img = new Image();
        const reader = new FileReader();
        reader.onload = (ev) => {
            img.onload = () => {
                const canvas = document.createElement("canvas");
                const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
                canvas.width = Math.round(img.width * scale);
                canvas.height = Math.round(img.height * scale);
                canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
                const mime = (file.type === "image/png") ? "image/png" : "image/jpeg";
                const quality = mime === "image/png" ? undefined : 0.85;
                setS((cur) => ({ ...cur, [field]: canvas.toDataURL(mime, quality) }));
                toast.success(`${field === "logo" ? "Logo" : "Banner"} actualizado — recuerda guardar`);
            };
            img.src = ev.target.result;
        };
        reader.readAsDataURL(file);
    };

    return (
        <div className="space-y-4" data-testid="ajustes-section">
            {/* Sticky Save All Bar */}
            <div className={`sticky top-0 z-30 -mx-2 px-4 py-3 backdrop-blur-md rounded-lg border transition-all ${isDirty ? "bg-[var(--tb-gold)]/15 border-[var(--tb-gold)]/50 shadow-lg" : "bg-[var(--tb-bg)]/80 border-[var(--tb-border)]"}`} data-testid="ajustes-save-bar">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                        <span className={`w-2 h-2 rounded-full ${isDirty ? "bg-amber-400 animate-pulse" : "bg-emerald-400"}`} />
                        <div>
                            <div className="font-display text-base">
                                {isDirty ? "Cambios sin guardar" : "Todo está guardado"}
                            </div>
                            <div className="text-[10px] text-[var(--tb-text-2)]">
                                {isDirty ? "Recuerda hacer clic en Guardar para aplicar los cambios" : "Los ajustes están sincronizados con el servidor"}
                            </div>
                        </div>
                    </div>
                    <button onClick={save} disabled={saving || !isDirty}
                        className={`tb-btn-primary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-transform ${isDirty ? "scale-100 hover:scale-105" : "scale-95"}`}
                        data-testid="ajustes-save-all">
                        <Save className="w-4 h-4" />
                        {saving ? "Guardando..." : "Guardar todos los cambios"}
                    </button>
                </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
            <div className="tb-card p-6 space-y-4">
                <h3 className="font-display text-2xl mb-1">Identidad visual</h3>
                <p className="text-xs text-[var(--tb-text-2)] -mt-1">Sube tu logo y banner desde tu computadora (PNG/JPG, máx 5 MB).</p>

                {/* LOGO */}
                <div>
                    <label className="tb-overline">Logo de la tienda</label>
                    <div className="flex items-start gap-3 mt-1">
                        <div className="w-24 h-24 rounded border border-[var(--tb-border)] bg-[var(--tb-bg-2)]/30 flex items-center justify-center overflow-hidden flex-shrink-0">
                            {s.logo ? (
                                <img src={s.logo} alt="Logo" className="w-full h-full object-contain" />
                            ) : (
                                <ImageIcon className="w-8 h-8 text-[var(--tb-muted)]" />
                            )}
                        </div>
                        <div className="flex-1 space-y-2">
                            <label className="inline-flex items-center gap-2 tb-btn-secondary !text-xs cursor-pointer" data-testid="settings-logo-pick">
                                <Upload className="w-3.5 h-3.5" /> {s.logo ? "Cambiar logo" : "Subir logo"}
                                <input type="file" accept="image/*" className="hidden" onChange={(e) => onPickBrandImage(e.target.files?.[0], "logo", 512)} data-testid="settings-logo-file" />
                            </label>
                            {s.logo && (
                                <button onClick={() => setS({ ...s, logo: "" })} className="text-[11px] text-rose-400 hover:underline ml-2" data-testid="settings-logo-clear">Quitar</button>
                            )}
                            <p className="text-[10px] text-[var(--tb-muted)]">Recomendado: cuadrado, fondo transparente (PNG). Se redimensiona a 512px.</p>
                        </div>
                    </div>
                </div>

                {/* BANNER */}
                <div>
                    <label className="tb-overline">Banner promocional</label>
                    <div className="space-y-2 mt-1">
                        {s.banner && (
                            <div className="w-full h-32 rounded border border-[var(--tb-border)] bg-[var(--tb-bg-2)]/30 overflow-hidden">
                                <img src={s.banner} alt="Banner" className="w-full h-full object-cover" />
                            </div>
                        )}
                        <div className="flex items-center gap-3">
                            <label className="inline-flex items-center gap-2 tb-btn-secondary !text-xs cursor-pointer" data-testid="settings-banner-pick">
                                <Upload className="w-3.5 h-3.5" /> {s.banner ? "Cambiar banner" : "Subir banner"}
                                <input type="file" accept="image/*" className="hidden" onChange={(e) => onPickBrandImage(e.target.files?.[0], "banner", 1600)} data-testid="settings-banner-file" />
                            </label>
                            {s.banner && (
                                <button onClick={() => setS({ ...s, banner: "" })} className="text-[11px] text-rose-400 hover:underline" data-testid="settings-banner-clear">Quitar</button>
                            )}
                        </div>
                        <p className="text-[10px] text-[var(--tb-muted)]">Recomendado: 1600×600 px (panorámico). Se redimensiona automáticamente.</p>
                    </div>
                </div>

                {/* DIRECCIONES POR PAÍS */}
                <div className="pt-3 border-t border-[var(--tb-border)]">
                    <div className="tb-overline mb-2">Direcciones de la tienda por país</div>
                    <p className="text-[10px] text-[var(--tb-muted)] mb-3">Cada país muestra su propia dirección en checkout, footer y recibos PDF.</p>

                    <div className="space-y-3">
                        <div>
                            <label className="text-xs font-medium text-[var(--tb-gold)] flex items-center gap-2">🇸🇻 Dirección en El Salvador</label>
                            <textarea
                                className="tb-input min-h-[80px] mt-1"
                                placeholder="Ej. Av. La Revolución #123, Colonia San Benito, San Salvador, El Salvador"
                                value={s.address_sv || ""}
                                onChange={(e) => setS({ ...s, address_sv: e.target.value })}
                                data-testid="settings-address-sv"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-medium text-[var(--tb-gold)] flex items-center gap-2">🇺🇸 Dirección / contacto en Estados Unidos</label>
                            <textarea
                                className="tb-input min-h-[80px] mt-1"
                                placeholder="Ej. Bodega/centro de distribución o &quot;Envíos a todo Estados Unidos vía USPS&quot;"
                                value={s.address_us || ""}
                                onChange={(e) => setS({ ...s, address_us: e.target.value })}
                                data-testid="settings-address-us"
                            />
                        </div>
                        <div className="pt-3 border-t border-[var(--tb-border)]">
                            <label className="text-xs font-medium text-[var(--tb-gold)] flex items-center gap-2">📬 Correos para notificar nuevos pedidos</label>
                            <input
                                type="text"
                                className="tb-input mt-1"
                                placeholder="admin@terraboutique.com, ventas@terraboutique.com"
                                value={s.order_notify_emails || ""}
                                onChange={(e) => setS({ ...s, order_notify_emails: e.target.value })}
                                data-testid="settings-order-notify-emails"
                            />
                            <div className="text-[10px] text-[var(--tb-muted)] mt-1">Recibirás un correo por cada pedido nuevo. Separa múltiples direcciones con comas. Si lo dejas vacío, usa el correo principal del sitio.</div>
                            <button
                                type="button"
                                onClick={async () => {
                                    try {
                                        const { data } = await api.post("/admin/test-order-notification");
                                        toast.success(`Correo de prueba enviado a: ${data.recipients.join(", ")}`);
                                    } catch (e) {
                                        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo enviar el correo de prueba");
                                    }
                                }}
                                className="tb-btn-secondary !text-xs mt-2 inline-flex items-center gap-1"
                                data-testid="settings-test-order-email">
                                <Mail className="w-3.5 h-3.5" /> Enviar correo de prueba
                            </button>
                            <div className="text-[10px] text-[var(--tb-muted)] mt-1">Guarda los cambios primero, luego prueba para confirmar que el correo llega correctamente a tu bandeja.</div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="tb-card p-6 space-y-4">
                <h3 className="font-display text-2xl mb-1">Envíos</h3>
                <div className="space-y-4">
                    <SvShippingTiersInline s={s} setS={setS} />
                    <div className="pt-3 border-t border-[var(--tb-border)]">
                        <UsShippingTiersInline s={s} setS={setS} />
                    </div>
                </div>
                <div className="text-xs text-[var(--tb-muted)]">Apartado en tienda no aplica costo de envío.</div>
            </div>

            <div className="tb-card p-6 space-y-4 md:col-span-2">
                <div className="flex items-center justify-between">
                    <h3 className="font-display text-2xl">Credenciales PayPal</h3>
                    <span className={`tb-badge ${s.paypal_client_id ? "tb-badge-success" : "tb-badge-muted"}`}>
                        {s.paypal_client_id ? "Configurado" : "Sin configurar"}
                    </span>
                </div>
                <p className="text-xs text-[var(--tb-muted)]">
                    Obtén tu Client ID y Secret en <a href="https://developer.paypal.com/dashboard/applications/live" target="_blank" rel="noreferrer" className="text-[var(--tb-gold)] underline">developer.paypal.com → My Apps & Credentials</a>. Para cobrar dinero real usa modo Live (requiere cuenta business verificada).
                </p>
                <div className="grid md:grid-cols-3 gap-3">
                    <div>
                        <label className="tb-overline">Modo</label>
                        <select className="tb-input" value={s.paypal_mode || "sandbox"} onChange={(e) => setS({ ...s, paypal_mode: e.target.value })} data-testid="settings-paypal-mode">
                            <option value="sandbox">Sandbox (pruebas)</option>
                            <option value="live">Live (producción)</option>
                        </select>
                    </div>
                    <div className="md:col-span-2">
                        <label className="tb-overline">Email PayPal (display al cliente)</label>
                        <input className="tb-input" placeholder="pagos@terraboutique.com" value={s.paypal_email || ""} onChange={(e) => setS({ ...s, paypal_email: e.target.value })} />
                    </div>
                    <div className="md:col-span-3">
                        <label className="tb-overline">Client ID</label>
                        <input className="tb-input font-mono text-xs" placeholder="AYjK...abcXYZ" value={s.paypal_client_id || ""} onChange={(e) => setS({ ...s, paypal_client_id: e.target.value })} data-testid="settings-paypal-id" />
                    </div>
                    <div className="md:col-span-3">
                        <label className="tb-overline">Secret</label>
                        <input type="password" className="tb-input font-mono text-xs" placeholder="EHt...secretXYZ" value={s.paypal_secret || ""} onChange={(e) => setS({ ...s, paypal_secret: e.target.value })} data-testid="settings-paypal-secret" />
                    </div>
                </div>
            </div>

            <div className="tb-card p-6 space-y-4 md:col-span-2" data-testid="stripe-config-card">
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <h3 className="font-display text-2xl">Credenciales Stripe</h3>
                    {(() => {
                        const mode = s.stripe_mode || "test";
                        const liveOk = !!(s.stripe_live_secret_key || (mode === "live" && s.stripe_secret_key));
                        const testOk = !!(s.stripe_test_secret_key || (mode === "test" && s.stripe_secret_key));
                        const activeOk = mode === "live" ? liveOk : testOk;
                        return (
                            <span className={`tb-badge ${activeOk ? (mode === "live" ? "tb-badge-success" : "tb-badge-warning") : "tb-badge-muted"}`}>
                                {mode === "live" ? "🟢 LIVE" : "🟡 TEST"} {activeOk ? "· Configurado" : "· Sin configurar"}
                            </span>
                        );
                    })()}
                </div>
                <p className="text-xs text-[var(--tb-muted)]">
                    Obtén tus llaves en <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer" className="text-[var(--tb-gold)] underline">dashboard.stripe.com → API keys</a>.
                    Guarda <strong>ambos pares</strong> (test y live); el modo activo decide cuál se usa para cobrar.
                </p>

                {/* Active mode selector */}
                <div className="grid md:grid-cols-3 gap-3 items-end">
                    <div className="md:col-span-3">
                        <label className="tb-overline">Modo activo (qué llaves se usan al cobrar)</label>
                        <div className="grid grid-cols-2 gap-2">
                            <label className={`p-3 border cursor-pointer text-sm transition ${(s.stripe_mode || "test") === "test" ? "border-amber-400 bg-amber-400/10" : "border-[var(--tb-border)]"}`}>
                                <input type="radio" name="stripe_mode" checked={(s.stripe_mode || "test") === "test"} onChange={() => setS({ ...s, stripe_mode: "test" })} className="mr-2" data-testid="settings-stripe-mode-test" />
                                <strong>🟡 TEST</strong>
                                <div className="text-[10px] text-[var(--tb-text-2)] mt-0.5">Tarjetas ficticias (4242 4242 4242 4242), sin cobrar dinero real.</div>
                            </label>
                            <label className={`p-3 border cursor-pointer text-sm transition ${s.stripe_mode === "live" ? "border-emerald-400 bg-emerald-400/10" : "border-[var(--tb-border)]"}`}>
                                <input type="radio" name="stripe_mode" checked={s.stripe_mode === "live"} onChange={() => setS({ ...s, stripe_mode: "live" })} className="mr-2" data-testid="settings-stripe-mode-live" />
                                <strong>🟢 LIVE</strong>
                                <div className="text-[10px] text-[var(--tb-text-2)] mt-0.5">Producción: cobra dinero real a tarjetas reales. Asegúrate de tener llaves <code>sk_live_*</code>.</div>
                            </label>
                        </div>
                    </div>
                </div>

                {/* TEST keys */}
                <div className="border-t border-[var(--tb-border)] pt-3" data-testid="stripe-test-keys">
                    <div className="text-xs font-medium text-amber-400 mb-2 flex items-center gap-2">🟡 Llaves de TEST</div>
                    <div className="grid md:grid-cols-2 gap-3">
                        <div>
                            <label className="tb-overline">Test publishable (pk_test_...)</label>
                            <input className="tb-input font-mono text-xs" placeholder="pk_test_..."
                                value={s.stripe_test_publishable_key || ""}
                                onChange={(e) => setS({ ...s, stripe_test_publishable_key: e.target.value })}
                                data-testid="settings-stripe-test-pub" />
                        </div>
                        <div>
                            <label className="tb-overline">Test secret (sk_test_...)</label>
                            <input type="password" className="tb-input font-mono text-xs" placeholder="sk_test_..."
                                value={s.stripe_test_secret_key || ""}
                                onChange={(e) => setS({ ...s, stripe_test_secret_key: e.target.value })}
                                data-testid="settings-stripe-test-secret" autoComplete="off" />
                        </div>
                    </div>
                </div>

                {/* LIVE keys */}
                <div className="border-t border-[var(--tb-border)] pt-3" data-testid="stripe-live-keys">
                    <div className="text-xs font-medium text-emerald-400 mb-2 flex items-center gap-2">🟢 Llaves de LIVE (producción)</div>
                    <div className="grid md:grid-cols-2 gap-3">
                        <div>
                            <label className="tb-overline">Live publishable (pk_live_...)</label>
                            <input className="tb-input font-mono text-xs" placeholder="pk_live_..."
                                value={s.stripe_live_publishable_key || ""}
                                onChange={(e) => setS({ ...s, stripe_live_publishable_key: e.target.value })}
                                data-testid="settings-stripe-live-pub" />
                        </div>
                        <div>
                            <label className="tb-overline">Live secret (sk_live_...)</label>
                            <input type="password" className="tb-input font-mono text-xs" placeholder="sk_live_..."
                                value={s.stripe_live_secret_key || ""}
                                onChange={(e) => setS({ ...s, stripe_live_secret_key: e.target.value })}
                                data-testid="settings-stripe-live-secret" autoComplete="off" />
                        </div>
                    </div>
                </div>

                {/* Live mode warning */}
                {s.stripe_mode === "live" && !s.stripe_live_secret_key && !s.stripe_secret_key && (
                    <div className="text-xs text-rose-400 bg-rose-500/10 border border-rose-500/30 rounded p-2" data-testid="stripe-live-missing">
                        ⚠️ Modo LIVE activo pero NO has guardado las llaves live. Los cobros fallarán hasta que llenes <code>sk_live_*</code>.
                    </div>
                )}
                {s.stripe_mode === "live" && (s.stripe_live_secret_key || "").startsWith("sk_test_") && (
                    <div className="text-xs text-rose-400 bg-rose-500/10 border border-rose-500/30 rounded p-2" data-testid="stripe-live-wrong-key">
                        ⚠️ La llave que pusiste en LIVE empieza con <code>sk_test_</code>. Reemplázala por una <code>sk_live_</code> real.
                    </div>
                )}
                <div className="text-[10px] text-[var(--tb-muted)]">
                    💡 Cambiar el modo <strong>no</strong> borra las llaves del otro modo — quedan guardadas para que puedas alternar cuando quieras.
                </div>
            </div>

            <div className="tb-card p-6 space-y-4 md:col-span-2">
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <h3 className="font-display text-2xl">Cuentas Bancarias (Transferencias)</h3>
                    <button onClick={addBank} className="tb-btn-secondary !text-xs inline-flex items-center gap-2" data-testid="settings-add-bank">
                        <Plus className="w-3.5 h-3.5" /> Agregar cuenta
                    </button>
                </div>
                <p className="text-xs text-[var(--tb-muted)]">Estas cuentas aparecen en el PDF del cliente cuando elige pagar por transferencia.</p>
                {(s.bank_accounts || []).length === 0 ? (
                    <div className="text-sm text-[var(--tb-text-2)] py-4 text-center border border-dashed border-[var(--tb-border)] rounded">Aún no hay cuentas bancarias agregadas.</div>
                ) : (
                    <div className="space-y-3">
                        {(s.bank_accounts || []).map((b, i) => (
                            <div key={`bank-${i}`} className="border border-[var(--tb-border)] p-4 grid md:grid-cols-12 gap-3" data-testid={`bank-${i}`}>
                                <input className="tb-input md:col-span-3" placeholder="Banco (ej. Banco Agrícola)" value={b.bank_name} onChange={(e) => updBank(i, "bank_name", e.target.value)} />
                                <input className="tb-input md:col-span-3" placeholder="Titular" value={b.account_holder} onChange={(e) => updBank(i, "account_holder", e.target.value)} />
                                <select className="tb-input md:col-span-2" value={b.account_type} onChange={(e) => updBank(i, "account_type", e.target.value)}>
                                    <option>Ahorro</option><option>Corriente</option>
                                </select>
                                <input className="tb-input md:col-span-3 font-mono" placeholder="Número de cuenta" value={b.account_number} onChange={(e) => updBank(i, "account_number", e.target.value)} />
                                <button onClick={() => delBank(i)} className="tb-link md:col-span-1 flex justify-center items-center"><Trash2 className="w-4 h-4" /></button>
                                <input className="tb-input md:col-span-12" placeholder="Notas adicionales (referencia, horarios, etc.)" value={b.notes} onChange={(e) => updBank(i, "notes", e.target.value)} />
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <C807ConfigCard />

            <SocialItemsCard s={s} setS={setS} save={save} />

            <PaymentMethodsCard s={s} setS={setS} save={save} />
            <ReceiptConfigCard s={s} setS={setS} save={save} />
            <SmtpConfigCard s={s} setS={setS} save={save} />

            <DangerZone />
            </div>
        </div>
    );
}

const FONT_OPTIONS = [
    { v: "helvetica", l: "Helvetica (limpio, moderno)" },
    { v: "times", l: "Times (clásico, serif)" },
    { v: "courier", l: "Courier (monoespaciado)" },
];

const DEFAULT_SOCIAL_LINES = [
    { label: "Facebook", value: "Terra Boutique", show: true },
    { label: "Instagram", value: "@terra_boutique2026", show: true },
    { label: "TikTok", value: "", show: false },
    { label: "WhatsApp", value: "", show: false },
];

function SvShippingTiersInline({ s, setS }) {
    return <ShippingTiersInline s={s} setS={setS} country="SV" />;
}

function UsShippingTiersInline({ s, setS }) {
    return <ShippingTiersInline s={s} setS={setS} country="US" />;
}

function ShippingTiersInline({ s, setS, country }) {
    const isSV = country === "SV";
    const fieldList = isSV ? "sv_shipping_tiers" : "us_shipping_tiers";
    const fieldActive = isSV ? "sv_shipping_tiers_active" : "us_shipping_tiers_active";
    const tiers = Array.isArray(s[fieldList]) ? s[fieldList] : [];
    const active = !!s[fieldActive];
    const setTiers = (next) => setS({ ...s, [fieldList]: next });
    const flagLabel = isSV ? "🇸🇻 El Salvador" : "🇺🇸 Estados Unidos";
    const exclusions = isSV
        ? "Aplica solo a envíos domicilio SV — no afecta Retiro en tienda, CCE ni Apartado."
        : "Aplica a todos los envíos USA (USPS Ground).";
    const tid = (key) => `${isSV ? "sv" : "us"}-tier-${key}`;
    const addTier = () => setTiers([...tiers, { id: `tier_${Date.now()}`, min_subtotal: 0, max_subtotal: 0, cost: 0, label: "" }]);
    const removeTier = (i) => setTiers(tiers.filter((_, idx) => idx !== i));
    const update = (i, key, val) => setTiers(tiers.map((t, idx) => idx === i ? { ...t, [key]: val } : t));
    return (
        <div data-testid={`${isSV ? "sv" : "us"}-tiers-inline`}>
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                <div className="text-xs font-medium text-[var(--tb-gold)] flex items-center gap-2">{flagLabel} — Niveles de envío (inverso)</div>
                <label className="flex items-center gap-2 cursor-pointer text-[11px]">
                    <input type="checkbox" checked={active}
                        onChange={(e) => setS({ ...s, [fieldActive]: e.target.checked })}
                        data-testid={tid("active-toggle")} />
                    <span className={active ? "text-[var(--tb-gold)]" : "text-[var(--tb-text-2)]"}>{active ? "Activado" : "Desactivado"}</span>
                </label>
            </div>
            <p className="text-[11px] text-[var(--tb-text-2)] mb-3">Mientras más cuesta el pedido, más barato (o gratis) el envío. {exclusions}</p>
            <div className="space-y-2">
                <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wide text-[var(--tb-muted)] px-1">
                    <div className="col-span-3">Desde subtotal $</div>
                    <div className="col-span-3">Hasta subtotal $ (0 = ∞)</div>
                    <div className="col-span-2">Costo envío $</div>
                    <div className="col-span-3">Etiqueta (opcional)</div>
                    <div className="col-span-1"></div>
                </div>
                {tiers.map((t, i) => (
                    <div key={t.id || i} className="grid grid-cols-12 gap-2 items-center" data-testid={tid(`row-${i}`)}>
                        <input type="number" step="0.01" min="0" className="tb-input col-span-3" value={t.min_subtotal ?? 0}
                            onChange={(e) => update(i, "min_subtotal", parseFloat(e.target.value || 0))} data-testid={tid(`min-${i}`)} />
                        <input type="number" step="0.01" min="0" className="tb-input col-span-3" value={t.max_subtotal ?? 0}
                            onChange={(e) => update(i, "max_subtotal", parseFloat(e.target.value || 0))} data-testid={tid(`max-${i}`)} />
                        <input type="number" step="0.01" min="0" className="tb-input col-span-2" value={t.cost ?? 0}
                            onChange={(e) => update(i, "cost", parseFloat(e.target.value || 0))} data-testid={tid(`cost-${i}`)} />
                        <input type="text" className="tb-input col-span-3" placeholder="Ej. Pedidos pequeños" value={t.label || ""}
                            onChange={(e) => update(i, "label", e.target.value)} data-testid={tid(`label-${i}`)} />
                        <button type="button" onClick={() => removeTier(i)}
                            className="col-span-1 text-[var(--tb-text-2)] hover:text-red-400 transition" data-testid={tid(`delete-${i}`)}>
                            <Trash2 className="w-4 h-4 mx-auto" />
                        </button>
                    </div>
                ))}
                {tiers.length === 0 && (
                    <div className="text-xs text-[var(--tb-muted)] py-3 text-center border border-dashed border-[var(--tb-border)] rounded">
                        No has creado niveles. Crea al menos uno y activa el sistema para que se aplique al checkout.
                    </div>
                )}
                <button type="button" onClick={addTier}
                    className="tb-btn-secondary text-xs inline-flex items-center gap-1 mt-1" data-testid={tid("add")}>
                    <Plus className="w-3 h-3" /> Agregar nivel
                </button>
            </div>
            <div className="text-[10px] text-[var(--tb-muted)] mt-3 bg-[var(--tb-bg)]/40 p-2.5 rounded border border-[var(--tb-border)]">
                <strong className="text-[var(--tb-text-2)]">Cómo funciona:</strong> el sistema toma el subtotal del pedido (sin envío ni descuentos) y aplica el primer nivel donde <code>desde ≤ subtotal &lt; hasta</code>. Para envío gratis a partir de $X, crea un nivel con <code>desde=X, hasta=0, costo=0</code>.
            </div>
        </div>
    );
}


function PaymentMethodsCard({ s, setS, save }) {
    const pm = s.payment_methods || {};
    const setPm = (key, val) => setS({ ...s, payment_methods: { ...pm, [key]: val } });
    const isEnabled = (k) => pm[k] !== false; // default = enabled when missing

    const methods = [
        { key: "cash", label: "Efectivo en Tienda (Cash)", desc: "Apartado para pago físico al retirar el producto" },
        { key: "stripe", label: "Tarjeta de Crédito/Débito (Stripe)", desc: "Pago en línea con tarjeta. Requiere credenciales Stripe configuradas" },
        { key: "paypal", label: "PayPal", desc: "Pago con cuenta PayPal. Requiere credenciales PayPal configuradas" },
        { key: "transferencia", label: "Transferencia Bancaria", desc: "Cliente transfiere y admin confirma manualmente" },
    ];

    return (
        <div className="tb-card p-6 space-y-4 md:col-span-2" data-testid="payment-methods-card">
            <div className="flex items-center gap-3">
                <ShoppingBag className="w-6 h-6 text-[var(--tb-gold)]" />
                <div className="flex-1">
                    <h3 className="font-display text-2xl">Métodos de Pago</h3>
                    <p className="text-xs text-[var(--tb-text-2)]">Activa o desactiva los métodos de pago disponibles en el checkout</p>
                </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
                {methods.map((m) => (
                    <label key={m.key} className={`p-4 border rounded cursor-pointer transition ${isEnabled(m.key) ? "border-[var(--tb-gold)]/60 bg-[var(--tb-border-gold)]/5" : "border-[var(--tb-border)] opacity-60"}`}
                        data-testid={`pm-toggle-${m.key}`}>
                        <div className="flex items-start gap-3">
                            <input type="checkbox" className="mt-1" checked={isEnabled(m.key)} onChange={(e) => setPm(m.key, e.target.checked)} />
                            <div className="flex-1">
                                <div className="text-sm font-medium">{m.label}</div>
                                <div className="text-[11px] text-[var(--tb-text-2)] mt-1">{m.desc}</div>
                                {!isEnabled(m.key) && <div className="text-[10px] text-red-300 mt-1">Desactivado · no se muestra al cliente</div>}
                            </div>
                        </div>
                    </label>
                ))}
            </div>

            <div className="flex justify-end pt-2">
                <button onClick={save} className="tb-btn-primary inline-flex items-center gap-2" data-testid="pm-save">
                    <Save className="w-4 h-4" /> Guardar Métodos de Pago
                </button>
            </div>
        </div>
    );
}


function ReceiptConfigCard({ s, setS, save }) {
    const rc = s.receipt_config || {};
    const setRc = (patch) => setS({ ...s, receipt_config: { ...rc, ...patch } });

    const preview = async () => {
        try {
            await generateReceipt(SAMPLE_RECEIPT_ORDER, s);
            toast.success("Vista previa descargada");
        } catch (e) { toast.error("Error generando vista previa: " + (e?.message || "")); }
    };

    return (
        <div className="tb-card p-6 space-y-5 md:col-span-2" data-testid="receipt-config-card">
            <div className="flex items-center gap-3">
                <Receipt className="w-6 h-6 text-[var(--tb-gold)]" />
                <div className="flex-1">
                    <h3 className="font-display text-2xl">Personalización de Recibos PDF</h3>
                    <p className="text-xs text-[var(--tb-text-2)]">Edita textos, colores, fuente y redes sociales que aparecen en cada recibo descargable.</p>
                </div>
                <button onClick={preview} className="tb-btn-secondary !text-xs inline-flex items-center gap-2" data-testid="receipt-preview-btn">
                    <Eye className="w-3.5 h-3.5" /> Vista previa
                </button>
            </div>

            {/* Tipografía y tamaño */}
            <div className="grid md:grid-cols-3 gap-3">
                <div>
                    <label className="tb-overline">Fuente principal (cuerpo)</label>
                    <select className="tb-input" value={rc.body_font || "helvetica"} onChange={(e) => setRc({ body_font: e.target.value })} data-testid="rc-body-font">
                        {FONT_OPTIONS.map((f) => <option key={f.v} value={f.v}>{f.l}</option>)}
                    </select>
                </div>
                <div>
                    <label className="tb-overline">Fuente de títulos (display)</label>
                    <select className="tb-input" value={rc.display_font || "times"} onChange={(e) => setRc({ display_font: e.target.value })} data-testid="rc-display-font">
                        {FONT_OPTIONS.map((f) => <option key={f.v} value={f.v}>{f.l}</option>)}
                    </select>
                </div>
                <div>
                    <label className="tb-overline">Tamaño base ({rc.base_font_size || 9}pt)</label>
                    <input type="range" min={7} max={12} step={1} value={rc.base_font_size || 9}
                        onChange={(e) => setRc({ base_font_size: parseInt(e.target.value) })}
                        className="w-full" data-testid="rc-font-size" />
                </div>
            </div>

            {/* Colores */}
            <div className="grid md:grid-cols-3 gap-3">
                <div>
                    <label className="tb-overline">Color principal (dorado)</label>
                    <div className="flex gap-2">
                        <input type="color" className="h-10 w-14 border border-[var(--tb-border)] bg-transparent" value={rc.primary_color || "#B8923A"} onChange={(e) => setRc({ primary_color: e.target.value })} data-testid="rc-primary-color" />
                        <input className="tb-input font-mono text-xs flex-1" value={rc.primary_color || "#B8923A"} onChange={(e) => setRc({ primary_color: e.target.value })} />
                    </div>
                </div>
                <div>
                    <label className="tb-overline">Color oscuro (fondo header/footer)</label>
                    <div className="flex gap-2">
                        <input type="color" className="h-10 w-14 border border-[var(--tb-border)] bg-transparent" value={rc.dark_color || "#2C2C30"} onChange={(e) => setRc({ dark_color: e.target.value })} data-testid="rc-dark-color" />
                        <input className="tb-input font-mono text-xs flex-1" value={rc.dark_color || "#2C2C30"} onChange={(e) => setRc({ dark_color: e.target.value })} />
                    </div>
                </div>
                <div>
                    <label className="tb-overline">Color crema (cajas suaves)</label>
                    <div className="flex gap-2">
                        <input type="color" className="h-10 w-14 border border-[var(--tb-border)] bg-transparent" value={rc.cream_color || "#FAF6EE"} onChange={(e) => setRc({ cream_color: e.target.value })} data-testid="rc-cream-color" />
                        <input className="tb-input font-mono text-xs flex-1" value={rc.cream_color || "#FAF6EE"} onChange={(e) => setRc({ cream_color: e.target.value })} />
                    </div>
                </div>
            </div>

            {/* Textos del encabezado */}
            <div className="grid md:grid-cols-2 gap-3">
                <div>
                    <label className="tb-overline">Marca / Encabezado</label>
                    <input className="tb-input" value={rc.header_brand || "Terra Boutique"} onChange={(e) => setRc({ header_brand: e.target.value })} data-testid="rc-header-brand" />
                </div>
                <div>
                    <label className="tb-overline">Subtítulo del encabezado</label>
                    <input className="tb-input" value={rc.header_subtitle || "EL SALVADOR · BOUTIQUE PREMIUM"} onChange={(e) => setRc({ header_subtitle: e.target.value })} data-testid="rc-header-subtitle" />
                </div>
                <div>
                    <label className="tb-overline">Título Comprobante de Pedido</label>
                    <input className="tb-input" value={rc.title_pedido || "Comprobante de Pedido"} onChange={(e) => setRc({ title_pedido: e.target.value })} />
                </div>
                <div>
                    <label className="tb-overline">Título Comprobante de Apartado</label>
                    <input className="tb-input" value={rc.title_apartado || "Comprobante de Apartado"} onChange={(e) => setRc({ title_apartado: e.target.value })} />
                </div>
            </div>

            {/* Pie y mensajes */}
            <div className="grid md:grid-cols-2 gap-3">
                <div>
                    <label className="tb-overline">Texto del pie de página</label>
                    <input className="tb-input" value={rc.footer_text || "Gracias por elegir Terra Boutique"} onChange={(e) => setRc({ footer_text: e.target.value })} data-testid="rc-footer-text" />
                </div>
                <div>
                    <label className="tb-overline">Subtexto del pie (opcional)</label>
                    <input className="tb-input" placeholder="Ej: 'Devoluciones hasta 7 días'" value={rc.footer_subtext || ""} onChange={(e) => setRc({ footer_subtext: e.target.value })} />
                </div>
            </div>

            {/* Bloque social */}
            <div className="border-t border-[var(--tb-border)] pt-4">
                <label className="flex items-center gap-2 text-sm mb-2 cursor-pointer">
                    <input type="checkbox" checked={rc.show_social_block !== false} onChange={(e) => setRc({ show_social_block: e.target.checked })} data-testid="rc-show-social" />
                    <span>Mostrar bloque de redes sociales en el recibo</span>
                </label>
                <div>
                    <label className="tb-overline">Título del bloque</label>
                    <input className="tb-input" value={rc.social_block_title || "Síguenos y mantente al tanto de nuestras novedades"} onChange={(e) => setRc({ social_block_title: e.target.value })} data-testid="rc-social-title" />
                </div>
                <div className="mt-3 p-3 border border-[var(--tb-gold)]/30 bg-[var(--tb-border-gold)]/10 rounded text-xs leading-relaxed" data-testid="rc-social-redirect">
                    <div className="font-medium text-[var(--tb-gold)] mb-1">📌 Los logos y usuarios se configuran en la sección unificada</div>
                    Ve a <strong>Ajustes → Redes sociales</strong> en esta misma página para subir el <strong>logo de cada red</strong> y escribir tu <strong>usuario/handle</strong>. Esos datos aparecerán en este bloque del recibo PDF (logo + usuario, sin repetir el nombre de la red).
                </div>
            </div>

            {/* Bloque bancario */}
            <div className="border-t border-[var(--tb-border)] pt-4">
                <label className="flex items-center gap-2 text-sm mb-2 cursor-pointer">
                    <input type="checkbox" checked={rc.show_bank_block !== false} onChange={(e) => setRc({ show_bank_block: e.target.checked })} data-testid="rc-show-bank" />
                    <span>Mostrar instrucciones bancarias en recibos de transferencia</span>
                </label>
                <div>
                    <label className="tb-overline">Título del bloque bancario</label>
                    <input className="tb-input" value={rc.bank_block_title || "INSTRUCCIONES DE PAGO POR TRANSFERENCIA"} onChange={(e) => setRc({ bank_block_title: e.target.value })} />
                </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
                <button onClick={preview} className="tb-btn-secondary inline-flex items-center gap-2" data-testid="receipt-preview-btn-2">
                    <Eye className="w-4 h-4" /> Vista previa
                </button>
                <button onClick={save} className="tb-btn-primary inline-flex items-center gap-2" data-testid="rc-save">
                    <Save className="w-4 h-4" /> Guardar recibos
                </button>
            </div>
        </div>
    );
}


// ============================================================
// Configuración C807 Xpress — credenciales + modo (mock|real) en .env
// ============================================================
function C807ConfigCard() {
    const [cfg, setCfg] = useState(null);
    const [form, setForm] = useState({ user: "", password: "", mode: "mock", sede: "" });
    const [showPwd, setShowPwd] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const load = async () => {
        try {
            const { data } = await api.get("/admin/c807/config");
            setCfg(data);
            setForm({ user: data.user || "", password: "", mode: data.mode || "mock", sede: data.sede || "" });
        } catch (e) {
            toast.error("No se pudo leer la configuración C807");
        }
    };
    useEffect(() => { load(); }, []);

    const onSave = async () => {
        setError("");
        // Frontend validations
        if (!form.user.trim() && form.mode === "real") {
            setError("El usuario API es requerido para modo real.");
            return;
        }
        if (form.mode === "real" && !form.password.trim() && !cfg?.password_set) {
            setError("Para activar modo real necesitas ingresar la contraseña API.");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.put("/admin/c807/config", {
                user: form.user.trim(),
                password: form.password,  // empty → backend keeps existing
                mode: form.mode,
                sede: form.sede,
            });
            setCfg(data);
            setForm((f) => ({ ...f, password: "" }));
            toast.success("Cambios guardados correctamente. Reinicia el token para aplicar la conexión.");
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail));
        } finally { setSaving(false); }
    };

    const onRefreshToken = async () => {
        try {
            const { data } = await api.post("/c807-token");
            toast.success(`Token renovado${data.mock ? " (MOCK)" : ""} · expira ${new Date(data.expires_at).toLocaleString("es-SV")}`);
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const [printingSample, setPrintingSample] = useState(false);
    const [sampleModalOpen, setSampleModalOpen] = useState(false);
    const printSampleLabel = () => setSampleModalOpen(true);

    if (!cfg) return <div className="tb-card p-6 md:col-span-2 text-sm text-[var(--tb-text-2)]">Cargando configuración C807...</div>;

    const isReal = cfg.mode === "real";

    return (
        <div className="tb-card p-6 space-y-5 md:col-span-2" data-testid="c807-config-card">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <h3 className="font-display text-2xl flex items-center gap-2">
                    <Truck className="w-6 h-6 text-[var(--tb-gold)]" />
                    Configuración C807 Xpress
                </h3>
                <button type="button" onClick={onRefreshToken} className="tb-btn-secondary !text-xs" data-testid="c807-config-refresh-token">
                    Renovar token ahora
                </button>
            </div>
            <p className="text-xs text-[var(--tb-text-2)]">
                Conecta tu cuenta de C807 Xpress para emisión real de guías, etiquetas y tracking. En modo <strong className="text-amber-400">mock</strong> el sistema simula respuestas sin contactar a C807 (útil para pruebas y demos).
            </p>

            <div className="grid md:grid-cols-2 gap-4">
                <div>
                    <label className="tb-overline">Usuario API *</label>
                    <input className="tb-input" placeholder="usuario_c807"
                        value={form.user} onChange={(e) => setForm({ ...form, user: e.target.value })}
                        data-testid="c807-config-user" autoComplete="off" />
                </div>
                <div>
                    <label className="tb-overline flex items-center justify-between">
                        <span>Contraseña API {cfg.password_set ? <span className="text-[10px] text-emerald-400 ml-2">✓ guardada ({cfg.password_preview})</span> : null}</span>
                        <button type="button" onClick={() => setShowPwd(!showPwd)} className="text-[10px] tb-link">{showPwd ? "Ocultar" : "Mostrar"}</button>
                    </label>
                    <input className="tb-input" type={showPwd ? "text" : "password"}
                        placeholder={cfg.password_set ? "Dejar vacío para mantener la actual" : "Nueva contraseña API"}
                        value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                        data-testid="c807-config-password" autoComplete="new-password" />
                </div>
                <div>
                    <label className="tb-overline">Modo de conexión *</label>
                    <select className="tb-input" value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} data-testid="c807-config-mode">
                        <option value="mock">mock — Simular respuestas (no crea guías reales)</option>
                        <option value="real">real — Conectar a la API real de C807</option>
                    </select>
                </div>
                <div>
                    <label className="tb-overline">ID de sede (opcional)</label>
                    <input className="tb-input" type="number" placeholder="Ej. 1" min="0"
                        value={form.sede} onChange={(e) => setForm({ ...form, sede: e.target.value })}
                        data-testid="c807-config-sede" />
                </div>
            </div>

            {error && (
                <div className="text-xs text-rose-400 bg-rose-500/10 border border-rose-500/30 rounded p-2" data-testid="c807-config-error">
                    {error}
                </div>
            )}

            <div className="flex items-center gap-3 flex-wrap">
                <button onClick={onSave} disabled={saving} className="tb-btn-primary inline-flex items-center gap-2 disabled:opacity-50" data-testid="c807-config-save">
                    <Save className="w-4 h-4" /> {saving ? "Guardando..." : "GUARDAR CAMBIOS"}
                </button>
                <button onClick={printSampleLabel} disabled={printingSample} className="tb-btn-secondary inline-flex items-center gap-2 disabled:opacity-50 !text-amber-300 !border-amber-500/40 hover:!bg-amber-500/10" data-testid="c807-config-sample-label" title="Abre un formulario con todos los datos de checkout y genera una etiqueta C807 realista de prueba (orden TEST-*)">
                    <Printer className="w-4 h-4" /> Imprimir etiqueta de prueba…
                </button>
                <div className={`text-xs font-medium px-3 py-2 rounded-full border ${isReal ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : "border-amber-500/40 bg-amber-500/10 text-amber-300"}`} data-testid="c807-config-status">
                    <span className="mr-1">{isReal ? "🟢" : "🟡"}</span>
                    {isReal ? "Conectado a C807 real — guías y etiquetas reales activas." : "Modo prueba (mock) activo — no se crean guías reales."}
                </div>
            </div>

            <div className="border-t border-[var(--tb-border)] pt-3 text-[10px] text-[var(--tb-muted)] leading-relaxed">
                💡 Después de guardar y antes de emitir guías, haz clic en <strong>&quot;Renovar token ahora&quot;</strong> para limpiar el token cacheado y forzar la próxima petición con las nuevas credenciales.
                {cfg.webhook_secret_set && <span className="ml-1 text-emerald-400">· Webhook HMAC configurado</span>}
            </div>
            <C807SampleLabelModal open={sampleModalOpen} onClose={() => setSampleModalOpen(false)} />
        </div>
    );
}


function SocialItemsCard({ s, setS, save }) {
    const items = s.social_items || [];
    const update = (next) => setS({ ...s, social_items: next });
    const addItem = () => update([...items, { id: `sl-${Date.now()}`, icon: "", url: "", alt: "" }]);
    const removeItem = (i) => update(items.filter((_, idx) => idx !== i));
    const updItem = (i, field, val) => update(items.map((it, idx) => idx === i ? { ...it, [field]: val } : it));

    const onPickIcon = (i, file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) { toast.error("Solo imágenes"); return; }
        if (file.size > 2 * 1024 * 1024) { toast.error("Máximo 2MB"); return; }
        const img = new Image();
        const reader = new FileReader();
        reader.onload = (ev) => {
            img.onload = () => {
                const canvas = document.createElement("canvas");
                const max = 256; // icon stays small
                const scale = Math.min(1, max / Math.max(img.width, img.height));
                canvas.width = Math.round(img.width * scale);
                canvas.height = Math.round(img.height * scale);
                canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
                updItem(i, "icon", canvas.toDataURL("image/png"));
            };
            img.src = ev.target.result;
        };
        reader.readAsDataURL(file);
    };

    return (
        <div className="tb-card p-6 space-y-4 md:col-span-2" data-testid="social-items-card">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="font-display text-2xl">Redes sociales</h3>
                    <p className="text-xs text-[var(--tb-text-2)]">Sube el <strong className="text-[var(--tb-gold)]">logo</strong> de cada red social y escribe tu <strong className="text-[var(--tb-gold)]">usuario/handle</strong>. Esto aparece en <strong>el footer del sitio</strong> y en <strong>los recibos PDF</strong>.</p>
                </div>
                <button onClick={addItem} className="tb-btn-secondary !text-xs inline-flex items-center gap-1" data-testid="social-add">
                    <Plus className="w-3 h-3" /> Agregar red
                </button>
            </div>
            {items.length === 0 && (
                <div className="border border-dashed border-[var(--tb-border)] rounded p-6 text-center text-xs text-[var(--tb-muted)]" data-testid="social-empty">
                    Aún no has agregado redes sociales. Haz clic en &quot;Agregar red&quot; para subir el primer logo.
                </div>
            )}
            <div className="space-y-3">
                {items.map((it, i) => (
                    <div key={it.id || i} className="border border-[var(--tb-border)] rounded p-3 flex gap-3 items-start" data-testid={`social-item-${i}`}>
                        <div className="w-16 h-16 rounded border border-[var(--tb-border)] bg-[var(--tb-bg-2)]/30 flex items-center justify-center overflow-hidden flex-shrink-0">
                            {it.icon ? (
                                <img src={it.icon} alt={it.alt || "icon"} className="w-full h-full object-contain" />
                            ) : (
                                <ImageIcon className="w-6 h-6 text-[var(--tb-muted)]" />
                            )}
                        </div>
                        <div className="flex-1 grid sm:grid-cols-2 gap-2">
                            <label className="inline-flex items-center gap-2 tb-btn-secondary !text-xs cursor-pointer h-9 self-start" data-testid={`social-pick-${i}`}>
                                <Upload className="w-3.5 h-3.5" /> {it.icon ? "Cambiar logo" : "Subir logo"}
                                <input type="file" accept="image/*" className="hidden" onChange={(e) => onPickIcon(i, e.target.files?.[0])} data-testid={`social-file-${i}`} />
                            </label>
                            <input className="tb-input" placeholder="Mi usuario / handle (@terra_boutique)" value={it.alt || ""} onChange={(e) => updItem(i, "alt", e.target.value)} data-testid={`social-alt-${i}`} />
                            <input className="tb-input sm:col-span-2" placeholder="URL completa (https://instagram.com/terra_boutique)" value={it.url || ""} onChange={(e) => updItem(i, "url", e.target.value)} data-testid={`social-url-${i}`} />
                            <p className="text-[10px] text-[var(--tb-muted)] sm:col-span-2 -mt-1">
                                💡 En los recibos PDF se mostrará: <span className="text-[var(--tb-gold)]">[logo]</span> seguido de tu usuario (sin repetir el nombre de la red social). Máximo 6 redes se imprimen en el PDF.
                            </p>
                        </div>
                        <button onClick={() => removeItem(i)} className="text-[var(--tb-muted)] hover:text-rose-400 transition" data-testid={`social-remove-${i}`}>
                            <Trash2 className="w-4 h-4" />
                        </button>
                    </div>
                ))}
            </div>
            <button onClick={save} className="tb-btn-primary inline-flex items-center gap-2" data-testid="settings-save"><Save className="w-4 h-4" /> Guardar redes sociales</button>
        </div>
    );
}


function SmtpConfigCard({ s, setS, save }) {
    const sm = s.smtp || {};
    const [testTo, setTestTo] = useState("");
    const [testing, setTesting] = useState(false);
    const setSm = (patch) => setS({ ...s, smtp: { ...sm, ...patch } });
    const sendTest = async () => {
        if (!testTo) { toast.error("Ingresa el correo destinatario"); return; }
        setTesting(true);
        try {
            await api.put("/settings", s);
            const { data } = await api.post("/admin/smtp-test", { to: testTo });
            toast.success(data.message || "Correo enviado");
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setTesting(false); }
    };
    const configured = !!(sm.email && sm.password);

    return (
        <div className="tb-card p-6 space-y-4 md:col-span-2" data-testid="smtp-config-card">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3">
                    <Mail className="w-6 h-6 text-[var(--tb-gold)]" />
                    <div>
                        <h3 className="font-display text-2xl">Configuración de correo (SMTP)</h3>
                        <p className="text-xs text-[var(--tb-text-2)]">Usado para enviar el enlace de recuperación de contraseña. Recomendado: Outlook/Hotmail con App Password.</p>
                    </div>
                </div>
                <span className={`tb-badge ${configured ? "tb-badge-success" : "tb-badge-muted"}`}>
                    {configured ? "Configurado" : "No configurado"}
                </span>
            </div>

            <div className="text-xs text-[var(--tb-muted)] p-3 border border-dashed border-[var(--tb-border)] rounded space-y-1">
                <div><strong className="text-[var(--tb-gold)]">Outlook/Hotmail:</strong> host <code>smtp-mail.outlook.com</code> puerto <code>587</code>. Crea una <a href="https://account.live.com/proofs/AppPassword" target="_blank" rel="noreferrer" className="text-[var(--tb-gold)] underline">App Password aquí</a> (requiere 2FA).</div>
                <div><strong className="text-[var(--tb-gold)]">Gmail:</strong> host <code>smtp.gmail.com</code> puerto <code>587</code>. Crea una <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="text-[var(--tb-gold)] underline">App Password aquí</a> (requiere 2FA).</div>
            </div>

            <div className="grid md:grid-cols-2 gap-3">
                <div>
                    <label className="tb-overline">Correo (FROM)</label>
                    <input className="tb-input" placeholder="terraboutique@outlook.com" value={sm.email || ""} onChange={(e) => setSm({ email: e.target.value })} data-testid="smtp-email" />
                </div>
                <div>
                    <label className="tb-overline">Contraseña / App Password</label>
                    <input type="password" className="tb-input font-mono text-xs" placeholder="••••••••••••••••" value={sm.password || ""} onChange={(e) => setSm({ password: e.target.value })} data-testid="smtp-password" />
                </div>
                <div>
                    <label className="tb-overline">Servidor SMTP (host)</label>
                    <input className="tb-input" placeholder="smtp-mail.outlook.com" value={sm.host || ""} onChange={(e) => setSm({ host: e.target.value })} data-testid="smtp-host" />
                </div>
                <div>
                    <label className="tb-overline">Puerto</label>
                    <input type="number" className="tb-input" placeholder="587" value={sm.port || ""} onChange={(e) => setSm({ port: parseInt(e.target.value || 587) })} data-testid="smtp-port" />
                </div>
                <div className="md:col-span-2">
                    <label className="tb-overline">Nombre del remitente</label>
                    <input className="tb-input" placeholder="Terra Boutique" value={sm.from_name || ""} onChange={(e) => setSm({ from_name: e.target.value })} data-testid="smtp-from-name" />
                </div>
            </div>

            <div className="border-t border-[var(--tb-border)] pt-4 flex items-end gap-2 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                    <label className="tb-overline">Enviar correo de prueba a</label>
                    <input className="tb-input" placeholder="prueba@correo.com" value={testTo} onChange={(e) => setTestTo(e.target.value)} data-testid="smtp-test-to" />
                </div>
                <button onClick={sendTest} disabled={testing} className="tb-btn-secondary inline-flex items-center gap-2 disabled:opacity-50" data-testid="smtp-test-btn">
                    <Send className="w-4 h-4" /> {testing ? "Enviando..." : "Enviar prueba"}
                </button>
                <button onClick={save} className="tb-btn-primary inline-flex items-center gap-2" data-testid="smtp-save">
                    <Save className="w-4 h-4" /> Guardar SMTP
                </button>
            </div>
        </div>
    );
}



const CLEANUP_TARGETS = [
    { id: "orders", label: "Pedidos (también libera reservas)" },
    { id: "product_requests", label: "Solicitudes de producto" },
    { id: "incoming_products", label: "Productos en camino" },
    { id: "incoming_reservations", label: "Apartados en camino" },
    { id: "reviews", label: "Reseñas" },
    { id: "notifications", label: "Notificaciones" },
    { id: "tickets", label: "Tickets de soporte" },
    { id: "password_resets", label: "Tokens de recuperación" },
];

function DangerZone() {
    const [open, setOpen] = useState(false);
    const [password, setPassword] = useState("");
    const [targets, setTargets] = useState([]);
    const [loading, setLoading] = useState(false);

    const toggle = (id) => setTargets((cur) => cur.includes(id) ? cur.filter((t) => t !== id) : [...cur, id]);

    const submit = async (e) => {
        e.preventDefault();
        if (targets.length === 0) { toast.error("Selecciona al menos una colección"); return; }
        if (!window.confirm(`Esto eliminará PERMANENTEMENTE los registros seleccionados (${targets.length} colección/es). No hay vuelta atrás. ¿Continuar?`)) return;
        setLoading(true);
        try {
            const { data } = await api.post("/admin/cleanup-test-records", { password, targets });
            const summary = Object.entries(data.deleted).map(([k, v]) => `${k}: ${v}`).join(", ");
            toast.success(`Eliminado correctamente — ${summary}`);
            setOpen(false); setPassword(""); setTargets([]);
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error"); }
        finally { setLoading(false); }
    };

    return (
        <div className="tb-card p-6 md:col-span-2 border-red-500/30">
            <div className="flex items-center gap-3 mb-3">
                <AlertTriangle className="w-6 h-6 text-red-400" />
                <h3 className="font-display text-2xl text-red-300">Zona peligrosa</h3>
            </div>
            <p className="text-sm text-[var(--tb-text-2)] mb-4">
                Elimina datos de prueba acumulados durante el desarrollo o testing. Esta acción es <strong>permanente e irreversible</strong>. Solo afecta los registros seleccionados — los productos del inventario, usuarios y ajustes <strong>no se tocan</strong>.
            </p>
            {!open ? (
                <button onClick={() => setOpen(true)} className="tb-btn-secondary !border-red-400 !text-red-400 text-sm inline-flex items-center gap-2" data-testid="danger-open">
                    <Trash2 className="w-4 h-4" /> Eliminar registros de prueba
                </button>
            ) : (
                <form onSubmit={submit} className="space-y-4 border-t border-[var(--tb-border)] pt-4" data-testid="danger-form">
                    <div>
                        <div className="tb-overline mb-2">¿Qué colecciones eliminar?</div>
                        <div className="grid sm:grid-cols-2 gap-2">
                            {CLEANUP_TARGETS.map((t) => (
                                <label key={t.id} className="flex items-center gap-2 text-sm cursor-pointer">
                                    <input type="checkbox" checked={targets.includes(t.id)} onChange={() => toggle(t.id)} data-testid={`danger-target-${t.id}`} />
                                    <span>{t.label}</span>
                                </label>
                            ))}
                        </div>
                        <button type="button" onClick={() => setTargets(CLEANUP_TARGETS.map((t) => t.id))} className="tb-link text-xs mt-2">Seleccionar todo</button>
                    </div>
                    <div>
                        <label className="tb-overline">Confirma tu contraseña de admin *</label>
                        <input type="password" required className="tb-input" placeholder="Tu contraseña" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="danger-password" />
                    </div>
                    <div className="flex gap-2">
                        <button type="submit" disabled={loading} className="tb-btn-primary !bg-red-500 !text-white !text-sm disabled:opacity-50" data-testid="danger-submit">
                            {loading ? "Eliminando..." : "ELIMINAR PERMANENTEMENTE"}
                        </button>
                        <button type="button" onClick={() => { setOpen(false); setPassword(""); setTargets([]); }} className="tb-link text-sm">Cancelar</button>
                    </div>
                </form>
            )}
        </div>
    );
}

const REQ_STATUS_LABELS = {
    solicitado: "Solicitado", en_revision: "En revisión", cotizado: "Cotizado",
    aceptado: "Aceptado", en_proceso: "En proceso", en_camino_tienda: "En camino a tienda",
    listo_tienda: "Listo en tienda", en_reparto: "En reparto", entregado: "Entregado",
    cancelado: "Cancelado", rechazado: "Rechazado",
};
const REQ_STATUS_LIST = Object.keys(REQ_STATUS_LABELS);
const REQ_TYPE_LABELS = {
    vestido_15: "Vestido de 15", vestido_graduacion: "Vestido de graduación",
    vestido_novia: "Vestido de novia", traje: "Traje / Esmoquin",
    vestido_gala: "Vestido de gala", otro: "Otro",
};

function Solicitudes() {
    const [list, setList] = useState([]);
    const [filter, setFilter] = useState("all");
    const [selected, setSelected] = useState(null);
    const [quote, setQuote] = useState("");
    const [deposit, setDeposit] = useState("");
    const [tracking, setTracking] = useState("");
    const [note, setNote] = useState("");

    const load = async () => {
        const url = filter === "all" ? "/product-requests" : `/product-requests?status=${filter}`;
        const { data } = await api.get(url);
        setList(data);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

    const openDetail = async (rid) => {
        const { data } = await api.get(`/product-requests/${rid}`);
        setSelected(data);
        setQuote(data.quoted_price || "");
        setDeposit(data.deposit_percent || "");
        setTracking(data.tracking_number || "");
        setNote("");
    };

    const updateStatus = async (newStatus) => {
        if (!selected) return;
        try {
            const payload = { status: newStatus };
            if (note) payload.admin_note = note;
            const { data } = await api.patch(`/product-requests/${selected._id}/status`, payload);
            setSelected(data);
            toast.success("Estado actualizado");
            setNote("");
            load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const saveQuote = async () => {
        if (!selected) return;
        try {
            const payload = {};
            if (quote !== "") payload.quoted_price = parseFloat(quote);
            if (deposit !== "") payload.deposit_percent = parseInt(deposit);
            if (tracking !== "") payload.tracking_number = tracking;
            if (note) payload.admin_note = note;
            const { data } = await api.patch(`/product-requests/${selected._id}/status`, payload);
            setSelected(data);
            toast.success("Datos guardados");
            setNote("");
            load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    return (
        <div>
            <div className="flex items-center gap-3 mb-4 flex-wrap">
                <h2 className="font-display text-2xl flex-1">Productos Solicitados</h2>
                <select className="tb-input !w-auto" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="req-filter">
                    <option value="all">Todos</option>
                    {REQ_STATUS_LIST.map((s) => <option key={s} value={s}>{REQ_STATUS_LABELS[s]}</option>)}
                </select>
            </div>

            <div className="grid lg:grid-cols-2 gap-4">
                {/* List */}
                <div className="space-y-2 max-h-[70vh] overflow-y-auto">
                    {list.length === 0 ? (
                        <div className="tb-card p-6 text-center text-[var(--tb-text-2)]">Sin solicitudes</div>
                    ) : list.map((r) => (
                        <button key={r._id} onClick={() => openDetail(r._id)}
                            className={`tb-card p-4 w-full text-left hover:border-[var(--tb-gold)] transition ${selected?._id === r._id ? "border-[var(--tb-gold)]" : ""}`}
                            data-testid={`req-row-${r._id}`}>
                            <div className="flex justify-between items-start gap-2 mb-1">
                                <div className="font-mono text-xs text-[var(--tb-muted)]">{r._id}</div>
                                <span className="tb-badge tb-badge-gold !text-[10px]">{REQ_STATUS_LABELS[r.status]}</span>
                            </div>
                            <div className="font-medium text-sm">{r.user_name} · {r.items.length} prenda(s)</div>
                            <div className="text-xs text-[var(--tb-text-2)]">Para {r.needed_date} · {new Date(r.created_at).toLocaleDateString("es-SV")}</div>
                        </button>
                    ))}
                </div>

                {/* Detail */}
                <div>
                    {!selected ? (
                        <div className="tb-card p-8 text-center text-[var(--tb-text-2)]">Selecciona una solicitud</div>
                    ) : (
                        <div className="tb-card p-5 space-y-4 max-h-[70vh] overflow-y-auto" data-testid="req-detail">
                            <div className="flex justify-between items-start flex-wrap gap-2">
                                <div>
                                    <div className="font-mono text-xs text-[var(--tb-muted)]">{selected._id}</div>
                                    <div className="font-display text-xl">{selected.user_name}</div>
                                    <div className="text-xs text-[var(--tb-text-2)]">{selected.user_email}</div>
                                    {selected.customer_phone && <div className="text-xs">📞 {selected.customer_phone}</div>}
                                </div>
                                <span className="tb-badge tb-badge-gold">{REQ_STATUS_LABELS[selected.status]}</span>
                            </div>

                            <div className="grid grid-cols-2 gap-2 text-xs">
                                <div><span className="tb-overline">Necesaria:</span><div>{selected.needed_date}</div></div>
                                <div><span className="tb-overline">Solicitada:</span><div>{new Date(selected.created_at).toLocaleDateString("es-SV")}</div></div>
                            </div>

                            {selected.general_notes && (
                                <div className="text-sm border-l-2 border-[var(--tb-gold)] pl-3 italic">{selected.general_notes}</div>
                            )}

                            {/* Items */}
                            <div className="space-y-2">
                                {selected.items.map((it, idx) => (
                                    <div key={`it-${idx}`} className="border border-[var(--tb-border)] p-3 text-xs">
                                        <div className="flex justify-between mb-1">
                                            <span className="font-medium">{REQ_TYPE_LABELS[it.type] || it.type}</span>
                                            {it.estimated_price > 0 && <span className="text-[var(--tb-muted)]">~{formatPrice(it.estimated_price)}</span>}
                                        </div>
                                        <div className="font-medium text-[var(--tb-text)]">{it.name}</div>
                                        <div className="text-[var(--tb-text-2)] mt-1">
                                            {it.size && `Talla: ${it.size} · `}
                                            {it.color && `Color: ${it.color}`}
                                        </div>
                                        {it.notes && <div className="text-[var(--tb-muted)] mt-1">📝 {it.notes}</div>}
                                        {(it.reference_images || []).length > 0 && (
                                            <div className="flex gap-1 mt-2 flex-wrap">
                                                {it.reference_images.map((src, k) => (
                                                    <a key={`ref-${idx}-${k}`} href={src} target="_blank" rel="noreferrer">
                                                        <img src={src} alt="ref" className="w-14 h-14 object-cover rounded border border-[var(--tb-border)]" />
                                                    </a>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {/* Quote */}
                            <div className="border-t border-[var(--tb-border)] pt-3 space-y-2">
                                <div className="tb-overline text-[var(--tb-gold)]">Cotización y datos</div>
                                <div className="grid grid-cols-3 gap-2">
                                    <input type="number" step="0.01" className="tb-input !text-xs" placeholder="Precio USD" value={quote} onChange={(e) => setQuote(e.target.value)} data-testid="req-quote" />
                                    <input type="number" className="tb-input !text-xs" placeholder="Anticipo %" value={deposit} onChange={(e) => setDeposit(e.target.value)} data-testid="req-deposit" />
                                    <input className="tb-input !text-xs" placeholder="Nº guía" value={tracking} onChange={(e) => setTracking(e.target.value)} data-testid="req-tracking" />
                                </div>
                                <button onClick={saveQuote} className="tb-btn-secondary !text-xs">Guardar cotización/datos</button>
                            </div>

                            {/* Status changer */}
                            <div className="border-t border-[var(--tb-border)] pt-3 space-y-2">
                                <div className="tb-overline text-[var(--tb-gold)]">Cambiar estado</div>
                                <textarea className="tb-input !text-xs min-h-[60px]" placeholder="Nota interna / mensaje al cliente (opcional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="req-note" />
                                <div className="flex flex-wrap gap-1.5">
                                    {REQ_STATUS_LIST.filter((s) => s !== selected.status).map((s) => (
                                        <button key={s} onClick={() => updateStatus(s)} className="text-[10px] px-2 py-1 border border-[var(--tb-border)] hover:border-[var(--tb-gold)] hover:text-[var(--tb-gold)]" data-testid={`req-set-${s}`}>
                                            {REQ_STATUS_LABELS[s]}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {selected.ticket_id && (
                                <a href={`/soporte?tid=${selected.ticket_id}`} className="tb-btn-primary !text-xs inline-flex items-center gap-1.5" data-testid="req-open-chat">
                                    <Bell className="w-3.5 h-3.5" /> Abrir chat con cliente
                                </a>
                            )}

                            {/* Status history */}
                            {(selected.status_history || []).length > 1 && (
                                <div className="border-t border-[var(--tb-border)] pt-3">
                                    <div className="tb-overline text-[var(--tb-gold)] mb-2">Historial</div>
                                    <div className="text-xs space-y-1">
                                        {selected.status_history.slice().reverse().map((h, idx) => (
                                            <div key={`hist-${idx}`} className="text-[var(--tb-text-2)]">
                                                <span className="font-medium text-[var(--tb-text)]">{REQ_STATUS_LABELS[h.status]}</span>
                                                <span className="text-[var(--tb-muted)]"> · {new Date(h.at).toLocaleString("es-SV")}</span>
                                                {h.note && <div className="text-[var(--tb-muted)] pl-2">↳ {h.note}</div>}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}


const RES_STATUS_LABELS = {
    pendiente_pago: "Pendiente pago",
    anticipo_pagado: "Anticipo pagado",
    producto_recibido: "Producto recibido",
    completada: "Completada",
    cancelada: "Cancelada",
};
const RES_STATUS_LIST = Object.keys(RES_STATUS_LABELS);

const compressIncomingImg = (file) => new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
        const canvas = document.createElement("canvas");
        const maxW = 1400;
        const scale = Math.min(1, maxW / img.width);
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.82));
    };
    img.src = URL.createObjectURL(file);
});

function IncomingAdmin() {
    const [items, setItems] = useState([]);
    const [reservations, setReservations] = useState([]);
    const [editing, setEditing] = useState(null);
    const blank = { sku: "", name: "", description: "", images: [], estimated_price: 0, estimated_arrival: "", deposit_percent: 30, quantity_available: 1, accepts_reservation: true, active: true, categoria: "", condicion: "Nuevo" };

    const load = async () => {
        const [a, b] = await Promise.all([
            api.get("/incoming-products?all_status=true"),
            api.get("/incoming-reservations"),
        ]);
        setItems(a.data);
        setReservations(b.data);
    };
    useEffect(() => { load().catch(() => {}); }, []);

    const startNew = () => setEditing({ ...blank });
    const cancelEdit = () => setEditing(null);

    const onPickImgs = async (files) => {
        const arr = Array.from(files).slice(0, 8 - (editing.images?.length || 0));
        const urls = await Promise.all(arr.map(compressIncomingImg));
        setEditing({ ...editing, images: [...(editing.images || []), ...urls].slice(0, 8) });
    };
    const removeImg = (idx) => setEditing({ ...editing, images: editing.images.filter((_, i) => i !== idx) });

    const save = async () => {
        if (!editing.name.trim()) { toast.error("Nombre requerido"); return; }
        if (!editing.images?.length) { toast.error("Agrega al menos una foto"); return; }
        try {
            const payload = {
                ...editing,
                estimated_price: parseFloat(editing.estimated_price || 0) || 0,
                deposit_percent: parseInt(editing.deposit_percent || 30) || 30,
            };
            if (editing._id) await api.put(`/incoming-products/${editing._id}`, payload);
            else await api.post("/incoming-products", payload);
            toast.success("Producto guardado");
            setEditing(null);
            await load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const remove = async (iid) => {
        if (!window.confirm("¿Eliminar este producto en camino?")) return;
        await api.delete(`/incoming-products/${iid}`);
        toast.success("Eliminado");
        await load();
    };

    const moveToInventory = async (iid) => {
        if (!window.confirm("¿Liberar este producto al inventario regular?\n\nEsto:\n• Crea el producto en el catálogo público con stock disponible.\n• Asocia los apartados actuales al nuevo SKU.\n• Desactiva la entrada 'en camino'.\n\n¿Continuar?")) return;
        try {
            const { data } = await api.post(`/incoming-products/${iid}/release`);
            toast.success(data.message || `Liberado con SKU ${data.sku}`);
            await load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al liberar"); }
    };

    const updateResStatus = async (rid, status) => {
        try {
            await api.patch(`/incoming-reservations/${rid}/status`, { status });
            toast.success("Estado actualizado");
            await load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    return (
        <div className="space-y-8">
            <div>
                <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                    <h2 className="font-display text-2xl">Productos en Camino</h2>
                    <button onClick={startNew} className="tb-btn-primary inline-flex items-center gap-2 !text-sm" data-testid="incoming-new">
                        <Plus className="w-4 h-4" /> Nuevo producto en tránsito
                    </button>
                </div>

                {editing && (
                    <div className="tb-card p-5 space-y-3 mb-4" data-testid="incoming-form">
                        <div className="grid md:grid-cols-2 gap-3">
                            <div>
                                <label className="tb-overline">ID / SKU del producto *</label>
                                <input className="tb-input font-mono" placeholder="Ej. T-0201" value={editing.sku || ""} onChange={(e) => setEditing({ ...editing, sku: e.target.value.toUpperCase() })} data-testid="incoming-sku-in" />
                                <div className="text-[10px] text-[var(--tb-muted)] mt-1">Si lo dejas vacío al pasar a inventario, se genera automáticamente.</div>
                            </div>
                            <div>
                                <label className="tb-overline">Cantidad disponible (unidades en camino) *</label>
                                <input type="number" min="1" className="tb-input" value={editing.quantity_available || 1} onChange={(e) => setEditing({ ...editing, quantity_available: parseInt(e.target.value || 1) })} data-testid="incoming-qty-in" />
                            </div>
                            <div className="md:col-span-2">
                                <label className="tb-overline">Nombre *</label>
                                <input className="tb-input" placeholder="Ej. Vestido floral primavera" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="incoming-name-in" />
                            </div>
                            <div className="md:col-span-2">
                                <label className="tb-overline">Descripción</label>
                                <textarea className="tb-input min-h-[60px]" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
                            </div>
                            <div>
                                <label className="tb-overline">Precio estimado USD (por unidad)</label>
                                <input type="number" step="0.01" min="0" className="tb-input" value={editing.estimated_price} onChange={(e) => setEditing({ ...editing, estimated_price: e.target.value })} data-testid="incoming-price-in" />
                            </div>
                            <div>
                                <label className="tb-overline">Llegada estimada</label>
                                <input className="tb-input" placeholder="Ej. Octubre 2026" value={editing.estimated_arrival} onChange={(e) => setEditing({ ...editing, estimated_arrival: e.target.value })} />
                            </div>
                            <div>
                                <label className="tb-overline">Categoría</label>
                                <select className="tb-input" value={editing.categoria || ""} onChange={(e) => setEditing({ ...editing, categoria: e.target.value })}>
                                    <option value="">— Seleccionar —</option>
                                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="tb-overline">Condición</label>
                                <select className="tb-input" value={editing.condicion || "Nuevo"} onChange={(e) => setEditing({ ...editing, condicion: e.target.value })}>
                                    <option value="Nuevo">Nuevo</option>
                                    <option value="Como nuevo">Usado / Como nuevo</option>
                                    <option value="Usado">Usado</option>
                                </select>
                            </div>
                            <div>
                                <label className="tb-overline">% Anticipo</label>
                                <input type="number" min="0" max="100" className="tb-input" value={editing.deposit_percent} onChange={(e) => setEditing({ ...editing, deposit_percent: e.target.value })} />
                            </div>
                            <div className="flex items-center gap-4 pt-5">
                                <label className="flex items-center gap-2 text-sm">
                                    <input type="checkbox" checked={!!editing.accepts_reservation} onChange={(e) => setEditing({ ...editing, accepts_reservation: e.target.checked })} /> Acepta apartado
                                </label>
                                <label className="flex items-center gap-2 text-sm">
                                    <input type="checkbox" checked={!!editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} /> Visible en sitio
                                </label>
                            </div>
                        </div>

                        <div>
                            <label className="tb-overline">Fotos (hasta 8)</label>
                            <div className="flex flex-wrap gap-2 mt-2">
                                {(editing.images || []).map((src, i) => (
                                    <div key={`img-${i}`} className="relative w-24 h-24 group">
                                        <img src={src} alt="" className="w-24 h-24 object-cover rounded border border-[var(--tb-border)]" />
                                        <button onClick={() => removeImg(i)} className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"><Trash2 className="w-3 h-3" /></button>
                                    </div>
                                ))}
                                {(editing.images?.length || 0) < 8 && (
                                    <label className="w-24 h-24 border-2 border-dashed border-[var(--tb-border)] rounded flex flex-col items-center justify-center cursor-pointer hover:border-[var(--tb-gold)] text-[var(--tb-muted)] text-[10px] gap-1">
                                        <Upload className="w-4 h-4" />
                                        <span>Subir</span>
                                        <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => onPickImgs(e.target.files)} data-testid="incoming-upload" />
                                    </label>
                                )}
                            </div>
                        </div>

                        <div className="flex gap-2">
                            <button onClick={save} className="tb-btn-primary !text-sm inline-flex items-center gap-2" data-testid="incoming-save"><Save className="w-4 h-4" /> Guardar</button>
                            <button onClick={cancelEdit} className="tb-link text-sm">Cancelar</button>
                        </div>
                    </div>
                )}

                {/* Grid */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {items.length === 0 && !editing ? (
                        <div className="tb-card p-6 text-center text-sm text-[var(--tb-text-2)] col-span-full">Sin productos en camino. Crea el primero arriba.</div>
                    ) : items.map((it) => {
                        const remaining = Math.max(0, (parseInt(it.quantity_available || 0)) - (parseInt(it.quantity_reserved || 0)));
                        return (
                            <div key={it._id} className="tb-card overflow-hidden" data-testid={`incoming-row-${it._id}`}>
                                <div className="aspect-square bg-[var(--tb-bg-2)] relative">
                                    {it.images?.[0] && <img src={it.images[0]} alt="" className="w-full h-full object-cover" />}
                                    {!it.active && <span className="absolute top-2 left-2 tb-badge tb-badge-muted">Oculto</span>}
                                    {it.moved_to_inventory && <span className="absolute top-2 right-2 tb-badge tb-badge-success !text-[10px]">En inventario</span>}
                                </div>
                                <div className="p-3 space-y-1">
                                    {it.sku && <div className="text-[10px] font-mono text-[var(--tb-muted)]">{it.sku}</div>}
                                    <div className="font-medium text-sm truncate">{it.name}</div>
                                    <div className="text-xs text-[var(--tb-text-2)]">{formatPrice(it.estimated_price || 0)} c/u · {it.deposit_percent || 30}% anticipo</div>
                                    <div className="text-xs">
                                        <span className="text-[var(--tb-gold)] font-medium">{remaining}</span> de {it.quantity_available || 0} disponible(s)
                                        {(it.quantity_reserved || 0) > 0 && <span className="text-[var(--tb-muted)]"> · {it.quantity_reserved} apartado(s)</span>}
                                    </div>
                                    {it.estimated_arrival && <div className="text-[10px] text-[var(--tb-muted)]">Llega: {it.estimated_arrival}</div>}
                                    <div className="flex flex-wrap gap-2 pt-2">
                                        <button onClick={() => setEditing({ ...it })} className="text-xs tb-link inline-flex items-center gap-1" data-testid={`incoming-edit-${it._id}`}><Edit3 className="w-3 h-3" /> Editar</button>
                                        {it.active !== false && !it.released_at && (
                                            <button onClick={() => moveToInventory(it._id)}
                                                className="text-xs bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25 rounded px-2 py-1 inline-flex items-center gap-1 font-medium transition"
                                                data-testid={`incoming-release-${it._id}`}>
                                                <Package className="w-3 h-3" /> Liberar al inventario
                                            </button>
                                        )}
                                        {it.released_to_sku && (
                                            <span className="text-[10px] text-emerald-400 inline-flex items-center gap-1">
                                                ✓ Liberado como <strong>{it.released_to_sku}</strong>
                                            </span>
                                        )}
                                        <button onClick={() => remove(it._id)} className="text-xs text-red-500 inline-flex items-center gap-1 ml-auto"><Trash2 className="w-3 h-3" /> Eliminar</button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Reservations */}
            <div>
                <h3 className="font-display text-xl mb-3">Apartados de productos en camino</h3>
                {reservations.length === 0 ? (
                    <div className="tb-card p-5 text-center text-sm text-[var(--tb-text-2)]">Sin apartados aún.</div>
                ) : (
                    <div className="space-y-2">
                        {reservations.map((r) => (
                            <div key={r._id} className="tb-card p-4" data-testid={`incoming-res-${r._id}`}>
                                <div className="flex justify-between items-start gap-3 flex-wrap mb-2">
                                    <div className="flex items-center gap-3">
                                        {r.incoming_image && <img src={r.incoming_image} alt="" className="w-12 h-12 object-cover rounded" />}
                                        <div>
                                            <div className="font-mono text-xs text-[var(--tb-muted)]">{r._id}</div>
                                            <div className="font-medium text-sm">{r.incoming_name} · {r.customer_name}</div>
                                            <div className="text-xs text-[var(--tb-text-2)]">📞 {r.customer_phone} · Anticipo: {formatPrice(r.deposit_amount)} ({r.deposit_percent}% de {formatPrice(r.estimated_price)}) · {r.payment_method}</div>
                                            {r.notes && <div className="text-xs text-[var(--tb-muted)] italic">📝 {r.notes}</div>}
                                        </div>
                                    </div>
                                    <span className="tb-badge tb-badge-gold !text-[10px]">{RES_STATUS_LABELS[r.status] || r.status}</span>
                                </div>
                                <div className="flex flex-wrap gap-1.5 pt-2 border-t border-[var(--tb-border)]">
                                    {RES_STATUS_LIST.filter((s) => s !== r.status).map((s) => (
                                        <button key={`${r._id}-${s}`} onClick={() => updateResStatus(r._id, s)} className="text-[10px] px-2 py-1 border border-[var(--tb-border)] hover:border-[var(--tb-gold)] hover:text-[var(--tb-gold)]" data-testid={`incoming-res-${r._id}-${s}`}>
                                            {RES_STATUS_LABELS[s]}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}


function Herramientas() {
    const [sub, setSub] = useState("anuncios");
    const subs = [
        { id: "anuncios", label: "Anuncios", icon: Bell, desc: "Comunica novedades, ofertas y avisos a tus clientes" },
        { id: "descuentos", label: "Descuentos", icon: Sparkles, desc: "Crea promociones globales o por categoría" },
        { id: "cupones", label: "Cupones", icon: BadgeCheck, desc: "Códigos de descuento que tus clientes ingresan en checkout" },
        { id: "sugerencias", label: "Descuento de sugerencia", icon: ThumbsUp, desc: "Campaña mensual: usuarios dejan una sugerencia y reciben crédito en su cuenta" },
        { id: "lanzamiento", label: "Lanzamiento / Mantenimiento", icon: Rocket, desc: "Bloquea compras hasta el día del lanzamiento o pausa el sitio para mantenimiento" },
        { id: "feedback", label: "Feedback", icon: MessageSquare, desc: "Opiniones que tus usuarios envían desde el botón flotante (puede ser anónimo)" },
        { id: "reviews", label: "Reseñas del sitio", icon: Star, desc: "Reseñas públicas sobre la experiencia general (modera antes de publicar)" },
        { id: "tema", label: "Tema", icon: Palette, desc: "Paleta de colores por país (SV y USA)" },
        { id: "ventas", label: "Ventas", icon: ShoppingBag, desc: "Historial de ventas con filtros, KPIs y exportación CSV" },
        { id: "tickets", label: "Tickets", icon: Mail, desc: "Soporte: conversaciones con clientes y resolución" },
        { id: "analytics", label: "Análisis", icon: TrendingUp, desc: "Métricas, ventas y comportamiento de la tienda" },
        { id: "vendedores", label: "Vendedores (SV)", icon: User, desc: "Promueve usuarios verificados a vendedores, ajusta comisión y suscripción" },
        { id: "solicitudes-vendedor", label: "Solicitudes de Vendedor", icon: Store, desc: "Revisa las solicitudes que los usuarios envían para convertirse en vendedores. Aprueba con modelo de cobro personalizado" },
        { id: "socios", label: "Socios (Afiliados)", icon: Handshake, desc: "Códigos únicos vinculados a usuarios: rastrea ventas, comisiones opcionales y reconoce a promotores" },
        { id: "banner-vendedores", label: "Banner Vendedores", icon: ImageIcon, desc: "Banner horizontal con texto informativo en la parte superior de /vendedores. Actívalo/ocúltalo cuando quieras" },
        { id: "traduccion", label: "Traducción EN", icon: Languages, desc: "Traduce en lote nombres, descripciones y categorías al inglés con IA (para el sitio USA)" },
        { id: "discord-sync", label: "Sincronización Discord", icon: Bot, desc: "Sincronización bidireccional del inventario con tu bot de Discord (SV). Ver estado, historial y probar conexión" },
        { id: "catalogo-discord", label: "Catálogo Discord", icon: Tag, desc: "Lee en vivo el inventario del bot y edita nombres, descripciones, precios y categorías. Los cambios se reflejan en Discord y en la tienda" },
    ];
    return (
        <div className="space-y-6" data-testid="herramientas-section">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h2 className="font-display text-2xl mb-1">Herramientas, Análisis y Operaciones</h2>
                    <p className="text-xs text-[var(--tb-text-2)]">Marketing, ventas, soporte y análisis en un solo lugar</p>
                </div>
                <HideByIdTool />
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {subs.map((s) => {
                    const Icon = s.icon;
                    const active = sub === s.id;
                    return (
                        <button key={s.id} onClick={() => setSub(s.id)}
                            data-testid={`herramientas-sub-${s.id}`}
                            className={`text-left p-4 border transition rounded ${active ? "border-[var(--tb-gold)] bg-[var(--tb-border-gold)]/10" : "border-[var(--tb-border)] hover:border-[var(--tb-gold)]/60"}`}>
                            <div className="flex items-center gap-2 mb-1">
                                <Icon className={`w-4 h-4 ${active ? "text-[var(--tb-gold)]" : "text-[var(--tb-text-2)]"}`} />
                                <span className={`text-sm font-medium ${active ? "text-[var(--tb-gold)]" : "text-[var(--tb-text)]"}`}>{s.label}</span>
                            </div>
                            <p className="text-[11px] text-[var(--tb-text-2)] leading-relaxed">{s.desc}</p>
                        </button>
                    );
                })}
            </div>
            <div className="pt-2">
                {sub === "anuncios" && <Anuncios />}
                {sub === "descuentos" && <Descuentos />}
                {sub === "cupones" && <Cupones />}
                {sub === "sugerencias" && <SuggestionCampaignAdmin />}
                {sub === "lanzamiento" && <LaunchControl />}
                {sub === "feedback" && <FeedbackAdmin />}
                {sub === "reviews" && <SiteReviewsAdmin />}
                {sub === "tema" && <Tema />}
                {sub === "ventas" && <Ventas />}
                {sub === "tickets" && <Tickets />}
                {sub === "analytics" && <Analytics />}
                {sub === "vendedores" && <VendorAdminPanel />}
                {sub === "solicitudes-vendedor" && <VendorApplicationsAdmin />}
                {sub === "socios" && <SociosAdmin />}
                {sub === "banner-vendedores" && <VendorsBannerAdmin />}
                {sub === "traduccion" && <TranslateAllPanel />}
                {sub === "discord-sync" && <InventorySyncAdmin />}
                {sub === "catalogo-discord" && <CatalogoDiscordAdmin />}
            </div>
        </div>
    );
}

// ---------- Panel Vendedores (Admin) ----------
function VendorAdminPanel() {
    const [users, setUsers] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [defaults, setDefaults] = useState({ vendor_default_commission: 15, vendor_default_subscription: 0 });
    const [search, setSearch] = useState("");
    const [editing, setEditing] = useState(null);
    const [promoteFor, setPromoteFor] = useState(null); // user object being promoted
    const load = async () => {
        const [u, v, s] = await Promise.all([
            api.get("/admin/users").then((r) => r.data).catch(() => []),
            api.get("/admin/vendors").then((r) => r.data).catch(() => []),
            api.get("/settings").then((r) => r.data).catch(() => ({})),
        ]);
        setUsers(u); setVendors(v);
        setDefaults({
            vendor_default_commission: s.vendor_default_commission ?? 15,
            vendor_default_subscription: s.vendor_default_subscription ?? 0,
        });
    };
    useEffect(() => { load(); }, []);

    const saveDefaults = async () => {
        try { await api.put("/settings", defaults); toast.success("Defaults guardados"); }
        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    const promote = async (uid, extra = {}) => {
        try {
            await api.post(`/admin/users/${uid}/promote-vendor`, extra);
            toast.success("Usuario promovido a vendedor");
            setPromoteFor(null);
            load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error"); }
    };
    const demote = async (uid) => {
        if (!window.confirm("¿Quitar el rol de vendedor a este usuario?")) return;
        try { await api.post(`/admin/users/${uid}/demote-vendor`); toast.success("Rol removido"); load(); }
        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error"); }
    };
    const saveVendor = async (uid, data) => {
        try {
            await api.patch(`/admin/users/${uid}/vendor-settings`, data);
            toast.success("Guardado"); setEditing(null); load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error"); }
    };

    const eligibles = users.filter((u) => u.is_verified && u.role !== "vendedor" && u.role !== "admin"
        && (!search || (u.email + " " + (u.name || "")).toLowerCase().includes(search.toLowerCase())));

    return (
        <div className="space-y-5" data-testid="vendors-admin">
            <div className="tb-card p-4">
                <h3 className="font-medium mb-3">Valores por defecto</h3>
                <div className="grid grid-cols-2 gap-3">
                    <label className="text-xs">Comisión default (%)
                        <input type="number" step="0.5" className="tb-input mt-1" value={defaults.vendor_default_commission}
                            onChange={(e) => setDefaults({ ...defaults, vendor_default_commission: parseFloat(e.target.value || 0) })} data-testid="vendor-default-commission" />
                    </label>
                    <label className="text-xs">Suscripción mensual default (USD)
                        <input type="number" step="0.01" className="tb-input mt-1" value={defaults.vendor_default_subscription}
                            onChange={(e) => setDefaults({ ...defaults, vendor_default_subscription: parseFloat(e.target.value || 0) })} data-testid="vendor-default-subscription" />
                    </label>
                </div>
                <button onClick={saveDefaults} className="tb-btn-primary !text-xs mt-3 inline-flex items-center gap-1"><Save className="w-3.5 h-3.5" /> Guardar defaults</button>
                <div className="text-[10px] text-[var(--tb-muted)] mt-2">Estos valores se usan al promover un nuevo vendedor. Cada vendedor solo cobra por UN modelo (suscripción o comisión), no ambos.</div>
            </div>

            <VendorReportsPanel />
            <ModerationQueuePanel />
            <VendorSubscriptionCharges />

            <div className="tb-card p-4">
                <h3 className="font-medium mb-3">Vendedores activos ({vendors.length})</h3>
                {vendors.length === 0 ? <div className="text-xs text-[var(--tb-muted)]">Aún no hay vendedores.</div> : (
                    <div className="space-y-2">
                        {vendors.map((v) => (
                            <div key={v.id} className="border border-[var(--tb-border)] rounded p-3 flex items-center gap-3 flex-wrap" data-testid={`vendor-row-${v.id}`}>
                                {v.foto ? <img src={v.foto} alt="" className="w-10 h-10 rounded-full object-cover" /> : <div className="w-10 h-10 rounded-full bg-[var(--tb-bg-2)]" />}
                                <div className="flex-1 min-w-0">
                                    <div className="font-medium text-sm truncate">
                                        {v.display_name} <span className="text-[10px] text-[var(--tb-muted)]">· {v.categoria || "—"}</span>
                                        {v.profile_views > 0 && <span className="text-[10px] text-[var(--tb-muted)] ml-2">👁 {v.profile_views}</span>}
                                    </div>
                                    <div className="text-[10px] text-[var(--tb-muted)]">{v.email} · {v.real_name}</div>
                                </div>
                                {editing === v.id ? (
                                    <VendorInlineEdit v={v} onCancel={() => setEditing(null)} onSave={(d) => saveVendor(v.id, d)} />
                                ) : (
                                    <>
                                        <div className="text-xs text-right">
                                            {v.billing_model === "subscription" ? (
                                                <div>
                                                    <span className="tb-badge tb-badge-gold !text-[9px]">SUSCRIPCIÓN</span>
                                                    <div className="text-[var(--tb-gold)] mt-1">{formatPrice(v.subscription_fee)}/mes</div>
                                                </div>
                                            ) : (
                                                <div>
                                                    <span className="tb-badge tb-badge-warm !text-[9px]">COMISIÓN</span>
                                                    <div className="text-[var(--tb-gold)] mt-1">{v.commission_percent}% / venta</div>
                                                </div>
                                            )}
                                        </div>
                                        <button onClick={() => setEditing(v.id)} className="tb-btn-ghost !p-1.5" aria-label="Editar" data-testid={`vendor-edit-${v.id}`}><Edit3 className="w-3.5 h-3.5" /></button>
                                        <button onClick={() => demote(v.id)} className="tb-btn-ghost !p-1.5 text-rose-400" aria-label="Quitar"><X className="w-3.5 h-3.5" /></button>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="tb-card p-4">
                <h3 className="font-medium mb-2">Promover usuario</h3>
                <div className="text-[10px] text-[var(--tb-muted)] mb-2">Solo aparecen usuarios verificados (check azul).</div>
                <input className="tb-input mb-2" placeholder="Buscar por correo o nombre..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="vendor-user-search" />
                <div className="space-y-1 max-h-64 overflow-y-auto">
                    {eligibles.length === 0 ? <div className="text-xs text-[var(--tb-muted)] py-2">Ningún usuario verificado disponible.</div>
                        : eligibles.map((u) => (
                            <div key={u._id} className="flex items-center justify-between text-xs border border-[var(--tb-border)] rounded px-2 py-1.5">
                                <div className="min-w-0"><div className="truncate">{u.name}</div><div className="text-[10px] text-[var(--tb-muted)] truncate">{u.email}</div></div>
                                <button onClick={() => setPromoteFor(u)} className="tb-btn-primary !text-[11px] !px-2 !py-1 inline-flex items-center gap-1" data-testid={`vendor-promote-${u._id}`}>
                                    <Plus className="w-3 h-3" /> Promover
                                </button>
                            </div>
                        ))}
                </div>
            </div>

            {promoteFor && (
                <VendorPromoteModal
                    user={promoteFor}
                    defaults={defaults}
                    onCancel={() => setPromoteFor(null)}
                    onConfirm={(payload) => promote(promoteFor._id, payload)}
                />
            )}
        </div>
    );
}

function VendorPromoteModal({ user, defaults, onCancel, onConfirm }) {
    const [model, setModel] = useState("commission");
    const [commission, setCommission] = useState(defaults.vendor_default_commission ?? 15);
    const [subscription, setSubscription] = useState(defaults.vendor_default_subscription ?? 5);
    return (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-3" onClick={(e) => e.target === e.currentTarget && onCancel()}>
            <div className="tb-card p-5 w-full max-w-md space-y-4" data-testid="vendor-promote-modal">
                <div>
                    <h3 className="font-display text-xl">Promover a vendedor</h3>
                    <p className="text-xs text-[var(--tb-text-2)] mt-1">{user.name} · {user.email}</p>
                </div>
                <div className="space-y-2">
                    <div className="text-xs font-medium">Modelo de cobro</div>
                    <label className={`block p-3 rounded border cursor-pointer transition ${model === "commission" ? "border-[var(--tb-gold)] bg-[var(--tb-border-gold)]/10" : "border-[var(--tb-border)]"}`}>
                        <input type="radio" name="promote-model" checked={model === "commission"} onChange={() => setModel("commission")} className="mr-2" data-testid="promote-model-commission" />
                        <span className="text-sm font-medium">Comisión por venta</span>
                        <div className="text-[10px] text-[var(--tb-muted)] mt-1 pl-6">Sin suscripción. Cobras un % de cada venta suya.</div>
                    </label>
                    <label className={`block p-3 rounded border cursor-pointer transition ${model === "subscription" ? "border-[var(--tb-gold)] bg-[var(--tb-border-gold)]/10" : "border-[var(--tb-border)]"}`}>
                        <input type="radio" name="promote-model" checked={model === "subscription"} onChange={() => setModel("subscription")} className="mr-2" data-testid="promote-model-subscription" />
                        <span className="text-sm font-medium">Suscripción mensual</span>
                        <div className="text-[10px] text-[var(--tb-muted)] mt-1 pl-6">Pago fijo mensual. El vendedor se queda con 100% de sus ventas.</div>
                    </label>
                </div>
                {model === "commission" ? (
                    <label className="text-xs block">Comisión (%)
                        <input type="number" step="0.5" min="0" max="90" className="tb-input mt-1" value={commission}
                            onChange={(e) => setCommission(parseFloat(e.target.value || 0))} data-testid="promote-commission-input" />
                    </label>
                ) : (
                    <label className="text-xs block">Suscripción mensual (USD)
                        <input type="number" step="0.01" min="0" className="tb-input mt-1" value={subscription}
                            onChange={(e) => setSubscription(parseFloat(e.target.value || 0))} data-testid="promote-subscription-input" />
                    </label>
                )}
                <div className="flex justify-end gap-2 pt-2">
                    <button onClick={onCancel} className="tb-btn-secondary !text-xs">Cancelar</button>
                    <button onClick={() => onConfirm({
                        billing_model: model,
                        commission_percent: model === "commission" ? commission : 0,
                        subscription_fee: model === "subscription" ? subscription : 0,
                    })} className="tb-btn-primary !text-xs" data-testid="promote-confirm-btn">Confirmar promoción</button>
                </div>
            </div>
        </div>
    );
}

function VendorInlineEdit({ v, onCancel, onSave }) {
    const [model, setModel] = useState(v.billing_model || (v.commission_percent > 0 ? "commission" : "subscription"));
    const [comm, setComm] = useState(v.commission_percent);
    const [sub, setSub] = useState(v.subscription_fee);
    return (
        <div className="flex flex-col gap-2 w-full mt-2 pt-2 border-t border-[var(--tb-border)]">
            <div className="flex gap-3 text-[11px]">
                <label className="inline-flex items-center gap-1 cursor-pointer">
                    <input type="radio" checked={model === "commission"} onChange={() => setModel("commission")} data-testid={`vendor-edit-model-comm-${v.id}`} />
                    Comisión %
                </label>
                <label className="inline-flex items-center gap-1 cursor-pointer">
                    <input type="radio" checked={model === "subscription"} onChange={() => setModel("subscription")} data-testid={`vendor-edit-model-sub-${v.id}`} />
                    Suscripción $/mes
                </label>
            </div>
            <div className="flex items-center gap-1 flex-wrap">
                {model === "commission" ? (
                    <input type="number" step="0.5" value={comm} onChange={(e) => setComm(parseFloat(e.target.value || 0))} className="tb-input !w-24 !py-1 !text-xs" placeholder="%" data-testid={`vendor-edit-comm-${v.id}`} />
                ) : (
                    <input type="number" step="0.01" value={sub} onChange={(e) => setSub(parseFloat(e.target.value || 0))} className="tb-input !w-24 !py-1 !text-xs" placeholder="USD" data-testid={`vendor-edit-sub-${v.id}`} />
                )}
                <button onClick={() => onSave({
                    billing_model: model,
                    commission_percent: model === "commission" ? comm : 0,
                    subscription_fee: model === "subscription" ? sub : 0,
                })} className="tb-btn-primary !p-1.5" data-testid={`vendor-edit-save-${v.id}`}><Save className="w-3.5 h-3.5" /></button>
                <button onClick={onCancel} className="tb-btn-ghost !p-1.5"><X className="w-3.5 h-3.5" /></button>
            </div>
        </div>
    );
}

function VendorReportsPanel() {
    const now = new Date();
    const cur = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    const [month, setMonth] = useState(cur);
    const [busy, setBusy] = useState(false);
    const download = async () => {
        setBusy(true);
        try {
            const res = await api.get(`/admin/vendors/commissions-report.xlsx?month=${month}`, { responseType: "blob" });
            const url = window.URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url; a.download = `comisiones-${month}.xlsx`;
            document.body.appendChild(a); a.click();
            setTimeout(() => { window.URL.revokeObjectURL(url); a.remove(); }, 100);
            toast.success("Reporte descargado");
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo generar el reporte"); }
        finally { setBusy(false); }
    };
    return (
        <div className="tb-card p-4" data-testid="vendor-reports-panel">
            <h3 className="font-medium mb-2">Reporte de comisiones (Excel)</h3>
            <p className="text-[10px] text-[var(--tb-muted)] mb-3">Descarga un XLSX con ingresos, comisiones cobradas y neto pagado por vendedor para el mes seleccionado. Ideal para tu contabilidad.</p>
            <div className="flex flex-wrap items-end gap-2">
                <label className="text-xs">Mes
                    <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="tb-input mt-1" data-testid="vendor-report-month" />
                </label>
                <button onClick={download} disabled={busy} className="tb-btn-primary !text-xs inline-flex items-center gap-1" data-testid="vendor-report-download">
                    <Download className="w-3.5 h-3.5" /> {busy ? "Generando..." : "Descargar Excel"}
                </button>
            </div>
        </div>
    );
}

function VendorSubscriptionCharges() {
    const [items, setItems] = useState([]);
    const [running, setRunning] = useState(false);
    const load = () => api.get("/admin/vendors/subscription-charges").then(({ data }) => setItems(data)).catch(() => setItems([]));
    useEffect(() => { load(); }, []);
    const markPaid = async (id) => {
        try { await api.patch(`/admin/vendors/subscription-charges/${id}`, { status: "paid" }); toast.success("Marcado pagado"); load(); }
        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error"); }
    };
    const waive = async (id) => {
        if (!window.confirm("¿Marcar como cortesía (waived)?")) return;
        try { await api.patch(`/admin/vendors/subscription-charges/${id}`, { status: "waived" }); toast.success("Cargo condonado"); load(); }
        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error"); }
    };
    const runNow = async () => {
        setRunning(true);
        try {
            const { data } = await api.post("/admin/vendors/subscription-billing/run");
            toast.success(`Ciclo ejecutado: ${data.charged} cargos, ${data.reminded} recordatorios`);
            load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error"); }
        finally { setRunning(false); }
    };
    return (
        <div className="tb-card p-4" data-testid="vendor-subscription-charges">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <h3 className="font-medium">Cargos de suscripción</h3>
                <button onClick={runNow} disabled={running} className="tb-btn-secondary !text-xs inline-flex items-center gap-1" data-testid="run-subscription-billing">
                    <RefreshCw className={`w-3.5 h-3.5 ${running ? "animate-spin" : ""}`} /> Ejecutar ciclo ahora
                </button>
            </div>
            <p className="text-[10px] text-[var(--tb-muted)] mb-3">El sistema envía un recordatorio 3 días antes del cobro y registra el cargo en la fecha. Cambia el estado a &quot;Pagado&quot; cuando confirmes recepción del pago.</p>
            {items.length === 0 ? (
                <div className="text-xs text-[var(--tb-muted)]">Aún no hay cargos registrados.</div>
            ) : (
                <div className="space-y-1">
                    {items.map((c) => (
                        <div key={c.id} className="border border-[var(--tb-border)] rounded p-2 flex items-center justify-between gap-2 flex-wrap text-xs" data-testid={`subscription-charge-${c.id}`}>
                            <div className="flex-1 min-w-0">
                                <div className="font-medium truncate">{c.vendor_display_name}</div>
                                <div className="text-[10px] text-[var(--tb-muted)]">{c.vendor_email} · Vencimiento {c.due_date}</div>
                            </div>
                            <div className="text-[var(--tb-gold)] font-semibold">{formatPrice(c.amount)}</div>
                            <span className={`tb-badge !text-[9px] ${c.status === "paid" ? "tb-badge-success" : c.status === "waived" ? "tb-badge-muted" : "tb-badge-warning"}`}>{c.status.toUpperCase()}</span>
                            {c.status === "pending" && (
                                <>
                                    <button onClick={markPaid.bind(null, c.id)} className="tb-btn-primary !text-[10px] !py-1 !px-2" title="Marcar pagado">Pagado</button>
                                    <button onClick={waive.bind(null, c.id)} className="tb-btn-ghost !text-[10px] !py-1 !px-2" title="Condonar">Condonar</button>
                                </>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}



// ---------- Inventario Especial (En Camino + Solicitados) ----------
function ModerationQueuePanel() {
    const [filter, setFilter] = useState("pending_review");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [rejectingSku, setRejectingSku] = useState(null);
    const [rejectReason, setRejectReason] = useState("");
    const [expanded, setExpanded] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/products/moderation-queue?status=${filter}`);
            setItems(data);
        } catch { setItems([]); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

    const approve = async (sku) => {
        try { await api.post(`/admin/products/${sku}/moderation/approve`); toast.success("Producto aprobado"); load(); }
        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error"); }
    };
    const submitReject = async () => {
        if (!rejectReason.trim()) { toast.error("Escribe el motivo del rechazo"); return; }
        try {
            await api.post(`/admin/products/${rejectingSku}/moderation/reject`, { reason: rejectReason.trim() });
            toast.success("Producto rechazado — vendedor notificado");
            setRejectingSku(null); setRejectReason("");
            load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error"); }
    };

    return (
        <div className="tb-card p-4" data-testid="moderation-queue-panel">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <h3 className="font-medium inline-flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-[var(--tb-gold)]" />
                    Moderación de productos
                    {filter === "pending_review" && items.length > 0 && (
                        <span className="tb-badge tb-badge-warning !text-[9px]" data-testid="moderation-count-badge">{items.length}</span>
                    )}
                </h3>
                <div className="flex gap-1">
                    {[
                        ["pending_review", "Pendientes"],
                        ["rejected", "Rechazados"],
                        ["approved", "Aprobados"],
                        ["all", "Todos"],
                    ].map(([k, l]) => (
                        <button key={k} onClick={() => setFilter(k)}
                            className={`text-[10px] px-2 py-1 rounded border transition ${filter === k ? "bg-[var(--tb-gold)] text-black border-[var(--tb-gold)]" : "border-[var(--tb-border)] hover:border-[var(--tb-gold)]"}`}
                            data-testid={`moderation-filter-${k}`}>
                            {l}
                        </button>
                    ))}
                </div>
            </div>
            <p className="text-[10px] text-[var(--tb-muted)] mb-3">
                Cada producto que un vendedor crea (y ediciones a nombre, precio, descripción o foto) requiere tu aprobación. Los productos rechazados regresan al vendedor con el motivo para que corrija.
            </p>
            {loading ? (
                <div className="text-xs text-[var(--tb-muted)] py-4 text-center">Cargando…</div>
            ) : items.length === 0 ? (
                <div className="text-xs text-[var(--tb-muted)] py-4 text-center" data-testid="moderation-empty">
                    {filter === "pending_review" ? "No hay productos por revisar. 🎉" : "Sin resultados."}
                </div>
            ) : (
                <div className="space-y-2">
                    {items.map((p) => (
                        <div key={p.sku} className="border border-[var(--tb-border)] rounded p-3" data-testid={`moderation-item-${p.sku}`}>
                            <div className="flex items-start gap-3 flex-wrap">
                                {(() => { const s = productImage(p); return s ? <img src={s} alt="" className="w-16 h-16 rounded object-cover flex-shrink-0" /> : <div className="w-16 h-16 rounded bg-[var(--tb-bg-2)] flex-shrink-0" />; })()}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <div className="font-medium truncate">{p.nombre}</div>
                                        {p.moderation_status === "approved" && <span className="tb-badge tb-badge-success !text-[9px]">APROBADO</span>}
                                        {p.moderation_status === "rejected" && <span className="tb-badge !text-[9px] bg-rose-500/20 text-rose-300 border border-rose-500/40">RECHAZADO</span>}
                                        {p.moderation_status === "pending_review" && <span className="tb-badge tb-badge-warning !text-[9px]">PENDIENTE</span>}
                                    </div>
                                    <div className="text-[10px] text-[var(--tb-muted)]">
                                        {p.sku} · {p.categoria} · {p.condicion} · Stock {p.cantidad_disponible}
                                    </div>
                                    <div className="text-[var(--tb-gold)] text-sm mt-0.5">{formatPrice(p.precio_venta)}</div>
                                    <div className="text-[11px] text-[var(--tb-text-2)] mt-1">
                                        Vendedor: <strong>{p.vendor?.display_name || "—"}</strong> · <span className="text-[var(--tb-muted)]">{p.vendor?.email || ""}</span>
                                    </div>
                                    {p.descripcion && (
                                        <button onClick={() => setExpanded(expanded === p.sku ? null : p.sku)} className="text-[10px] text-[var(--tb-gold)] mt-1 hover:underline">
                                            {expanded === p.sku ? "Ocultar descripción" : "Ver descripción"}
                                        </button>
                                    )}
                                    {expanded === p.sku && (
                                        <div className="text-[11px] text-[var(--tb-text-2)] mt-2 p-2 bg-[var(--tb-bg-2)] rounded whitespace-pre-line">{p.descripcion}</div>
                                    )}
                                </div>
                                {p.moderation_status === "pending_review" && (
                                    <div className="flex gap-1 flex-shrink-0">
                                        <button onClick={() => approve(p.sku)} className="tb-btn-primary !text-[10px] !py-1 !px-2 inline-flex items-center gap-1" data-testid={`moderation-approve-${p.sku}`}>
                                            <Check className="w-3 h-3" /> Aprobar
                                        </button>
                                        <button onClick={() => { setRejectingSku(p.sku); setRejectReason(""); }} className="tb-btn-secondary !text-[10px] !py-1 !px-2 !text-rose-300 !border-rose-500/50 inline-flex items-center gap-1" data-testid={`moderation-reject-${p.sku}`}>
                                            <X className="w-3 h-3" /> Rechazar
                                        </button>
                                    </div>
                                )}
                            </div>
                            {p.moderation_status === "rejected" && p.moderation_reason && (
                                <div className="mt-2 pt-2 border-t border-rose-500/30 text-[11px]">
                                    <span className="text-rose-400 font-medium">Motivo:</span> <span className="text-[var(--tb-text-2)]">{p.moderation_reason}</span>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
            {rejectingSku && (
                <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-3" onClick={(e) => e.target === e.currentTarget && setRejectingSku(null)}>
                    <div className="tb-card p-5 w-full max-w-md space-y-3" data-testid="moderation-reject-modal">
                        <h3 className="font-display text-lg">Rechazar producto</h3>
                        <p className="text-xs text-[var(--tb-text-2)]">Explica al vendedor qué necesita corregir. Este texto se enviará por correo.</p>
                        <textarea rows={4} className="tb-input" placeholder="Ej: La foto está borrosa. Usa una imagen clara con fondo neutro..."
                            value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} maxLength={500}
                            data-testid="moderation-reject-reason" />
                        <div className="text-[10px] text-[var(--tb-muted)] text-right">{rejectReason.length}/500</div>
                        <div className="flex justify-end gap-2">
                            <button onClick={() => setRejectingSku(null)} className="tb-btn-secondary !text-xs">Cancelar</button>
                            <button onClick={submitReject} className="tb-btn-primary !text-xs !text-rose-300 !border-rose-500/50" data-testid="moderation-reject-confirm">Rechazar y notificar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}


function InventarioEspecial() {
    const [sub, setSub] = useState("incoming");
    const subs = [
        { id: "incoming", label: "Productos en Camino", icon: Truck, desc: "Productos importados que aún no han llegado al inventario" },
        { id: "solicitudes", label: "Productos Solicitados", icon: Send, desc: "Pedidos personalizados de clientes (Solicitar producto)" },
    ];
    return (
        <div className="space-y-6" data-testid="inventario-especial-section">
            <div>
                <h2 className="font-display text-2xl mb-1">Inventario Especial</h2>
                <p className="text-xs text-[var(--tb-text-2)]">Productos pendientes de llegada y solicitudes personalizadas en un solo lugar</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
                {subs.map((s) => {
                    const Icon = s.icon;
                    const active = sub === s.id;
                    return (
                        <button key={s.id} onClick={() => setSub(s.id)}
                            data-testid={`inventario-sub-${s.id}`}
                            className={`text-left p-4 border transition rounded ${active ? "border-[var(--tb-gold)] bg-[var(--tb-border-gold)]/10" : "border-[var(--tb-border)] hover:border-[var(--tb-gold)]/60"}`}>
                            <div className="flex items-center gap-2 mb-1">
                                <Icon className={`w-4 h-4 ${active ? "text-[var(--tb-gold)]" : "text-[var(--tb-text-2)]"}`} />
                                <span className={`text-sm font-medium ${active ? "text-[var(--tb-gold)]" : "text-[var(--tb-text)]"}`}>{s.label}</span>
                            </div>
                            <p className="text-[11px] text-[var(--tb-text-2)] leading-relaxed">{s.desc}</p>
                        </button>
                    );
                })}
            </div>
            <div className="pt-2">
                {sub === "incoming" && <IncomingAdmin />}
                {sub === "solicitudes" && <Solicitudes />}
            </div>
        </div>
    );
}


// ---------- KANBAN BOARD (Pedidos) ----------
function Ventas() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(null);
    // Filters
    const today = new Date().toISOString().slice(0, 10);
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [countryF, setCountryF] = useState("ALL"); // ALL | SV | US
    const [paymentF, setPaymentF] = useState("ALL");
    const [search, setSearch] = useState("");
    const [preset, setPreset] = useState("all");

    useEffect(() => {
        api.get("/orders").then(({ data }) => {
            setOrders(data.filter((o) => o.status === "completed" || o.status === "completado"));
        }).catch(() => {}).finally(() => setLoading(false));
    }, []);

    const applyPreset = (key) => {
        setPreset(key);
        const now = new Date();
        const iso = (d) => d.toISOString().slice(0, 10);
        if (key === "today") { setFromDate(today); setToDate(today); }
        else if (key === "week") { const d = new Date(now); d.setDate(d.getDate() - 7); setFromDate(iso(d)); setToDate(today); }
        else if (key === "month") { const d = new Date(now); d.setDate(d.getDate() - 30); setFromDate(iso(d)); setToDate(today); }
        else if (key === "year") { const d = new Date(now); d.setDate(d.getDate() - 365); setFromDate(iso(d)); setToDate(today); }
        else { setFromDate(""); setToDate(""); }
    };

    // Apply filters
    const filtered = useMemo(() => {
        const s = search.trim().toLowerCase();
        return orders.filter((o) => {
            const d = (o.created_at || "").slice(0, 10);
            if (fromDate && d < fromDate) return false;
            if (toDate && d > toDate) return false;
            if (countryF !== "ALL" && (o.country || "SV") !== countryF) return false;
            if (paymentF !== "ALL" && o.payment_method !== paymentF) return false;
            if (s) {
                const blob = [
                    o._id, o.customer_name, o.customer_lastname, o.user_email, o.customer_phone, o.tracking_number,
                    ...((o.items || []).map((it) => `${it.sku} ${it.nombre}`)),
                ].filter(Boolean).join(" ").toLowerCase();
                if (!blob.includes(s)) return false;
            }
            return true;
        });
    }, [orders, fromDate, toDate, countryF, paymentF, search]);

    const grouped = useMemo(() => {
        const g = {};
        for (const o of filtered) {
            const d = (o.created_at || "").slice(0, 10);
            (g[d] = g[d] || []).push(o);
        }
        return Object.entries(g).sort((a, b) => b[0].localeCompare(a[0]));
    }, [filtered]);

    const stats = useMemo(() => {
        const total = filtered.reduce((a, o) => a + (o.total || 0), 0);
        const byMethod = {};
        const byCountry = { SV: 0, US: 0 };
        for (const o of filtered) {
            const m = o.payment_method || "—";
            byMethod[m] = (byMethod[m] || 0) + (o.total || 0);
            byCountry[o.country || "SV"] = (byCountry[o.country || "SV"] || 0) + (o.total || 0);
        }
        // Top products
        const productMap = {};
        for (const o of filtered) {
            for (const it of o.items || []) {
                const key = it.sku;
                if (!productMap[key]) productMap[key] = { sku: it.sku, nombre: it.nombre, qty: 0, revenue: 0 };
                productMap[key].qty += it.quantity || 0;
                productMap[key].revenue += it.subtotal || 0;
            }
        }
        const top = Object.values(productMap).sort((a, b) => b.revenue - a.revenue).slice(0, 8);
        const avg = filtered.length > 0 ? total / filtered.length : 0;
        return { total, byMethod, byCountry, top, avg, count: filtered.length };
    }, [filtered]);

    const exportCsv = () => {
        const rows = [
            ["ID", "Fecha", "Cliente", "Email", "Teléfono", "País", "Método de pago", "Envío", "Subtotal", "Envío$", "Total", "Tracking", "Productos"],
            ...filtered.map((o) => [
                o._id, (o.created_at || "").slice(0, 19).replace("T", " "),
                `${o.customer_name || ""} ${o.customer_lastname || ""}`.trim(),
                o.user_email || "", o.customer_phone || "",
                o.country || "SV", o.payment_method || "",
                o.needs_shipping ? "Sí" : "No",
                (o.subtotal || 0).toFixed(2), (o.shipping || 0).toFixed(2), (o.total || 0).toFixed(2),
                o.tracking_number || "",
                (o.items || []).map((it) => `${it.sku}×${it.quantity}`).join(" | "),
            ]),
        ];
        const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
        const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `ventas-terra-${today}.csv`; a.click();
        URL.revokeObjectURL(url);
        toast.success(`CSV exportado · ${filtered.length} ventas`);
    };

    const clearFilters = () => {
        setFromDate(""); setToDate(""); setCountryF("ALL"); setPaymentF("ALL"); setSearch(""); setPreset("all");
    };

    if (loading) return <div className="p-8 text-center text-[var(--tb-text-2)]">Cargando ventas...</div>;

    const presets = [
        { id: "today", label: "Hoy" }, { id: "week", label: "7 días" },
        { id: "month", label: "30 días" }, { id: "year", label: "Año" }, { id: "all", label: "Todo" },
    ];

    return (
        <div className="space-y-5" data-testid="ventas-section">
            {/* Header */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <h2 className="font-display text-2xl">Ventas Completadas</h2>
                    <p className="text-xs text-[var(--tb-text-2)]">Filtra, busca, analiza y exporta tus ventas confirmadas</p>
                </div>
                <button onClick={exportCsv} disabled={filtered.length === 0}
                    className="tb-btn-secondary inline-flex items-center gap-2 disabled:opacity-50" data-testid="ventas-export-csv">
                    <Download className="w-4 h-4" /> Exportar CSV
                </button>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="ventas-kpis">
                <div className="tb-card p-4">
                    <div className="tb-overline">Total filtrado</div>
                    <div className="font-display text-2xl text-[var(--tb-gold)]">{formatPrice(stats.total)}</div>
                    <div className="text-[10px] text-[var(--tb-text-2)]">{stats.count} venta(s)</div>
                </div>
                <div className="tb-card p-4">
                    <div className="tb-overline">Ticket promedio</div>
                    <div className="font-display text-2xl text-[var(--tb-gold)]">{formatPrice(stats.avg)}</div>
                    <div className="text-[10px] text-[var(--tb-text-2)]">por venta</div>
                </div>
                <div className="tb-card p-4">
                    <div className="tb-overline">🇸🇻 El Salvador</div>
                    <div className="font-display text-2xl text-[var(--tb-gold)]">{formatPrice(stats.byCountry.SV || 0)}</div>
                </div>
                <div className="tb-card p-4">
                    <div className="tb-overline">🇺🇸 Estados Unidos</div>
                    <div className="font-display text-2xl text-[var(--tb-gold)]">{formatPrice(stats.byCountry.US || 0)}</div>
                </div>
            </div>

            {/* Filters */}
            <div className="tb-card p-4 space-y-3" data-testid="ventas-filters">
                <div className="flex items-center gap-2 flex-wrap">
                    <Filter className="w-4 h-4 text-[var(--tb-gold)]" />
                    <span className="text-sm font-medium">Filtros rápidos</span>
                    {presets.map((p) => (
                        <button key={p.id} onClick={() => applyPreset(p.id)}
                            className={`text-xs px-3 py-1 rounded border transition ${preset === p.id ? "border-[var(--tb-gold)] text-[var(--tb-gold)] bg-[var(--tb-border-gold)]/10" : "border-[var(--tb-border)] text-[var(--tb-text-2)] hover:border-[var(--tb-gold)]/60"}`}
                            data-testid={`ventas-preset-${p.id}`}>
                            {p.label}
                        </button>
                    ))}
                    <button onClick={clearFilters} className="ml-auto text-xs text-[var(--tb-text-2)] hover:text-[var(--tb-gold)] inline-flex items-center gap-1" data-testid="ventas-clear-filters">
                        <X className="w-3 h-3" /> Limpiar
                    </button>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
                    <label className="text-xs">Desde
                        <input type="date" className="tb-input mt-1" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setPreset("custom"); }} data-testid="ventas-from" />
                    </label>
                    <label className="text-xs">Hasta
                        <input type="date" className="tb-input mt-1" value={toDate} onChange={(e) => { setToDate(e.target.value); setPreset("custom"); }} data-testid="ventas-to" />
                    </label>
                    <label className="text-xs">País
                        <select className="tb-input mt-1" value={countryF} onChange={(e) => setCountryF(e.target.value)} data-testid="ventas-country">
                            <option value="ALL">Todos</option><option value="SV">🇸🇻 SV</option><option value="US">🇺🇸 US</option>
                        </select>
                    </label>
                    <label className="text-xs">Método
                        <select className="tb-input mt-1" value={paymentF} onChange={(e) => setPaymentF(e.target.value)} data-testid="ventas-payment">
                            <option value="ALL">Todos</option>
                            <option value="apartado">Efectivo</option>
                            <option value="stripe">Tarjeta</option>
                            <option value="paypal">PayPal</option>
                            <option value="transferencia">Transferencia</option>
                        </select>
                    </label>
                    <label className="text-xs lg:col-span-1">Buscar
                        <div className="relative mt-1">
                            <Search className="w-3 h-3 absolute left-2 top-2.5 text-[var(--tb-muted)]" />
                            <input className="tb-input pl-7" placeholder="Cliente, SKU, ID..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="ventas-search" />
                        </div>
                    </label>
                </div>
            </div>

            {/* Breakdown by payment method + top products */}
            {stats.count > 0 && (
                <div className="grid lg:grid-cols-2 gap-3">
                    <div className="tb-card p-4" data-testid="ventas-by-method">
                        <div className="tb-overline mb-3">Ingresos por método de pago</div>
                        <div className="space-y-2">
                            {Object.entries(stats.byMethod).sort((a, b) => b[1] - a[1]).map(([m, v]) => {
                                const pct = stats.total > 0 ? (v / stats.total) * 100 : 0;
                                return (
                                    <div key={m}>
                                        <div className="flex justify-between text-xs mb-1">
                                            <span>{paymentMethodLabel(m)}</span>
                                            <span className="text-[var(--tb-gold)] font-medium">{formatPrice(v)} <span className="text-[var(--tb-muted)]">({pct.toFixed(1)}%)</span></span>
                                        </div>
                                        <div className="h-1.5 bg-[var(--tb-bg-2)]/40 rounded overflow-hidden">
                                            <div className="h-full bg-[var(--tb-gold)]" style={{ width: `${pct}%` }} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    <div className="tb-card p-4" data-testid="ventas-top-products">
                        <div className="tb-overline mb-3">Top 8 productos del período</div>
                        {stats.top.length === 0 ? (
                            <div className="text-xs text-[var(--tb-muted)]">Sin datos</div>
                        ) : (
                            <table className="w-full text-xs">
                                <thead className="text-[var(--tb-text-2)] border-b border-[var(--tb-border)]">
                                    <tr className="text-left"><th className="pb-1.5">SKU</th><th>Producto</th><th className="text-right">Uds</th><th className="text-right">Ingreso</th></tr>
                                </thead>
                                <tbody>
                                    {stats.top.map((p) => (
                                        <tr key={p.sku} className="border-b border-[var(--tb-border)]/40">
                                            <td className="py-1 font-mono text-[10px] text-[var(--tb-muted)]">{p.sku}</td>
                                            <td className="truncate max-w-[180px]">{p.nombre}</td>
                                            <td className="text-right">{p.qty}</td>
                                            <td className="text-right text-[var(--tb-gold)]">{formatPrice(p.revenue)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}

            {/* Sales by day */}
            {grouped.length === 0 ? (
                <div className="tb-card p-8 text-center text-[var(--tb-text-2)]" data-testid="ventas-empty">
                    {orders.length === 0 ? "Aún no hay ventas completadas." : "No hay resultados para los filtros aplicados."}
                </div>
            ) : (
                grouped.map(([day, list]) => {
                    const dayTotal = list.reduce((a, o) => a + (o.total || 0), 0);
                    return (
                        <div key={day} className="tb-card p-5" data-testid={`ventas-day-${day}`}>
                            <div className="flex items-center justify-between mb-4 pb-3 border-b border-[var(--tb-border)]">
                                <div>
                                    <h3 className="font-display text-xl text-[var(--tb-gold)] flex items-center gap-2">
                                        <Calendar className="w-4 h-4" /> {new Date(day).toLocaleDateString("es-SV", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
                                    </h3>
                                    <p className="text-xs text-[var(--tb-text-2)]">{list.length} venta(s)</p>
                                </div>
                                <div className="text-right">
                                    <div className="tb-overline">Total del día</div>
                                    <div className="font-display text-xl text-[var(--tb-gold)]">{formatPrice(dayTotal)}</div>
                                </div>
                            </div>
                            <div className="space-y-2">
                                {list.map((o) => (
                                    <div key={o._id} className="border border-[var(--tb-border)] rounded">
                                        <button onClick={() => setExpanded(expanded === o._id ? null : o._id)}
                                            className="w-full text-left p-3 hover:bg-[var(--tb-bg-2)]/40 transition flex items-center gap-3 flex-wrap"
                                            data-testid={`venta-${o._id}`}>
                                            <span className="font-mono text-xs text-[var(--tb-muted)]">{(o._id || "").slice(0, 12)}</span>
                                            <span className="text-sm text-[var(--tb-text)] flex-1 min-w-[150px]">{o.customer_name} {o.customer_lastname}</span>
                                            <span className="tb-badge tb-badge-muted">{(o.country || "SV") === "US" ? "🇺🇸 USA" : "🇸🇻 SV"}</span>
                                            <span className="tb-badge tb-badge-muted">{paymentMethodLabel(o.payment_method)}</span>
                                            <span className="tb-badge tb-badge-muted">{o.needs_shipping ? "Envío" : "Retiro"}</span>
                                            <span className="font-display text-lg text-[var(--tb-gold)]">{formatPrice(o.total)}</span>
                                            <span className="text-xs text-[var(--tb-muted)]">{new Date(o.created_at).toLocaleTimeString("es-SV", { hour: "2-digit", minute: "2-digit" })}</span>
                                        </button>
                                        {expanded === o._id && (
                                            <div className="p-4 border-t border-[var(--tb-border)] bg-[var(--tb-bg-2)]/30 text-sm space-y-3">
                                                <div className="grid sm:grid-cols-2 gap-3">
                                                    <div>
                                                        <div className="tb-overline">Cliente</div>
                                                        <div>{o.customer_name} {o.customer_lastname}</div>
                                                        <div className="text-xs text-[var(--tb-text-2)]">{o.user_email}</div>
                                                        <div className="text-xs text-[var(--tb-text-2)]">{o.customer_phone || "Sin teléfono"}</div>
                                                    </div>
                                                    <div>
                                                        <div className="tb-overline">{o.needs_shipping ? "Dirección de envío" : "Retiro en tienda"}</div>
                                                        <div className="text-xs whitespace-pre-line">{o.needs_shipping ? (o.shipping_address || "—") : "El cliente recogió en tienda"}</div>
                                                        {o.tracking_number && <div className="text-xs text-[var(--tb-gold)] font-mono mt-1">📦 {o.tracking_number}</div>}
                                                    </div>
                                                </div>
                                                <div>
                                                    <div className="tb-overline mb-1">Productos vendidos</div>
                                                    <div className="space-y-1">
                                                        {(o.items || []).map((it) => (
                                                            <div key={it.sku} className="flex items-center justify-between text-xs border-b border-[var(--tb-border)] pb-1">
                                                                <span className="font-mono text-[var(--tb-muted)]">{it.sku}</span>
                                                                <span className="flex-1 px-2">{it.nombre}</span>
                                                                <span>×{it.quantity}</span>
                                                                <span className="text-[var(--tb-gold)] w-20 text-right">{formatPrice(it.subtotal)}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                                <div className="flex justify-end gap-4 text-xs pt-2 border-t border-[var(--tb-border)]">
                                                    <div><span className="text-[var(--tb-text-2)]">Subtotal:</span> {formatPrice(o.subtotal)}</div>
                                                    <div><span className="text-[var(--tb-text-2)]">Envío:</span> {formatPrice(o.shipping || 0)}</div>
                                                    <div className="font-display text-sm"><span className="text-[var(--tb-text-2)]">Total:</span> <span className="text-[var(--tb-gold)]">{formatPrice(o.total)}</span></div>
                                                </div>
                                                {o.notes && <div className="text-xs text-[var(--tb-text-2)] italic">Notas: {o.notes}</div>}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })
            )}
        </div>
    );
}


// ---------- KANBAN BOARDS (3 per payment method) ----------
const KANBAN_BOARDS = {
    apartado: {
        label: "Cash (Efectivo)",
        icon: "💵",
        columns: [
            { id: "pending_cash", title: "Pendiente de Pago", accent: "bg-amber-500/20 text-amber-300 border-amber-500/40" },
            { id: "in_process",   title: "En Proceso",        accent: "bg-purple-500/20 text-purple-300 border-purple-500/40" },
            { id: "ready_pickup", title: "Listo para Recoger", accent: "bg-cyan-500/20 text-cyan-300 border-cyan-500/40" },
            { id: "completed",    title: "Completado",        accent: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" },
            { id: "cancelado",    title: "Cancelado",         accent: "bg-red-500/20 text-red-300 border-red-500/40" },
        ],
        inventoryNote: "Stock se descuenta al pasar a Completado",
    },
    "stripe-paypal": {
        label: "Tarjeta / PayPal",
        icon: "💳",
        methods: ["stripe", "paypal"],
        columns: [
            { id: "paid_new",     title: "Pagado / Nuevo",    accent: "bg-blue-500/20 text-blue-300 border-blue-500/40" },
            { id: "in_process",   title: "En Proceso",        accent: "bg-purple-500/20 text-purple-300 border-purple-500/40" },
            { id: "ready_pickup", title: "Listo para Recoger", accent: "bg-cyan-500/20 text-cyan-300 border-cyan-500/40" },
            { id: "shipped",      title: "Enviado",           accent: "bg-indigo-500/20 text-indigo-300 border-indigo-500/40" },
            { id: "completed",    title: "Completado",        accent: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" },
            { id: "cancelado",    title: "Cancelado",         accent: "bg-red-500/20 text-red-300 border-red-500/40" },
        ],
        inventoryNote: "Stock se descuenta al confirmarse el pago en línea",
    },
    transferencia: {
        label: "Transferencia Bancaria",
        icon: "🏦",
        columns: [
            { id: "pending_transfer", title: "Pendiente Transferencia", accent: "bg-amber-500/20 text-amber-300 border-amber-500/40" },
            { id: "payment_received", title: "Pago Recibido",           accent: "bg-blue-500/20 text-blue-300 border-blue-500/40" },
            { id: "in_process",       title: "En Proceso",              accent: "bg-purple-500/20 text-purple-300 border-purple-500/40" },
            { id: "ready_pickup",     title: "Listo para Recoger",      accent: "bg-cyan-500/20 text-cyan-300 border-cyan-500/40" },
            { id: "shipped",          title: "Enviado",                 accent: "bg-indigo-500/20 text-indigo-300 border-indigo-500/40" },
            { id: "completed",        title: "Completado",              accent: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" },
            { id: "cancelado",        title: "Cancelado",               accent: "bg-red-500/20 text-red-300 border-red-500/40" },
        ],
        inventoryNote: "Stock se descuenta al confirmar 'Pago Recibido'",
    },
};

// ============================================================
// Etiquetas de Envío — Centralizado SV + US, individual y batch
// ============================================================
function EtiquetasEnvio() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [countryFilter, setCountryFilter] = useState("ALL"); // ALL | SV | US
    const [statusFilter, setStatusFilter] = useState("pending"); // pending | ready | shipped
    const [selected, setSelected] = useState(new Set());
    const [trackingEdit, setTrackingEdit] = useState({}); // {order_id: value}
    const [busy, setBusy] = useState({}); // {order_id: bool}
    const [batchBusy, setBatchBusy] = useState(false);
    const [reprintModal, setReprintModal] = useState(null); // {order_id, count}
    const [printingQr, setPrintingQr] = useState(null); // order pending QR print

    const load = async () => {
        try {
            const { data } = await api.get("/orders");
            // Only orders that need shipping (US is always shipping, SV when needs_shipping=true and not pickup)
            const shippable = data.filter((o) =>
                (o.country || "SV") === "US" || (o.needs_shipping && o.payment_method !== "apartado")
            );
            setOrders(shippable);
        } catch (e) { toast.error("Error al cargar pedidos"); }
        finally { setLoading(false); }
    };
    useEffect(() => {
        load();
        const iv = setInterval(() => { load().catch(() => {}); }, 12000);
        return () => clearInterval(iv);
    }, []);

    // Status buckets
    const isPending = (o) => !o.tracking_number && o.status !== "shipped" && o.status !== "completed" && o.status !== "cancelado";
    const isReady = (o) => o.tracking_number && o.status !== "shipped" && o.status !== "completed" && o.status !== "cancelado";
    const isShipped = (o) => o.status === "shipped" || o.status === "completed";

    const visible = useMemo(() => {
        return orders
            .filter((o) => countryFilter === "ALL" || (o.country || "SV") === countryFilter)
            .filter((o) => statusFilter === "pending" ? isPending(o) : statusFilter === "ready" ? isReady(o) : isShipped(o))
            .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    }, [orders, countryFilter, statusFilter]);

    const stats = useMemo(() => {
        const filterByCountry = (list) => countryFilter === "ALL" ? list : list.filter((o) => (o.country || "SV") === countryFilter);
        const all = filterByCountry(orders);
        return {
            pending: all.filter(isPending).length,
            ready: all.filter(isReady).length,
            shipped: all.filter(isShipped).length,
            totalUs: orders.filter((o) => (o.country || "SV") === "US").length,
            totalSv: orders.filter((o) => (o.country || "SV") === "SV").length,
        };
    }, [orders, countryFilter]);

    const toggle = (oid) => {
        const next = new Set(selected);
        if (next.has(oid)) next.delete(oid); else next.add(oid);
        setSelected(next);
    };
    const toggleAll = () => {
        if (visible.every((o) => selected.has(o._id))) setSelected(new Set());
        else setSelected(new Set([...selected, ...visible.map((o) => o._id)]));
    };

    // SV — Save manual tracking
    const saveTracking = async (orderId) => {
        const tn = (trackingEdit[orderId] || "").trim();
        if (!tn) { toast.error("Ingresa un número de tracking"); return; }
        setBusy((s) => ({ ...s, [orderId]: true }));
        try {
            await api.put(`/orders/${orderId}/status`, { status: "shipped", tracking_number: tn });
            toast.success("Tracking guardado y pedido marcado como enviado");
            setTrackingEdit((s) => { const n = { ...s }; delete n[orderId]; return n; });
            load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setBusy((s) => ({ ...s, [orderId]: false })); }
    };

    // US — Generate single ShipStation label (anti-duplicate aware)
    const createLabel = async (orderId, force = false) => {
        setBusy((s) => ({ ...s, [orderId]: true }));
        try {
            const { data } = await api.post(`/admin/orders/${orderId}/shipstation-label`, { service_code: "usps_ground_advantage", force });
            if (data.label_base64) {
                const link = document.createElement("a");
                link.href = `data:application/pdf;base64,${data.label_base64}`;
                link.download = `label-${orderId.slice(0, 8)}-${data.tracking_number || "tn"}.pdf`;
                link.click();
            }
            if (data.cached) {
                toast.success(`Etiqueta ya existente · descargada (impresión #${data.label_print_count}). NO se cargó a ShipStation.`);
            } else {
                toast.success(`Label USPS generado · ${data.tracking_number || ""}`);
            }
            load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setBusy((s) => ({ ...s, [orderId]: false })); }
    };

    // Re-download a saved label (no ShipStation hit; bumps counter)
    const reDownload = async (orderId) => {
        setBusy((s) => ({ ...s, [orderId]: true }));
        try {
            const { data } = await api.get(`/admin/orders/${orderId}/label-pdf`);
            const link = document.createElement("a");
            link.href = `data:application/pdf;base64,${data.label_base64}`;
            link.download = `label-${orderId.slice(0, 8)}-${data.tracking_number || "tn"}.pdf`;
            link.click();
            toast.success(`Re-descargada (impresión #${data.label_print_count})`);
            load();
        } catch (e) { toast.error("No se pudo descargar la etiqueta guardada"); }
        finally { setBusy((s) => ({ ...s, [orderId]: false })); }
    };

    const handleReprintRequest = (order) => {
        // If order has been printed already, ask for confirmation
        const count = order.label_print_count || 1;
        if (count >= 1) {
            setReprintModal({ order_id: order._id, count, tracking: order.tracking_number });
        } else {
            reDownload(order._id);
        }
    };

    // US — Batch label generation
    const batchGenerate = async () => {
        const ids = visible.filter((o) => selected.has(o._id) && (o.country || "SV") === "US" && !o.tracking_number).map((o) => o._id);
        if (ids.length === 0) { toast.error("Selecciona pedidos USA sin etiqueta"); return; }
        setBatchBusy(true);
        try {
            const { data } = await api.post("/admin/orders/labels-batch", { order_ids: ids });
            // Download successful labels concatenated
            const okResults = (data.results || []).filter((r) => r.ok && r.label_base64);
            for (const r of okResults) {
                const link = document.createElement("a");
                link.href = `data:application/pdf;base64,${r.label_base64}`;
                link.download = `label-${r.order_id.slice(0, 8)}-${r.tracking_number || "tn"}.pdf`;
                link.click();
                await new Promise((res) => setTimeout(res, 250));
            }
            toast.success(`Lote: ${data.success}/${data.total} etiquetas generadas`);
            setSelected(new Set());
            load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setBatchBusy(false); }
    };

    // Bulk SV mark as shipped (requires tracking already saved)
    const bulkMarkShipped = async () => {
        const ids = visible.filter((o) => selected.has(o._id) && o.tracking_number && o.status !== "shipped").map((o) => o._id);
        if (ids.length === 0) { toast.error("Selecciona pedidos con tracking asignado"); return; }
        setBatchBusy(true);
        let ok = 0;
        for (const oid of ids) {
            try { await api.put(`/orders/${oid}/status`, { status: "shipped" }); ok++; }
            catch { /* skip */ }
        }
        toast.success(`${ok}/${ids.length} pedidos marcados como enviados`);
        setSelected(new Set());
        load();
        setBatchBusy(false);
    };

    if (loading) return <div className="p-8 text-center text-[var(--tb-text-2)]">Cargando etiquetas...</div>;

    const allVisibleSelected = visible.length > 0 && visible.every((o) => selected.has(o._id));
    const selectedCount = visible.filter((o) => selected.has(o._id)).length;
    const selectedUsPending = visible.filter((o) => selected.has(o._id) && (o.country || "SV") === "US" && !o.tracking_number).length;
    const selectedSvReady = visible.filter((o) => selected.has(o._id) && (o.country || "SV") === "SV" && o.tracking_number && o.status !== "shipped").length;

    return (
        <div className="space-y-5" data-testid="etiquetas-section">
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="font-display text-2xl flex items-center gap-2"><Printer className="w-6 h-6 text-[var(--tb-gold)]" /> Etiquetas de Envío</h2>
                    <p className="text-xs text-[var(--tb-text-2)]">Centro de comando para asignar tracking, generar etiquetas USPS y marcar pedidos como enviados.</p>
                </div>
                <button onClick={load} className="tb-btn-secondary !text-xs inline-flex items-center gap-1" data-testid="etiquetas-refresh"><RefreshCw className="w-3 h-3" /> Refrescar</button>
            </div>

            {/* KPI cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="etiquetas-kpis">
                <div className="tb-card p-4">
                    <div className="tb-overline">Pendientes</div>
                    <div className="font-display text-3xl text-amber-400">{stats.pending}</div>
                    <div className="text-[10px] text-[var(--tb-text-2)]">Sin tracking / sin etiqueta</div>
                </div>
                <div className="tb-card p-4">
                    <div className="tb-overline">Listos para enviar</div>
                    <div className="font-display text-3xl text-cyan-400">{stats.ready}</div>
                    <div className="text-[10px] text-[var(--tb-text-2)]">Con tracking, sin marcar enviado</div>
                </div>
                <div className="tb-card p-4">
                    <div className="tb-overline">Enviados</div>
                    <div className="font-display text-3xl text-emerald-400">{stats.shipped}</div>
                    <div className="text-[10px] text-[var(--tb-text-2)]">Histórico</div>
                </div>
                <div className="tb-card p-4">
                    <div className="tb-overline">Distribución</div>
                    <div className="text-lg flex items-center gap-3 font-display">
                        <span>🇸🇻 <span className="text-[var(--tb-gold)]">{stats.totalSv}</span></span>
                        <span>🇺🇸 <span className="text-[#8FA4C7]">{stats.totalUs}</span></span>
                    </div>
                    <div className="text-[10px] text-[var(--tb-text-2)]">Pedidos con envío</div>
                </div>
            </div>

            {/* Filter bar */}
            <div className="tb-card p-4 space-y-3" data-testid="etiquetas-filters">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-[var(--tb-text-2)]">País:</span>
                    {[
                        { id: "ALL", label: "Todos", flag: "🌎" },
                        { id: "SV", label: "El Salvador", flag: "🇸🇻" },
                        { id: "US", label: "Estados Unidos", flag: "🇺🇸" },
                    ].map((c) => (
                        <button key={c.id} onClick={() => { setCountryFilter(c.id); setSelected(new Set()); }} data-testid={`etiquetas-country-${c.id}`}
                            className={`text-xs px-3 py-1.5 rounded-full border transition flex items-center gap-1.5 ${countryFilter === c.id
                                ? "border-[var(--tb-gold)] text-[var(--tb-gold)] bg-[var(--tb-border-gold)]/15"
                                : "border-[var(--tb-border)] text-[var(--tb-text-2)] hover:border-[var(--tb-gold)]/60"}`}>
                            <span>{c.flag}</span><span>{c.label}</span>
                        </button>
                    ))}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-[var(--tb-text-2)]">Estado:</span>
                    {[
                        { id: "pending", label: "Pendientes", color: "amber" },
                        { id: "ready", label: "Listos", color: "cyan" },
                        { id: "shipped", label: "Enviados", color: "emerald" },
                    ].map((s) => (
                        <button key={s.id} onClick={() => { setStatusFilter(s.id); setSelected(new Set()); }} data-testid={`etiquetas-status-${s.id}`}
                            className={`text-xs px-3 py-1.5 rounded border transition ${statusFilter === s.id
                                ? "border-[var(--tb-gold)] text-[var(--tb-gold)] bg-[var(--tb-border-gold)]/15"
                                : "border-[var(--tb-border)] text-[var(--tb-text-2)] hover:border-[var(--tb-gold)]/60"}`}>
                            {s.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Bulk action bar */}
            {selectedCount > 0 && (
                <div className="tb-card p-3 flex items-center justify-between bg-[var(--tb-border-gold)]/8 border-[var(--tb-gold)]/30 flex-wrap gap-3" data-testid="etiquetas-bulk-bar">
                    <div className="text-sm">
                        <span className="text-[var(--tb-gold)] font-semibold">{selectedCount}</span> pedido(s) seleccionado(s)
                        {selectedUsPending > 0 && <span className="text-xs text-[var(--tb-text-2)] ml-2">({selectedUsPending} USA pendientes)</span>}
                        {selectedSvReady > 0 && <span className="text-xs text-[var(--tb-text-2)] ml-2">({selectedSvReady} SV listos)</span>}
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        {selectedUsPending > 0 && (
                            <button onClick={batchGenerate} disabled={batchBusy}
                                className="tb-btn-primary !text-xs inline-flex items-center gap-1 disabled:opacity-50" data-testid="etiquetas-batch-generate">
                                <Layers className="w-3 h-3" /> {batchBusy ? "Generando..." : `Generar ${selectedUsPending} etiqueta(s) USPS`}
                            </button>
                        )}
                        {selectedSvReady > 0 && (
                            <button onClick={bulkMarkShipped} disabled={batchBusy}
                                className="tb-btn-secondary !text-xs inline-flex items-center gap-1 disabled:opacity-50" data-testid="etiquetas-bulk-shipped">
                                <Truck className="w-3 h-3" /> Marcar {selectedSvReady} como enviados
                            </button>
                        )}
                        <button onClick={() => setSelected(new Set())} className="tb-link !text-xs">Limpiar selección</button>
                    </div>
                </div>
            )}

            {/* Table */}
            {visible.length === 0 ? (
                <div className="tb-card p-8 text-center text-[var(--tb-text-2)]" data-testid="etiquetas-empty">
                    No hay pedidos en este filtro.
                </div>
            ) : (
                <div className="tb-card overflow-x-auto" data-testid="etiquetas-table">
                    <table className="w-full text-xs">
                        <thead className="bg-[var(--tb-bg-2)]/40 text-[var(--tb-text-2)]">
                            <tr className="text-left">
                                <th className="p-3">
                                    <button onClick={toggleAll} data-testid="etiquetas-toggle-all">
                                        {allVisibleSelected ? <CheckSquare className="w-4 h-4 text-[var(--tb-gold)]" /> : <Square className="w-4 h-4" />}
                                    </button>
                                </th>
                                <th className="p-3">Pedido</th>
                                <th className="p-3">Cliente</th>
                                <th className="p-3">Destino</th>
                                <th className="p-3">Productos</th>
                                <th className="p-3 text-right">Total</th>
                                <th className="p-3">Tracking</th>
                                <th className="p-3 text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visible.map((o) => {
                                const isUs = (o.country || "SV") === "US";
                                const borderColor = isUs ? "#4A6FA5" : "#D4A744";
                                return (
                                    <tr key={o._id} className="border-t border-[var(--tb-border)] hover:bg-[var(--tb-bg-2)]/20 align-top"
                                        style={{ borderLeft: `3px solid ${borderColor}` }}
                                        data-testid={`etiqueta-row-${o._id}`}>
                                        <td className="p-3">
                                            <button onClick={() => toggle(o._id)} data-testid={`etiqueta-select-${o._id}`}>
                                                {selected.has(o._id) ? <CheckSquare className="w-4 h-4 text-[var(--tb-gold)]" /> : <Square className="w-4 h-4" />}
                                            </button>
                                        </td>
                                        <td className="p-3 align-top">
                                            <div className="flex items-center gap-1 mb-1">
                                                <span title={isUs ? "Estados Unidos" : "El Salvador"}>{isUs ? "🇺🇸" : "🇸🇻"}</span>
                                                <span className="font-mono text-[10px] text-[var(--tb-muted)]">{(o._id || "").slice(0, 10)}</span>
                                            </div>
                                            <span className="tb-badge tb-badge-muted text-[9px]">{paymentMethodLabel(o.payment_method)}</span>
                                            <div className="text-[10px] text-[var(--tb-muted)] mt-1">{new Date(o.created_at).toLocaleDateString("es-SV")}</div>
                                        </td>
                                        <td className="p-3 align-top">
                                            <div className="font-medium text-[var(--tb-text)]">{o.customer_name} {o.customer_lastname}</div>
                                            <div className="text-[10px] text-[var(--tb-text-2)]">{o.user_email}</div>
                                            <div className="text-[10px] text-[var(--tb-text-2)]">{o.customer_phone || "—"}</div>
                                        </td>
                                        <td className="p-3 align-top text-[var(--tb-text-2)] max-w-[220px]">
                                            {isUs ? (
                                                <div className="text-[11px] whitespace-pre-line leading-tight">
                                                    {o.shipping_address}
                                                    {o.us_city && <div className="mt-0.5">{o.us_city}, {o.us_state} {o.us_zip}</div>}
                                                </div>
                                            ) : (
                                                <div className="text-[11px] whitespace-pre-line leading-tight">
                                                    {o.shipping_address || "—"}
                                                    {o.sv_plus_code && (
                                                        <div className="mt-1 pt-1 border-t border-[var(--tb-border)] text-[10px]">
                                                            <span className="inline-flex items-center gap-1 text-[var(--tb-gold)]">
                                                                <MapPin className="w-2.5 h-2.5" />
                                                                <span className="font-mono">{o.sv_plus_code_human || o.sv_plus_code}</span>
                                                            </span>
                                                            {o.sv_maps_url && (
                                                                <a href={o.sv_maps_url} target="_blank" rel="noreferrer" className="ml-2 text-[var(--tb-gold)] underline">Abrir mapa</a>
                                                            )}
                                                            <button
                                                                onClick={() => setPrintingQr(o)}
                                                                className="ml-2 text-blue-400 underline"
                                                                data-testid={`c807-qr-${o._id}`}
                                                            >
                                                                Imprimir QR
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                        <td className="p-3 align-top text-[11px]">
                                            {(o.items || []).slice(0, 3).map((it) => (
                                                <div key={it.sku} className="truncate max-w-[180px]">×{it.quantity} {it.nombre}</div>
                                            ))}
                                            {(o.items || []).length > 3 && (
                                                <div className="text-[10px] text-[var(--tb-muted)]">+{o.items.length - 3} más</div>
                                            )}
                                        </td>
                                        <td className="p-3 text-right font-display text-[var(--tb-gold)] align-top">{formatPrice(o.total)}</td>
                                        <td className="p-3 align-top">
                                            {o.tracking_number ? (
                                                <div className="space-y-1">
                                                    <a href={isUs ? `https://tools.usps.com/go/TrackConfirmAction?tLabels=${o.tracking_number}` : "#"}
                                                        target={isUs ? "_blank" : "_self"} rel="noreferrer"
                                                        className="text-[10px] text-[var(--tb-gold)] font-mono inline-flex items-center gap-1 hover:underline"
                                                        data-testid={`etiqueta-tracking-${o._id}`}>
                                                        📦 {o.tracking_number}
                                                        {isUs && <ExternalLink className="w-2.5 h-2.5" />}
                                                    </a>
                                                    {(o.label_print_count || 0) > 0 && (
                                                        <div className="text-[9px] text-emerald-400 font-medium" data-testid={`etiqueta-printcount-${o._id}`}>
                                                            ✓ Impresa {o.label_print_count}x
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                isUs ? (
                                                    <span className="text-[10px] text-[var(--tb-muted)] italic">Sin generar</span>
                                                ) : (
                                                    <div className="flex gap-1">
                                                        <input className="tb-input !text-[10px] !py-1 !px-2 w-32" placeholder="C807XXXXX"
                                                            value={trackingEdit[o._id] || ""} onChange={(e) => setTrackingEdit({ ...trackingEdit, [o._id]: e.target.value })}
                                                            data-testid={`etiqueta-tracking-input-${o._id}`} />
                                                    </div>
                                                )
                                            )}
                                        </td>
                                        <td className="p-3 text-right align-top">
                                            <div className="flex flex-col gap-1 items-end">
                                                {isUs && !o.tracking_number && (
                                                    <button onClick={() => createLabel(o._id)} disabled={busy[o._id]}
                                                        className="text-[10px] px-2 py-1 border border-[var(--tb-gold)] text-[var(--tb-gold)] hover:bg-[var(--tb-border-gold)]/10 rounded inline-flex items-center gap-1 disabled:opacity-50"
                                                        data-testid={`etiqueta-generate-${o._id}`}>
                                                        <Printer className="w-3 h-3" /> {busy[o._id] ? "..." : "Crear USPS"}
                                                    </button>
                                                )}
                                                {isUs && o.tracking_number && (
                                                    <button onClick={() => handleReprintRequest(o)} disabled={busy[o._id]}
                                                        className="text-[10px] px-2 py-1 border border-[var(--tb-border)] hover:border-[var(--tb-gold)]/60 rounded inline-flex items-center gap-1 disabled:opacity-50"
                                                        data-testid={`etiqueta-redownload-${o._id}`}>
                                                        <FileDown className="w-3 h-3" /> {busy[o._id] ? "..." : "Re-imprimir"}
                                                        {(o.label_print_count || 0) > 0 && <span className="text-[8px] text-[var(--tb-muted)]">({o.label_print_count}x)</span>}
                                                    </button>
                                                )}
                                                {!isUs && !o.tracking_number && (
                                                    <button onClick={() => saveTracking(o._id)} disabled={busy[o._id] || !(trackingEdit[o._id] || "").trim()}
                                                        className="text-[10px] px-2 py-1 border border-[var(--tb-gold)] text-[var(--tb-gold)] hover:bg-[var(--tb-border-gold)]/10 rounded inline-flex items-center gap-1 disabled:opacity-50"
                                                        data-testid={`etiqueta-save-tracking-${o._id}`}>
                                                        <Truck className="w-3 h-3" /> {busy[o._id] ? "..." : "Guardar y enviar"}
                                                    </button>
                                                )}
                                                {o.tracking_number && o.status !== "shipped" && !isUs && (
                                                    <button onClick={async () => {
                                                        setBusy((s) => ({ ...s, [o._id]: true }));
                                                        try { await api.put(`/orders/${o._id}/status`, { status: "shipped" }); toast.success("Marcado como enviado"); load(); }
                                                        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
                                                        finally { setBusy((s) => ({ ...s, [o._id]: false })); }
                                                    }} className="text-[10px] px-2 py-1 border border-cyan-500/60 text-cyan-400 hover:bg-cyan-500/10 rounded inline-flex items-center gap-1"
                                                        data-testid={`etiqueta-mark-shipped-${o._id}`}>
                                                        <Truck className="w-3 h-3" /> Marcar enviado
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Reprint confirmation modal */}
            {reprintModal && (
                <div className="fixed inset-0 bg-black/80 z-50 flex items-start md:items-center justify-center p-4 overflow-y-auto" data-testid="reprint-modal">
                    <div className="tb-card max-w-md w-full p-6 space-y-4">
                        <div className="flex items-center gap-3">
                            <AlertTriangle className="w-8 h-8 text-amber-400 flex-shrink-0" />
                            <div>
                                <h3 className="font-display text-xl">¿Reimprimir etiqueta?</h3>
                                <p className="text-xs text-[var(--tb-text-2)] mt-1">Esta etiqueta ya fue impresa <span className="text-amber-400 font-semibold">{reprintModal.count} vez(es)</span>. Solo deberías reimprimirla si la anterior se dañó o se perdió.</p>
                            </div>
                        </div>
                        {reprintModal.tracking && (
                            <div className="text-xs bg-[var(--tb-bg-2)]/40 p-3 rounded font-mono">
                                Tracking: <span className="text-[var(--tb-gold)]">{reprintModal.tracking}</span>
                            </div>
                        )}
                        <div className="text-[10px] text-emerald-400 bg-emerald-500/5 border border-emerald-500/30 rounded p-2">
                            ✓ Esta acción NO genera una nueva etiqueta ni cobra a ShipStation. Solo descarga el PDF guardado.
                        </div>
                        <div className="flex gap-2 justify-end">
                            <button onClick={() => setReprintModal(null)} className="tb-btn-secondary !text-xs" data-testid="reprint-cancel">Cancelar</button>
                            <button onClick={() => { const oid = reprintModal.order_id; setReprintModal(null); reDownload(oid); }}
                                className="tb-btn-primary !text-xs" data-testid="reprint-confirm">
                                Sí, reimprimir
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {printingQr && (
                <div className="fixed inset-0 bg-black/80 z-50 flex items-start md:items-center justify-center p-4 overflow-y-auto" onClick={() => setPrintingQr(null)} data-testid="qr-print-modal">
                    <div className="bg-white text-black max-w-sm w-full p-6 rounded shadow-2xl my-4" onClick={(e) => e.stopPropagation()}>
                        <div className="text-center mb-4 print:mb-2">
                            <div className="text-xs uppercase tracking-widest text-gray-600">Etiqueta complementaria</div>
                            <div className="font-display text-lg leading-tight mt-1">Ubicación exacta de entrega</div>
                            <div className="text-[10px] text-gray-500 mt-0.5">Escanea con la cámara del teléfono para abrir Google Maps</div>
                        </div>
                        <div className="flex justify-center mb-3">
                            {printingQr.sv_maps_url ? (
                                <QRCodeSVG value={printingQr.sv_maps_url} size={220} level="M" includeMargin data-testid="qr-print-svg" />
                            ) : (
                                <div className="text-red-500 text-sm">Este pedido no tiene ubicación GPS.</div>
                            )}
                        </div>
                        <div className="text-center border-t border-b border-gray-300 py-2 my-2">
                            <div className="text-[10px] text-gray-500 uppercase tracking-widest">Plus Code</div>
                            <div className="font-mono text-lg font-bold">{printingQr.sv_plus_code_human || printingQr.sv_plus_code || "—"}</div>
                        </div>
                        <div className="text-[11px] text-gray-700 space-y-0.5">
                            <div><strong>Cliente:</strong> {printingQr.customer_name} {printingQr.customer_lastname}</div>
                            <div><strong>Tel:</strong> {printingQr.customer_phone || "—"}</div>
                            <div className="whitespace-pre-line mt-1"><strong>Dirección:</strong> {printingQr.shipping_address || "—"}</div>
                            {printingQr.sv_lat && (
                                <div className="text-[9px] text-gray-500 mt-1">Coord: {printingQr.sv_lat?.toFixed(6)}, {printingQr.sv_lng?.toFixed(6)}</div>
                            )}
                        </div>
                        <div className="mt-4 flex gap-2 justify-end print:hidden">
                            <button onClick={() => setPrintingQr(null)} className="px-3 py-1.5 text-xs border border-gray-300 rounded">Cerrar</button>
                            <button onClick={() => window.print()} className="px-3 py-1.5 text-xs bg-black text-white rounded inline-flex items-center gap-1" data-testid="qr-print-btn">
                                <Printer className="w-3 h-3" /> Imprimir
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}


// ============================================================
// Envíos C807 — Panel administrativo dedicado al courier salvadoreño
// ============================================================
function EnviosC807() {
    const [envios, setEnvios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterImpresa, setFilterImpresa] = useState("ALL"); // ALL | true | false
    const [filterTipo, setFilterTipo] = useState("ALL"); // ALL | SER | CCE
    const [search, setSearch] = useState("");
    const [busy, setBusy] = useState({});
    const [batchBusy, setBatchBusy] = useState(false);
    const [creating, setCreating] = useState(null); // {order, payload}
    const [orders, setOrders] = useState([]);
    const [departamentos, setDepartamentos] = useState([]);
    const [municipios, setMunicipios] = useState([]);

    const load = async () => {
        try {
            const params = new URLSearchParams();
            if (filterImpresa !== "ALL") params.set("impresa", filterImpresa);
            if (filterTipo !== "ALL") params.set("tipo", filterTipo);
            if (search.trim()) params.set("search", search.trim());
            const { data } = await api.get(`/c807/envios?${params}`);
            setEnvios(data);
        } catch (e) { toast.error("Error al cargar envíos C807"); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterImpresa, filterTipo]);
    // Auto-load orders + catálogos for the "create guía" modal once.
    useEffect(() => {
        api.get("/orders").then(({ data }) => setOrders((data || []).filter((o) => (o.country || "SV") === "SV" && o.needs_shipping && !o.c807_guia)));
        api.get("/c807/catalogo/departamentos").then(({ data }) => setDepartamentos(data.items || []));
    }, []);

    const onPickDepartamento = async (depId) => {
        if (!depId) { setMunicipios([]); return; }
        const { data } = await api.get(`/c807/catalogo/municipios?departamento=${depId}`);
        setMunicipios(data.items || []);
    };

    const downloadPdf = (b64, filename) => {
        const link = document.createElement("a");
        link.href = `data:application/pdf;base64,${b64}`;
        link.download = filename;
        link.click();
    };

    const imprimir = async (guia, reimprimir = false) => {
        setBusy((s) => ({ ...s, [guia]: true }));
        try {
            const path = reimprimir ? `/c807/reimprimir/${guia}` : `/c807/imprimir/${guia}`;
            const { data } = await api.post(path);
            downloadPdf(data.pdf_base64, `c807-${guia}.pdf`);
            toast.success(reimprimir ? "Re-impresa (no cambia estado)" : "Etiqueta impresa");
            load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setBusy((s) => ({ ...s, [guia]: false })); }
    };

    const eliminar = async (guia) => {
        if (!window.confirm(`¿Eliminar guía ${guia} del historial? La guía en el portal C807 NO se borra — solo desaparece de este tablero.`)) return;
        setBusy((s) => ({ ...s, [guia]: true }));
        try {
            await api.delete(`/c807/envios/${guia}`);
            toast.success("Guía eliminada del historial");
            setEnvios((prev) => prev.filter((e) => e.guia_numero !== guia));
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al eliminar"); }
        finally { setBusy((s) => ({ ...s, [guia]: false })); }
    };

    const imprimirPendientes = async () => {
        setBatchBusy(true);
        try {
            const { data } = await api.post("/c807/imprimir-pendientes");
            downloadPdf(data.pdf_base64, `c807-pendientes-${new Date().toISOString().slice(0, 10)}.pdf`);
            toast.success(`${data.count} etiquetas impresas en lote`);
            load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setBatchBusy(false); }
    };

    const refreshToken = async () => {
        try {
            const { data } = await api.post("/c807-token");
            toast.success(`Token C807 renovado${data.mock ? " (MOCK)" : ""}. Expira: ${new Date(data.expires_at).toLocaleString("es-SV")}`);
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const startCreate = (order) => {
        setCreating({
            order,
            payload: {
                tipo_servicio: "SER",
                peso: 1.5,
                unidad_medida: "LB",
                contenido: ((order.items || []).map((it) => it.nombre).join(", ") || "Mercadería").slice(0, 80),
                departamento_id: order.departamento_id || "",
                municipio_id: order.municipio_id || "",
                monto_cce: order.total || 0,
                recolecta_fecha: new Date(Date.now() + 18 * 3600 * 1000).toISOString().slice(0, 16).replace("T", " "),
            },
        });
        setMunicipios([]);
    };
    const submitCreate = async () => {
        const { order, payload } = creating;
        if (!payload.departamento_id || !payload.municipio_id) { toast.error("Selecciona departamento y municipio"); return; }
        try {
            const { data } = await api.post(`/c807/crear-guia/${order._id}`, payload);
            toast.success(`Guía creada: ${data.guia_numero}${data.mock ? " (MOCK)" : ""}`);
            setCreating(null);
            await load();
            // Refresh orders list (this order now has a guía)
            const ords = await api.get("/orders").then((r) => r.data);
            setOrders(ords.filter((o) => (o.country || "SV") === "SV" && o.needs_shipping && !o.c807_guia));
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    if (loading) return <div className="p-8 text-center text-[var(--tb-text-2)]">Cargando envíos C807...</div>;

    const stats = {
        total: envios.length,
        pendientes: envios.filter((e) => !e.impresa).length,
        ser: envios.filter((e) => e.tipo_servicio === "SER").length,
        cce: envios.filter((e) => e.tipo_servicio === "CCE").length,
        cce_total: envios.filter((e) => e.tipo_servicio === "CCE").reduce((a, e) => a + (e.monto_cce || 0), 0),
        mock: envios.filter((e) => e.is_mock).length,
    };

    return (
        <div className="space-y-5" data-testid="envios-c807-section">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="font-display text-2xl flex items-center gap-2"><Truck className="w-6 h-6 text-[var(--tb-gold)]" /> Envíos C807 Xpress</h2>
                    <p className="text-xs text-[var(--tb-text-2)]">Solo El Salvador · Guías SER y CCE · Impresión individual y en lote</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <button onClick={refreshToken} className="tb-btn-secondary !text-xs" data-testid="c807-refresh-token">Renovar Token</button>
                    <button onClick={load} className="tb-btn-secondary !text-xs"><RefreshCw className="w-3 h-3 inline-block" /> Refrescar</button>
                    <button onClick={imprimirPendientes} disabled={batchBusy || stats.pendientes === 0}
                        className="tb-btn-primary !text-xs disabled:opacity-50 inline-flex items-center gap-1" data-testid="c807-print-pending">
                        <Layers className="w-3 h-3" /> {batchBusy ? "Imprimiendo..." : `Imprimir ${stats.pendientes} pendientes`}
                    </button>
                </div>
            </div>

            {stats.mock > 0 && (
                <div className="tb-card p-3 border-amber-500/40 bg-amber-500/5 text-xs text-amber-300" data-testid="c807-mock-warning">
                    ⚠️ <strong>MOCK MODE activo</strong>: {stats.mock} de {stats.total} envíos fueron creados con datos simulados. Configura <code>C807_BASIC_USER</code> y <code>C807_BASIC_PASS</code> + pon <code>C807_MOCK=false</code> en el backend para conectar la API real.
                </div>
            )}

            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="c807-kpis">
                <div className="tb-card p-4"><div className="tb-overline">Total guías</div><div className="font-display text-2xl text-[var(--tb-gold)]">{stats.total}</div></div>
                <div className="tb-card p-4"><div className="tb-overline">Sin imprimir</div><div className="font-display text-2xl text-amber-400">{stats.pendientes}</div></div>
                <div className="tb-card p-4"><div className="tb-overline">SER (Regular)</div><div className="font-display text-2xl">{stats.ser}</div></div>
                <div className="tb-card p-4"><div className="tb-overline">CCE (Contra entrega)</div><div className="font-display text-2xl">{stats.cce}</div></div>
                <div className="tb-card p-4"><div className="tb-overline">$ pendiente CCE</div><div className="font-display text-2xl text-emerald-400">{formatPrice(stats.cce_total)}</div></div>
            </div>

            {/* Crear guía rápida */}
            {orders.length > 0 && (
                <div className="tb-card p-4" data-testid="c807-pending-orders">
                    <div className="flex items-center justify-between mb-3">
                        <div>
                            <h3 className="font-display text-lg">Pedidos SV sin guía generada</h3>
                            <p className="text-[10px] text-[var(--tb-text-2)]">{orders.length} pedido(s) listos para emitir guía C807.</p>
                        </div>
                    </div>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                        {orders.map((o) => (
                            <div key={o._id} className="flex items-center justify-between text-xs border border-[var(--tb-border)] p-2 rounded">
                                <div className="flex-1">
                                    <span className="font-mono text-[10px] text-[var(--tb-muted)]">{o._id.slice(0, 10)}</span>
                                    <span className="ml-2">{o.customer_name} {o.customer_lastname}</span>
                                    <span className="ml-2 text-[var(--tb-text-2)]">— {(o.items || []).length} prod · {formatPrice(o.total)}</span>
                                </div>
                                <button onClick={() => startCreate(o)} className="tb-btn-primary !text-[10px]" data-testid={`c807-create-${o._id}`}>
                                    <Plus className="w-3 h-3 inline mr-1" /> Crear guía
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className="tb-card p-4 flex items-center gap-3 flex-wrap" data-testid="c807-filters">
                <span className="text-xs text-[var(--tb-text-2)]">Filtros:</span>
                <select className="tb-input !py-1 !text-xs w-auto" value={filterTipo} onChange={(e) => setFilterTipo(e.target.value)} data-testid="c807-filter-tipo">
                    <option value="ALL">Todos los tipos</option>
                    <option value="SER">SER (Regular)</option>
                    <option value="CCE">CCE (Contra entrega)</option>
                </select>
                <select className="tb-input !py-1 !text-xs w-auto" value={filterImpresa} onChange={(e) => setFilterImpresa(e.target.value)} data-testid="c807-filter-impresa">
                    <option value="ALL">Todas</option>
                    <option value="false">Sin imprimir</option>
                    <option value="true">Ya impresas</option>
                </select>
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="w-3 h-3 absolute left-2 top-2 text-[var(--tb-muted)]" />
                    <input className="tb-input pl-7 !py-1 !text-xs" placeholder="Buscar por guía, cliente, email, orden..." value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="c807-search" />
                </div>
                <button onClick={load} className="tb-btn-secondary !text-xs">Aplicar</button>
            </div>

            {/* Tabla */}
            {envios.length === 0 ? (
                <div className="tb-card p-8 text-center text-[var(--tb-text-2)]" data-testid="c807-empty">No hay envíos en este filtro.</div>
            ) : (
                <div className="tb-card overflow-x-auto" data-testid="c807-table">
                    <table className="w-full text-xs">
                        <thead className="bg-[var(--tb-bg-2)]/40 text-[var(--tb-text-2)]">
                            <tr className="text-left">
                                <th className="p-3">Guía</th>
                                <th className="p-3">Cliente</th>
                                <th className="p-3">Tipo</th>
                                <th className="p-3">Último estado</th>
                                <th className="p-3">Creada</th>
                                <th className="p-3">Estado impresión</th>
                                <th className="p-3 text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {envios.map((e) => (
                                <tr key={e._id} className="border-t border-[var(--tb-border)] align-top" data-testid={`c807-row-${e.guia_numero}`}>
                                    <td className="p-3 font-mono text-[var(--tb-gold)]">{e.guia_numero}{e.is_mock && <span className="ml-1 text-amber-400 text-[9px]">MOCK</span>}</td>
                                    <td className="p-3">{e.customer_name}<div className="text-[10px] text-[var(--tb-muted)]">{e.user_email}</div></td>
                                    <td className="p-3"><span className={`tb-badge ${e.tipo_servicio === "CCE" ? "tb-badge-warning" : "tb-badge-muted"}`}>{e.tipo_servicio}{e.tipo_servicio === "CCE" && e.monto_cce ? ` · ${formatPrice(e.monto_cce)}` : ""}</span></td>
                                    <td className="p-3 text-[var(--tb-text-2)]">{e.ultimo_estatus || "—"}</td>
                                    <td className="p-3 text-[10px] text-[var(--tb-muted)]">{e.creada_en ? new Date(e.creada_en).toLocaleDateString("es-SV") : "—"}</td>
                                    <td className="p-3">
                                        {e.impresa ? (
                                            <span className="text-emerald-400 text-[11px] inline-flex items-center gap-1"><CheckSquare className="w-3 h-3" /> Impresa{e.impresiones > 1 && ` (${e.impresiones}x)`}</span>
                                        ) : (
                                            <span className="text-amber-400 text-[11px] inline-flex items-center gap-1"><Square className="w-3 h-3" /> Pendiente</span>
                                        )}
                                    </td>
                                    <td className="p-3 text-right">
                                        <div className="flex flex-col gap-1 items-end">
                                            {!e.impresa && (
                                                <button onClick={() => imprimir(e.guia_numero, false)} disabled={busy[e.guia_numero]}
                                                    className="text-[10px] px-2 py-1 border border-[var(--tb-gold)] text-[var(--tb-gold)] hover:bg-[var(--tb-border-gold)]/10 rounded inline-flex items-center gap-1 disabled:opacity-50"
                                                    data-testid={`c807-print-${e.guia_numero}`}>
                                                    <Printer className="w-3 h-3" /> {busy[e.guia_numero] ? "..." : "Imprimir"}
                                                </button>
                                            )}
                                            <button onClick={() => imprimir(e.guia_numero, true)} disabled={busy[e.guia_numero]}
                                                className="text-[10px] px-2 py-1 border border-[var(--tb-border)] hover:border-[var(--tb-gold)]/60 rounded inline-flex items-center gap-1 disabled:opacity-50"
                                                data-testid={`c807-reprint-${e.guia_numero}`}>
                                                <FileDown className="w-3 h-3" /> Reimprimir
                                            </button>
                                            <button onClick={() => eliminar(e.guia_numero)} disabled={busy[e.guia_numero]}
                                                className="text-[10px] px-2 py-1 border border-rose-500/40 text-rose-300 hover:bg-rose-500/10 rounded inline-flex items-center gap-1 disabled:opacity-50"
                                                data-testid={`c807-delete-${e.guia_numero}`}
                                                title="Eliminar del historial (no borra la guía en C807)">
                                                <Trash2 className="w-3 h-3" /> Eliminar
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Create guía modal */}
            {creating && (
                <div className="fixed inset-0 bg-black/80 z-50 flex items-start md:items-center justify-center p-4 overflow-y-auto" data-testid="c807-create-modal">
                    <div className="tb-card max-w-2xl w-full p-6 space-y-3 my-4">
                        <h3 className="font-display text-xl">Crear guía C807 · Pedido {creating.order._id.slice(0, 10)}</h3>
                        <div className="grid grid-cols-2 gap-3">
                            <label className="text-xs col-span-2">Tipo de servicio
                                <select className="tb-input mt-1" value={creating.payload.tipo_servicio}
                                    onChange={(e) => setCreating({ ...creating, payload: { ...creating.payload, tipo_servicio: e.target.value } })} data-testid="c807-modal-tipo">
                                    <option value="SER">SER — Entrega regular (cliente ya pagó)</option>
                                    <option value="CCE">CCE — Cobro contra entrega ({formatPrice(creating.order.total)})</option>
                                </select>
                            </label>
                            <label className="text-xs">Departamento *
                                <select className="tb-input mt-1" value={creating.payload.departamento_id}
                                    onChange={(e) => { const v = parseInt(e.target.value); setCreating({ ...creating, payload: { ...creating.payload, departamento_id: v, municipio_id: "" } }); onPickDepartamento(v); }}
                                    data-testid="c807-modal-dep">
                                    <option value="">Selecciona...</option>
                                    {departamentos.map((d) => <option key={d.id} value={d.id}>{d.nombre}</option>)}
                                </select>
                            </label>
                            <label className="text-xs">Municipio *
                                <select className="tb-input mt-1" value={creating.payload.municipio_id}
                                    onChange={(e) => setCreating({ ...creating, payload: { ...creating.payload, municipio_id: parseInt(e.target.value) } })}
                                    disabled={municipios.length === 0} data-testid="c807-modal-mun">
                                    <option value="">{municipios.length === 0 ? "Elige depto primero" : "Selecciona..."}</option>
                                    {municipios.map((m) => <option key={m.id} value={m.id}>{m.nombre}</option>)}
                                </select>
                            </label>
                            <label className="text-xs">Peso
                                <input type="number" step="0.1" min="0.1" className="tb-input mt-1" value={creating.payload.peso}
                                    onChange={(e) => setCreating({ ...creating, payload: { ...creating.payload, peso: parseFloat(e.target.value) || 1 } })} />
                            </label>
                            <label className="text-xs">Unidad
                                <select className="tb-input mt-1" value={creating.payload.unidad_medida}
                                    onChange={(e) => setCreating({ ...creating, payload: { ...creating.payload, unidad_medida: e.target.value } })}>
                                    <option value="LB">Libras (LB)</option>
                                    <option value="KG">Kilogramos (KG)</option>
                                </select>
                            </label>
                            <label className="text-xs col-span-2">Contenido del paquete
                                <input className="tb-input mt-1" value={creating.payload.contenido}
                                    onChange={(e) => setCreating({ ...creating, payload: { ...creating.payload, contenido: e.target.value } })} />
                            </label>
                            <label className="text-xs col-span-2">Fecha de recolecta (YYYY-MM-DD HH:MM)
                                <input className="tb-input mt-1 font-mono" value={creating.payload.recolecta_fecha}
                                    onChange={(e) => setCreating({ ...creating, payload: { ...creating.payload, recolecta_fecha: e.target.value } })} />
                            </label>
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <button onClick={() => setCreating(null)} className="tb-btn-secondary !text-xs">Cancelar</button>
                            <button onClick={submitCreate} className="tb-btn-primary !text-xs" data-testid="c807-modal-submit">Crear guía C807</button>
                        </div>
                    </div>
                </div>
            )}
            <AdminReturnsPanel country="SV" />
        </div>
    );
}


// ============================================================
function OrdersKanban() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    // Tablero dedicado a pedidos para recoger en tienda en El Salvador.
    // Pedidos de envío (SV/US) se gestionan en sus paneles dedicados.
    const board = "apartado";
    const [trackingOrder, setTrackingOrder] = useState(null);
    const [trackingInput, setTrackingInput] = useState("");

    const load = async () => {
        try {
            const { data } = await api.get("/orders");
            // SOLO El Salvador. Pickup-in-store independiente del método de pago.
            // Reglas:
            //  - apartado (efectivo)  → siempre se muestra (reserva, pago al recoger)
            //  - transferencia        → siempre se muestra (admin verifica pago manualmente)
            //  - stripe/paypal/card   → solo si payment_status=paid (pago confirmado)
            const filtered = (data || []).filter((o) => {
                if ((o.country || "SV") !== "SV") return false;
                if (o.needs_shipping) return false; // envíos en C807 / Etiquetas USA
                if (o.payment_method === "apartado") return true;
                if (o.payment_method === "transferencia") return true;
                return o.payment_status === "paid" || o.payment_confirmed === true;
            });
            setOrders(filtered);
        } catch (e) { toast.error("Error al cargar pedidos"); }
        finally { setLoading(false); }
    };
    useEffect(() => {
        load();
        const iv = setInterval(() => { load().catch(() => {}); }, 8000);
        return () => clearInterval(iv);
    }, []);

    const cfg = KANBAN_BOARDS[board];
    const boardOrders = orders;
    const grouped = cfg.columns.reduce((acc, col) => {
        acc[col.id] = boardOrders.filter((o) => (o.status || cfg.columns[0].id) === col.id);
        return acc;
    }, {});

    const onDragEnd = async (result) => {
        const { source, destination, draggableId } = result;
        if (!destination || source.droppableId === destination.droppableId) return;
        const newStatus = destination.droppableId;
        const order = orders.find((o) => o._id === draggableId);
        if (!order) return;

        if (newStatus === "completed" && order.payment_method === "apartado") {
            if (!window.confirm(`Al completar este pedido, se descontarán ${order.items.length} producto(s) del stock. ¿Continuar?`)) return;
        }
        if (newStatus === "payment_received" && order.payment_method === "transferencia") {
            if (!window.confirm(`Al confirmar 'Pago Recibido', se descontarán ${order.items.length} producto(s) del stock. ¿Continuar?`)) return;
        }
        if (newStatus === "cancelado") {
            if (!window.confirm("Cancelar este pedido liberará la reserva. ¿Continuar?")) return;
        }

        const prev = orders;
        setOrders(orders.map((o) => o._id === draggableId ? { ...o, status: newStatus } : o));
        try {
            if (newStatus === "shipped" && !order.tracking_number) {
                setTrackingOrder({ ...order, _pendingStatus: newStatus });
                setTrackingInput("");
                return;
            }
            await api.put(`/orders/${draggableId}/status`, { status: newStatus });
            toast.success(`Pedido movido a "${cfg.columns.find((c) => c.id === newStatus)?.title}"`);
            load();
        } catch (e) {
            setOrders(prev);
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al actualizar estado");
        }
    };

    const confirmTracking = async () => {
        if (!trackingOrder) return;
        try {
            await api.put(`/orders/${trackingOrder._id}/status`, { status: "shipped", tracking_number: trackingInput.trim() });
            toast.success("Pedido marcado como enviado con guía: " + trackingInput.trim());
            setTrackingOrder(null);
            setTrackingInput("");
            load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    if (loading) return <div className="p-8 text-center text-[var(--tb-text-2)]">Cargando pedidos...</div>;

    return (
        <div data-testid="orders-kanban">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div>
                    <h2 className="font-display text-2xl">🇸🇻 Pedidos para recoger en tienda</h2>
                    <p className="text-xs text-[var(--tb-text-2)]">
                        Arrastra las tarjetas para cambiar el estado · {boardOrders.length} pedido{boardOrders.length === 1 ? "" : "s"} de retiro en El Salvador.
                        Aquí ves <span className="text-emerald-400 font-medium">solo pagos exitosos</span> + apartados en efectivo.
                        Los envíos a SV/US son automáticos y se gestionan en <span className="text-[var(--tb-gold)]">Envíos C807</span> y <span className="text-[var(--tb-gold)]">Etiquetas USA</span>.
                    </p>
                </div>
                <button onClick={load} className="tb-btn-secondary !text-xs" data-testid="kanban-refresh">↻ Refrescar</button>
            </div>

            <DragDropContext onDragEnd={onDragEnd}>
                <div className="flex gap-3 overflow-x-auto pb-4" style={{ scrollbarWidth: "thin" }}>
                    {cfg.columns.map((col) => (
                        <Droppable droppableId={col.id} key={col.id}>
                            {(provided, snapshot) => (
                                <div ref={provided.innerRef} {...provided.droppableProps}
                                    className={`flex-shrink-0 w-72 bg-[var(--tb-bg-2)]/30 border border-[var(--tb-border)] rounded p-3 ${snapshot.isDraggingOver ? "bg-[var(--tb-border-gold)]/10 border-[var(--tb-gold)]" : ""}`}
                                    data-testid={`kanban-col-${col.id}`}>
                                    <div className="flex items-center justify-between mb-3 pb-2 border-b border-[var(--tb-border)]">
                                        <span className={`text-xs px-2 py-1 rounded font-medium border ${col.accent}`}>{col.title}</span>
                                        <span className="text-xs text-[var(--tb-muted)]">{(grouped[col.id] || []).length}</span>
                                    </div>
                                    <div className="space-y-2 min-h-[200px] max-h-[70vh] overflow-y-auto">
                                        {(grouped[col.id] || []).length === 0 && (
                                            <div className="text-center text-[10px] text-[var(--tb-muted)] py-6">Sin pedidos</div>
                                        )}
                                        {(grouped[col.id] || []).map((o, idx) => {
                                            const oc = (o.country || "SV") === "US" ? "US" : "SV";
                                            const borderColor = oc === "US" ? "#4A6FA5" : "#D4A744";
                                            return (
                                            <Draggable draggableId={o._id} index={idx} key={o._id}>
                                                {(prov, snap) => (
                                                    <div ref={prov.innerRef} {...prov.draggableProps}
                                                        style={{ ...prov.draggableProps.style, borderLeftColor: borderColor, borderLeftWidth: 3 }}
                                                        className={`bg-[var(--tb-bg)] border border-[var(--tb-border)] rounded p-3 text-xs select-none transition ${snap.isDragging ? "shadow-2xl rotate-1 border-[var(--tb-gold)]" : "hover:border-[var(--tb-gold)]/50"}`}
                                                        data-testid={`kanban-card-${o._id}`}>
                                                        <div className="flex items-start gap-2 mb-2">
                                                            <div {...prov.dragHandleProps} className="cursor-grab text-[var(--tb-muted)] hover:text-[var(--tb-gold)] pt-0.5">
                                                                <GripVertical className="w-3.5 h-3.5" />
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-center gap-1.5">
                                                                    <span title={oc === "US" ? "Estados Unidos" : "El Salvador"} data-testid={`kanban-card-flag-${o._id}`}>{oc === "US" ? "🇺🇸" : "🇸🇻"}</span>
                                                                    <span className="font-mono text-[10px] text-[var(--tb-muted)]">{(o._id || "").slice(0, 12)}</span>
                                                                </div>
                                                                <div className="font-medium text-[var(--tb-text)] truncate">{o.customer_name || o.user_email}</div>
                                                            </div>
                                                            {o.needs_shipping ? (
                                                                <Truck className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                                                            ) : (
                                                                <MapPin className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
                                                            )}
                                                        </div>
                                                        <div className="text-[var(--tb-text-2)] text-[11px] mb-1">
                                                            {(o.items || []).length} prod · <span className="text-[var(--tb-gold)] font-semibold">{formatPrice(o.total)}</span>
                                                        </div>
                                                        <div className="text-[10px] flex items-center gap-2 flex-wrap mt-1">
                                                            {o.payment_method === "apartado" ? (
                                                                <span className="px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 font-semibold">💰 EFECTIVO al recoger</span>
                                                            ) : o.payment_method === "transferencia" ? (
                                                                o.payment_confirmed
                                                                    ? <span className="px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 font-semibold">✅ TRANSFERENCIA confirmada</span>
                                                                    : <span className="px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-300 font-semibold">🏦 TRANSFERENCIA · verifica pago</span>
                                                            ) : o.payment_status === "paid" ? (
                                                                <span className="px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 font-semibold">✅ PAGADO {String(o.payment_method || "").toUpperCase()}</span>
                                                            ) : (
                                                                <span className="px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-300 font-semibold">⏳ Pago pendiente</span>
                                                            )}
                                                            <span className="capitalize text-[var(--tb-muted)]">{paymentMethodLabel(o.payment_method)}</span>
                                                            {o.inventory_locked && <span className="text-emerald-400">📦 Stock descontado</span>}
                                                        </div>
                                                        {o.tracking_number && (
                                                            <div className="text-[10px] mt-1 px-1.5 py-0.5 bg-[var(--tb-border-gold)]/10 text-[var(--tb-gold)] font-mono rounded inline-block">
                                                                📦 {o.tracking_number}
                                                            </div>
                                                        )}
                                                        <div className="text-[10px] text-[var(--tb-muted)] mt-1">
                                                            {o.created_at ? new Date(o.created_at).toLocaleDateString("es-SV") : ""}
                                                        </div>
                                                    </div>
                                                )}
                                            </Draggable>
                                            );
                                        })}
                                        {provided.placeholder}
                                    </div>
                                </div>
                            )}
                        </Droppable>
                    ))}
                </div>
            </DragDropContext>

            {trackingOrder && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-start md:items-center justify-center p-4 overflow-y-auto" onClick={() => setTrackingOrder(null)}>
                    <div className="bg-[var(--tb-bg)] border border-[var(--tb-border)] p-6 max-w-md w-full my-4" onClick={(e) => e.stopPropagation()} data-testid="tracking-modal">
                        <h3 className="font-display text-xl mb-2 flex items-center gap-2"><Truck className="w-5 h-5 text-[var(--tb-gold)]" /> Número de guía</h3>
                        <p className="text-xs text-[var(--tb-text-2)] mb-4">Ingresa el número de guía/tracking para este envío.</p>
                        <input className="tb-input mb-3" placeholder="Ej. C807-12345678" value={trackingInput} onChange={(e) => setTrackingInput(e.target.value)} autoFocus data-testid="tracking-input" />
                        <div className="flex gap-2">
                            <button onClick={confirmTracking} disabled={!trackingInput.trim()} className="tb-btn-primary !text-sm flex-1 disabled:opacity-50" data-testid="tracking-confirm">Confirmar envío</button>
                            <button onClick={() => { setTrackingOrder(null); load(); }} className="tb-link text-sm">Cancelar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}



// ---------- DISCOUNTS ----------
const blankDiscount = () => ({
    name: "", type: "percent", value: 10, scope: "all", categories: [],
    active: true, banner_image: "", banner_title: "", banner_subtitle: "",
    valid_from: "", valid_to: "",
});

function Descuentos() {
    const [list, setList] = useState([]);
    const [editing, setEditing] = useState(null);

    const load = async () => { const { data } = await api.get("/discounts"); setList(data); };
    useEffect(() => { load(); }, []);

    const save = async () => {
        if (!editing.name.trim()) { toast.error("Nombre requerido"); return; }
        if (!editing.value || editing.value <= 0) { toast.error("Valor debe ser mayor que 0"); return; }
        if (editing.scope === "categories" && (!editing.categories || editing.categories.length === 0)) {
            toast.error("Selecciona al menos una categoría"); return;
        }
        try {
            const payload = { ...editing, value: parseFloat(editing.value) };
            if (editing._id) await api.put(`/discounts/${editing._id}`, payload);
            else await api.post("/discounts", payload);
            toast.success("Descuento guardado");
            setEditing(null); await load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const toggle = async (did) => {
        try { await api.post(`/discounts/${did}/toggle`); await load(); }
        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const remove = async (did) => {
        if (!window.confirm("¿Eliminar este descuento permanentemente?")) return;
        await api.delete(`/discounts/${did}`);
        toast.success("Eliminado");
        await load();
    };

    const toggleCat = (cat) => {
        const cur = editing.categories || [];
        const next = cur.includes(cat) ? cur.filter((c) => c !== cat) : [...cur, cat];
        setEditing({ ...editing, categories: next });
    };

    const onPickBanner = async (file) => {
        if (!file) return;
        const r = new FileReader();
        r.onload = () => setEditing({ ...editing, banner_image: r.result });
        r.readAsDataURL(file);
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="font-display text-2xl">Sistema de descuentos</h2>
                    <p className="text-xs text-[var(--tb-text-2)]">Aplica descuentos a todo el catálogo o solo a categorías seleccionadas. Cuando hay uno activo, se muestra un banner en la página principal.</p>
                </div>
                <button onClick={() => setEditing(blankDiscount())} className="tb-btn-primary !text-sm inline-flex items-center gap-2" data-testid="discount-new">
                    <Plus className="w-4 h-4" /> Nuevo descuento
                </button>
            </div>

            {editing && (
                <div className="tb-card p-5 space-y-3" data-testid="discount-form">
                    <div className="grid md:grid-cols-2 gap-3">
                        <div>
                            <label className="tb-overline">Nombre del descuento *</label>
                            <input className="tb-input" placeholder="Ej. Promo de verano" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="discount-name" />
                        </div>
                        <div>
                            <label className="tb-overline">Estado</label>
                            <select className="tb-input" value={editing.active ? "1" : "0"} onChange={(e) => setEditing({ ...editing, active: e.target.value === "1" })}>
                                <option value="1">Activo</option>
                                <option value="0">Inactivo (guardado pero oculto)</option>
                            </select>
                        </div>
                        <div>
                            <label className="tb-overline">Tipo *</label>
                            <select className="tb-input" value={editing.type} onChange={(e) => setEditing({ ...editing, type: e.target.value })} data-testid="discount-type">
                                <option value="percent">Porcentaje (%)</option>
                                <option value="fixed">Monto fijo (USD)</option>
                            </select>
                        </div>
                        <div>
                            <label className="tb-overline">Valor *</label>
                            <input type="number" min="0" step="0.01" className="tb-input" value={editing.value} onChange={(e) => setEditing({ ...editing, value: e.target.value })} data-testid="discount-value" />
                        </div>
                        <div className="md:col-span-2">
                            <label className="tb-overline">Aplicar a *</label>
                            <div className="flex gap-3 mt-1">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input type="radio" checked={editing.scope === "all"} onChange={() => setEditing({ ...editing, scope: "all" })} data-testid="discount-scope-all" /> Todo el inventario
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input type="radio" checked={editing.scope === "categories"} onChange={() => setEditing({ ...editing, scope: "categories" })} data-testid="discount-scope-cats" /> Solo categorías
                                </label>
                            </div>
                            {editing.scope === "categories" && (
                                <div className="mt-2 flex flex-wrap gap-1.5">
                                    {CATEGORIES.map((c) => {
                                        const on = (editing.categories || []).includes(c);
                                        return (
                                            <button key={c} type="button" onClick={() => toggleCat(c)} className={`text-xs px-2 py-1 border transition ${on ? "border-[var(--tb-gold)] text-[var(--tb-gold)] bg-[var(--tb-border-gold)]/10" : "border-[var(--tb-border)] text-[var(--tb-text-2)]"}`}>
                                                {c}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                        <div>
                            <label className="tb-overline">Válido desde</label>
                            <input type="date" className="tb-input" value={editing.valid_from || ""} onChange={(e) => setEditing({ ...editing, valid_from: e.target.value })} />
                        </div>
                        <div>
                            <label className="tb-overline">Válido hasta</label>
                            <input type="date" className="tb-input" value={editing.valid_to || ""} onChange={(e) => setEditing({ ...editing, valid_to: e.target.value })} />
                        </div>
                        <div className="md:col-span-2 border-t border-[var(--tb-border)] pt-3 mt-2">
                            <div className="tb-overline text-[var(--tb-gold)] mb-2">Banner para la página principal</div>
                            <div className="grid md:grid-cols-2 gap-3">
                                <div>
                                    <label className="tb-overline">Título del banner</label>
                                    <input className="tb-input" placeholder="¡Ofertas de Temporada!" value={editing.banner_title || ""} onChange={(e) => setEditing({ ...editing, banner_title: e.target.value })} data-testid="discount-banner-title" />
                                </div>
                                <div>
                                    <label className="tb-overline">Subtítulo</label>
                                    <input className="tb-input" placeholder="En toda la colección Diva..." value={editing.banner_subtitle || ""} onChange={(e) => setEditing({ ...editing, banner_subtitle: e.target.value })} />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="tb-overline">Imagen del banner (opcional)</label>
                                    <div className="flex items-center gap-3 mt-1">
                                        {editing.banner_image && <img src={editing.banner_image} alt="" className="w-20 h-20 object-cover rounded border border-[var(--tb-border)]" />}
                                        <label className="tb-btn-secondary !text-xs cursor-pointer inline-flex items-center gap-1.5">
                                            <Upload className="w-3.5 h-3.5" /> Subir imagen
                                            <input type="file" accept="image/*" className="hidden" onChange={(e) => onPickBanner(e.target.files[0])} data-testid="discount-banner-upload" />
                                        </label>
                                        {editing.banner_image && <button type="button" onClick={() => setEditing({ ...editing, banner_image: "" })} className="text-xs text-red-500">Quitar</button>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="flex gap-2 pt-2">
                        <button onClick={save} className="tb-btn-primary !text-sm inline-flex items-center gap-2" data-testid="discount-save">
                            <Save className="w-4 h-4" /> Guardar descuento
                        </button>
                        <button onClick={() => setEditing(null)} className="tb-link text-sm">Cancelar</button>
                    </div>
                </div>
            )}

            <div className="space-y-2">
                {list.length === 0 ? (
                    <div className="tb-card p-6 text-center text-sm text-[var(--tb-text-2)]">Sin descuentos creados aún.</div>
                ) : list.map((d) => (
                    <div key={d._id} className="tb-card p-4" data-testid={`discount-row-${d._id}`}>
                        <div className="flex items-start gap-3 flex-wrap">
                            {d.banner_image && <img src={d.banner_image} alt="" className="w-14 h-14 object-cover rounded" />}
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-mono text-[10px] text-[var(--tb-muted)]">{d._id}</span>
                                    <span className={`tb-badge !text-[10px] ${d.active ? "tb-badge-success" : "tb-badge-muted"}`}>{d.active ? "Activo" : "Inactivo"}</span>
                                </div>
                                <div className="font-medium">{d.name}</div>
                                <div className="text-xs text-[var(--tb-text-2)]">
                                    <span className="text-[var(--tb-gold)] font-semibold">
                                        {d.type === "percent" ? `${d.value}% off` : `$${d.value} off`}
                                    </span>
                                    {" · "}
                                    {d.scope === "all" ? "Todo el catálogo" : `Categorías: ${(d.categories || []).join(", ")}`}
                                    {(d.valid_from || d.valid_to) && (
                                        <span> · Vigencia: {d.valid_from || "—"} → {d.valid_to || "—"}</span>
                                    )}
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button onClick={() => toggle(d._id)} className="text-xs tb-link" data-testid={`discount-toggle-${d._id}`}>
                                    {d.active ? "Desactivar" : "Activar"}
                                </button>
                                <button onClick={() => setEditing({ ...d, value: d.value })} className="text-xs tb-link inline-flex items-center gap-1">
                                    <Edit3 className="w-3 h-3" /> Editar
                                </button>
                                <button onClick={() => remove(d._id)} className="text-xs text-red-500 inline-flex items-center gap-1">
                                    <Trash2 className="w-3 h-3" /> Eliminar
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}



// ---------- Cupones (códigos de descuento) ----------
function Cupones() {
    const blank = { code: "", name: "", amount: 0, type: "fixed", active: true, max_uses: "", min_subtotal: 0, country: "" };
    const [list, setList] = useState([]);
    const [editing, setEditing] = useState(null);
    const [isNew, setIsNew] = useState(false);

    const load = () => api.get("/admin/discount-codes").then(({ data }) => setList(data || []));
    useEffect(() => { load(); }, []);

    const startNew = () => { setEditing({ ...blank }); setIsNew(true); };
    const startEdit = (d) => { setEditing({ ...d, max_uses: d.max_uses ?? "" }); setIsNew(false); };

    const save = async () => {
        if (!editing.code.trim()) { toast.error("El código es requerido"); return; }
        if (!editing.amount || Number(editing.amount) <= 0) { toast.error("El monto debe ser mayor a 0"); return; }
        if (editing.type === "percent" && Number(editing.amount) > 90) { toast.error("El porcentaje no puede ser mayor a 90"); return; }
        try {
            const payload = {
                code: editing.code,
                name: editing.name || "",
                amount: Number(editing.amount),
                type: editing.type,
                active: !!editing.active,
                max_uses: editing.max_uses ? Number(editing.max_uses) : null,
                min_subtotal: Number(editing.min_subtotal || 0),
                country: editing.country || null,
            };
            if (isNew) {
                await api.post("/admin/discount-codes", payload);
                toast.success(`Cupón ${payload.code.toUpperCase()} creado`);
            } else {
                await api.put(`/admin/discount-codes/${editing.code}`, payload);
                toast.success(`Cupón ${payload.code.toUpperCase()} actualizado`);
            }
            setEditing(null);
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al guardar");
        }
    };

    const toggle = async (code) => {
        try { await api.post(`/admin/discount-codes/${code}/toggle`); load(); }
        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    const remove = async (code) => {
        if (!window.confirm(`¿Eliminar cupón ${code}? Esta acción no se puede deshacer.`)) return;
        try { await api.delete(`/admin/discount-codes/${code}`); load(); toast.success("Cupón eliminado"); }
        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    return (
        <div className="space-y-4" data-testid="cupones-section">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h2 className="font-display text-2xl mb-1">Cupones de descuento</h2>
                    <p className="text-xs text-[var(--tb-text-2)] max-w-2xl">
                        Crea códigos que tus clientes ingresarán al pagar para obtener un descuento.
                        Tú decides el monto (fijo en USD o porcentaje) y cuáles están activos. Los códigos
                        desactivados siguen guardados, pero no podrán usarse hasta reactivarlos.
                    </p>
                </div>
                <button onClick={startNew} className="tb-btn-primary inline-flex items-center gap-2" data-testid="cupon-new">
                    <Plus className="w-4 h-4" /> Nuevo cupón
                </button>
            </div>

            {editing && (
                <div className="tb-card p-5 space-y-4 border-[var(--tb-gold)]/40" data-testid="cupon-editor">
                    <div className="flex items-center justify-between">
                        <h3 className="font-display text-lg">{isNew ? "Crear cupón" : `Editar cupón ${editing.code}`}</h3>
                        <button onClick={() => setEditing(null)} className="text-[var(--tb-muted)] hover:text-[var(--tb-text)]"><X className="w-4 h-4" /></button>
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                        <div>
                            <label className="tb-overline">Código (lo que el cliente escribirá) *</label>
                            <input
                                className="tb-input uppercase font-mono"
                                placeholder="VERANO2026"
                                value={editing.code}
                                disabled={!isNew}
                                onChange={(e) => setEditing({ ...editing, code: e.target.value.toUpperCase().replace(/\s/g, "") })}
                                data-testid="cupon-code"
                            />
                            {!isNew && <span className="text-[10px] text-[var(--tb-muted)]">El código no se puede cambiar. Elimina y crea uno nuevo si lo necesitas.</span>}
                        </div>
                        <div>
                            <label className="tb-overline">Nombre (sólo para tu referencia)</label>
                            <input
                                className="tb-input"
                                placeholder="Ej. Promoción de Verano"
                                value={editing.name || ""}
                                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                                data-testid="cupon-name"
                            />
                        </div>
                        <div>
                            <label className="tb-overline">Tipo de descuento *</label>
                            <select
                                className="tb-input"
                                value={editing.type}
                                onChange={(e) => setEditing({ ...editing, type: e.target.value })}
                                data-testid="cupon-type"
                            >
                                <option value="fixed">Monto fijo (USD)</option>
                                <option value="percent">Porcentaje (%)</option>
                            </select>
                        </div>
                        <div>
                            <label className="tb-overline">{editing.type === "percent" ? "Porcentaje a descontar *" : "Monto a descontar (USD) *"}</label>
                            <div className="relative">
                                <span className="absolute left-3 top-2.5 text-[var(--tb-text-2)] text-sm pointer-events-none">
                                    {editing.type === "percent" ? "%" : "$"}
                                </span>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max={editing.type === "percent" ? "90" : undefined}
                                    className="tb-input pl-8"
                                    placeholder={editing.type === "percent" ? "10" : "5.00"}
                                    value={editing.amount}
                                    onChange={(e) => setEditing({ ...editing, amount: e.target.value })}
                                    data-testid="cupon-amount"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="tb-overline">Compra mínima requerida (opcional)</label>
                            <div className="relative">
                                <span className="absolute left-3 top-2.5 text-[var(--tb-text-2)] text-sm pointer-events-none">$</span>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    className="tb-input pl-8"
                                    placeholder="0.00"
                                    value={editing.min_subtotal || ""}
                                    onChange={(e) => setEditing({ ...editing, min_subtotal: e.target.value })}
                                    data-testid="cupon-min-subtotal"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="tb-overline">Usos máximos (opcional)</label>
                            <input
                                type="number"
                                min="1"
                                className="tb-input"
                                placeholder="Dejar vacío = sin límite"
                                value={editing.max_uses}
                                onChange={(e) => setEditing({ ...editing, max_uses: e.target.value })}
                                data-testid="cupon-max-uses"
                            />
                        </div>
                        <div>
                            <label className="tb-overline">País (opcional)</label>
                            <select
                                className="tb-input"
                                value={editing.country || ""}
                                onChange={(e) => setEditing({ ...editing, country: e.target.value })}
                                data-testid="cupon-country"
                            >
                                <option value="">Ambos (SV y USA)</option>
                                <option value="SV">Solo El Salvador</option>
                                <option value="US">Solo Estados Unidos</option>
                            </select>
                        </div>
                        <div className="flex items-center gap-2 mt-6">
                            <input
                                type="checkbox"
                                id="cupon-active"
                                checked={!!editing.active}
                                onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                                data-testid="cupon-active"
                            />
                            <label htmlFor="cupon-active" className="text-sm">Cupón activo (los clientes pueden usarlo)</label>
                        </div>
                    </div>
                    <div className="flex gap-2 pt-2 border-t border-[var(--tb-border)]">
                        <button onClick={save} className="tb-btn-primary inline-flex items-center gap-2" data-testid="cupon-save">
                            <Save className="w-4 h-4" /> {isNew ? "Crear cupón" : "Guardar cambios"}
                        </button>
                        <button onClick={() => setEditing(null)} className="tb-btn-secondary">Cancelar</button>
                    </div>
                </div>
            )}

            {list.length === 0 ? (
                <div className="tb-card p-8 text-center" data-testid="cupones-empty">
                    <BadgeCheck className="w-10 h-10 mx-auto text-[var(--tb-muted)] mb-2" />
                    <div className="text-sm text-[var(--tb-text-2)]">Aún no has creado cupones.</div>
                    <div className="text-xs text-[var(--tb-muted)] mt-1">Haz clic en &quot;Nuevo cupón&quot; para crear el primero.</div>
                </div>
            ) : (
                <div className="space-y-2">
                    {list.map((c) => {
                        const usesLeft = c.max_uses ? `${c.max_uses - (c.uses_count || 0)}/${c.max_uses} restantes` : `${c.uses_count || 0} usos`;
                        return (
                            <div key={c._id} className="tb-card p-4 flex items-center gap-3 flex-wrap" data-testid={`cupon-row-${c._id}`}>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-mono font-semibold text-[var(--tb-gold)]">{c.code}</span>
                                        <span className={`tb-badge !text-[10px] ${c.active ? "tb-badge-success" : "tb-badge-muted"}`}>
                                            {c.active ? "Activo" : "Inactivo"}
                                        </span>
                                        {c.country && <span className="tb-badge !text-[10px] tb-badge-muted">{c.country}</span>}
                                    </div>
                                    {c.name && <div className="text-sm text-[var(--tb-text-2)]">{c.name}</div>}
                                    <div className="text-xs text-[var(--tb-text-2)] mt-0.5">
                                        <span className="text-[var(--tb-gold)] font-semibold">
                                            {c.type === "percent" ? `${c.amount}% off` : `$${Number(c.amount).toFixed(2)} off`}
                                        </span>
                                        {c.min_subtotal > 0 && <span> · Compra mínima ${Number(c.min_subtotal).toFixed(2)}</span>}
                                        {" · "}{usesLeft}
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <button onClick={() => toggle(c._id)} className="text-xs tb-link" data-testid={`cupon-toggle-${c._id}`}>
                                        {c.active ? "Desactivar" : "Activar"}
                                    </button>
                                    <button onClick={() => startEdit(c)} className="text-xs tb-link inline-flex items-center gap-1" data-testid={`cupon-edit-${c._id}`}>
                                        <Edit3 className="w-3 h-3" /> Editar
                                    </button>
                                    <button onClick={() => remove(c._id)} className="text-xs text-red-500 inline-flex items-center gap-1" data-testid={`cupon-delete-${c._id}`}>
                                        <Trash2 className="w-3 h-3" /> Eliminar
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ============================================================
// FEEDBACK ADMIN — opiniones de usuarios (anónimo o nombrado)
// ============================================================
function FeedbackAdmin() {
    const [list, setList] = useState([]);
    const [country, setCountry] = useState("SV");
    const [onlyUnread, setOnlyUnread] = useState(false);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ country });
            if (onlyUnread) params.append("only_unread", "true");
            const { data } = await api.get(`/admin/feedback?${params}`);
            setList(data || []);
        } catch (e) { toast.error("Error al cargar feedback"); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [country, onlyUnread]);

    const markRead = async (id) => {
        try { await api.post(`/admin/feedback/${id}/read`); load(); }
        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    const remove = async (id) => {
        if (!window.confirm("¿Eliminar este feedback? No se puede recuperar.")) return;
        try { await api.delete(`/admin/feedback/${id}`); load(); toast.success("Eliminado"); }
        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const counts = { unread: list.filter((f) => !f.read).length, total: list.length };
    const catLabel = { general: "General", elogio: "Elogio", sugerencia: "Sugerencia", bug: "Error", reclamo: "Reclamo" };
    const catColor = { elogio: "bg-emerald-500/15 text-emerald-300", sugerencia: "bg-blue-500/15 text-blue-300", bug: "bg-rose-500/15 text-rose-300", reclamo: "bg-amber-500/15 text-amber-300", general: "bg-gray-500/15 text-gray-300" };

    return (
        <div className="space-y-4" data-testid="feedback-admin">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h2 className="font-display text-2xl flex items-center gap-2"><MessageSquare className="w-6 h-6 text-[var(--tb-gold)]" /> Feedback de usuarios</h2>
                    <p className="text-xs text-[var(--tb-text-2)]">Opiniones enviadas desde el botón flotante. Pueden ser anónimas. Separadas por país.</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {/* Country tabs */}
                    <div className="flex bg-[var(--tb-bg-2)]/40 rounded-full p-1">
                        {[{ id: "SV", flag: "🇸🇻" }, { id: "US", flag: "🇺🇸" }].map((c) => (
                            <button key={c.id} onClick={() => setCountry(c.id)}
                                className={`text-xs px-3 py-1.5 rounded-full transition flex items-center gap-1 ${country === c.id ? "bg-[var(--tb-gold)] text-black font-semibold" : "text-[var(--tb-text-2)]"}`}
                                data-testid={`feedback-country-${c.id}`}>
                                <span>{c.flag}</span> {c.id}
                            </button>
                        ))}
                    </div>
                    <button onClick={() => setOnlyUnread(!onlyUnread)}
                        className={`text-xs px-3 py-1.5 rounded-full border transition ${onlyUnread ? "border-[var(--tb-gold)] text-[var(--tb-gold)]" : "border-[var(--tb-border)] text-[var(--tb-text-2)]"}`}
                        data-testid="feedback-only-unread">
                        {onlyUnread ? "Solo no leídas" : "Todas"} ({counts.unread}/{counts.total})
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="tb-card p-6 text-center text-sm text-[var(--tb-text-2)]">Cargando...</div>
            ) : list.length === 0 ? (
                <div className="tb-card p-10 text-center" data-testid="feedback-empty">
                    <MessageSquare className="w-10 h-10 mx-auto text-[var(--tb-muted)] mb-2" />
                    <p className="text-sm text-[var(--tb-text-2)]">No hay feedback aún para {country}.</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {list.map((f) => (
                        <article key={f._id} onClick={() => !f.read && markRead(f._id)}
                            className={`tb-card p-4 cursor-pointer transition ${f.read ? "opacity-70" : "border-l-4 !border-l-[var(--tb-gold)]"}`}
                            data-testid={`feedback-row-${f._id}`}>
                            <header className="flex items-center gap-2 mb-2 flex-wrap">
                                <span className={`text-[10px] px-2 py-0.5 rounded font-semibold uppercase ${catColor[f.category] || catColor.general}`}>
                                    {catLabel[f.category] || f.category}
                                </span>
                                {f.rating ? (
                                    <span className="text-[10px] inline-flex items-center gap-0.5">
                                        {[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`w-3 h-3 ${n <= f.rating ? "text-[var(--tb-gold)] fill-[var(--tb-gold)]" : "text-[var(--tb-muted)]"}`} />)}
                                    </span>
                                ) : null}
                                {f.anonymous ? (
                                    <span className="text-[10px] text-[var(--tb-muted)] italic">Anónimo</span>
                                ) : (
                                    <span className="text-[10px] text-[var(--tb-text-2)]">{f.name || f.user_email || f.email || "—"}</span>
                                )}
                                {!f.read && <span className="ml-auto w-2 h-2 bg-[var(--tb-gold)] rounded-full" />}
                            </header>
                            <p className="text-sm whitespace-pre-line">{f.message}</p>
                            <footer className="flex items-center justify-between mt-3 text-[10px] text-[var(--tb-muted)]">
                                <span>{f.page} · {new Date(f.created_at).toLocaleString("es-SV")}</span>
                                <button onClick={(e) => { e.stopPropagation(); remove(f._id); }} className="hover:text-rose-400 inline-flex items-center gap-1" data-testid={`feedback-delete-${f._id}`}>
                                    <Trash2 className="w-3 h-3" /> Eliminar
                                </button>
                            </footer>
                        </article>
                    ))}
                </div>
            )}
        </div>
    );
}


// ============================================================
// SITE REVIEWS ADMIN — moderación de reseñas públicas del sitio
// ============================================================
function SiteReviewsAdmin() {
    const [list, setList] = useState([]);
    const [country, setCountry] = useState("SV");
    const [status, setStatus] = useState("pending"); // pending | approved | rejected | all
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ country });
            if (status !== "all") params.append("status", status);
            const { data } = await api.get(`/admin/site-reviews?${params}`);
            setList(data || []);
        } catch (e) { toast.error("Error al cargar reseñas"); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [country, status]);

    const approve = async (id) => {
        try { await api.post(`/admin/site-reviews/${id}/approve`); toast.success("Reseña aprobada"); load(); }
        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    const reject = async (id) => {
        try { await api.post(`/admin/site-reviews/${id}/reject`); toast.success("Reseña rechazada"); load(); }
        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    const remove = async (id) => {
        if (!window.confirm("¿Eliminar reseña permanentemente?")) return;
        try { await api.delete(`/admin/site-reviews/${id}`); toast.success("Eliminada"); load(); }
        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const statusColor = { pending: "bg-amber-500/15 text-amber-300", approved: "bg-emerald-500/15 text-emerald-300", rejected: "bg-rose-500/15 text-rose-300" };

    return (
        <div className="space-y-4" data-testid="reviews-admin">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h2 className="font-display text-2xl flex items-center gap-2"><Star className="w-6 h-6 text-[var(--tb-gold)]" /> Reseñas del sitio</h2>
                    <p className="text-xs text-[var(--tb-text-2)]">Reseñas sobre la experiencia general (no productos). Aprueba para publicarlas. Separadas por país.</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex bg-[var(--tb-bg-2)]/40 rounded-full p-1">
                        {[{ id: "SV", flag: "🇸🇻" }, { id: "US", flag: "🇺🇸" }].map((c) => (
                            <button key={c.id} onClick={() => setCountry(c.id)}
                                className={`text-xs px-3 py-1.5 rounded-full transition flex items-center gap-1 ${country === c.id ? "bg-[var(--tb-gold)] text-black font-semibold" : "text-[var(--tb-text-2)]"}`}
                                data-testid={`reviews-country-${c.id}`}>
                                <span>{c.flag}</span> {c.id}
                            </button>
                        ))}
                    </div>
                    <select value={status} onChange={(e) => setStatus(e.target.value)} className="tb-input !text-xs !py-1.5" data-testid="reviews-status-filter">
                        <option value="pending">Pendientes</option>
                        <option value="approved">Aprobadas</option>
                        <option value="rejected">Rechazadas</option>
                        <option value="all">Todas</option>
                    </select>
                </div>
            </div>

            {loading ? (
                <div className="tb-card p-6 text-center text-sm text-[var(--tb-text-2)]">Cargando...</div>
            ) : list.length === 0 ? (
                <div className="tb-card p-10 text-center" data-testid="reviews-empty">
                    <Star className="w-10 h-10 mx-auto text-[var(--tb-muted)] mb-2" />
                    <p className="text-sm text-[var(--tb-text-2)]">No hay reseñas en este filtro.</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {list.map((r) => (
                        <article key={r._id} className="tb-card p-4" data-testid={`review-row-${r._id}`}>
                            <header className="flex items-start gap-3 mb-2">
                                {r.user_photo ? (
                                    <img src={r.user_photo} alt="" className="w-10 h-10 rounded-full object-cover border border-[var(--tb-border)] shrink-0" />
                                ) : (
                                    <div className="w-10 h-10 rounded-full bg-[var(--tb-bg-2)] border border-[var(--tb-border)] flex items-center justify-center font-display shrink-0">
                                        {(r.user_name || "?").charAt(0).toUpperCase()}
                                    </div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        <span className="font-medium text-sm">{r.user_name}</span>
                                        {r.user_verified && <BadgeCheck className="w-3.5 h-3.5 text-[#2196F3] fill-[#2196F3]/20" />}
                                        <span className={`text-[10px] px-2 py-0.5 rounded font-semibold uppercase ${statusColor[r.status] || ""}`}>{r.status}</span>
                                    </div>
                                    <div className="flex items-center gap-1 mt-0.5">
                                        {[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`w-3 h-3 ${n <= r.rating ? "text-[var(--tb-gold)] fill-[var(--tb-gold)]" : "text-[var(--tb-muted)]"}`} />)}
                                        <span className="text-[10px] text-[var(--tb-muted)] ml-2">{new Date(r.created_at).toLocaleString("es-SV")} · {r.user_email}</span>
                                    </div>
                                </div>
                            </header>
                            {r.title && <h4 className="font-medium text-sm mb-1">{r.title}</h4>}
                            <p className="text-sm text-[var(--tb-text-2)] whitespace-pre-line">{r.content}</p>
                            <div className="flex gap-2 mt-3 pt-2 border-t border-[var(--tb-border)]/50">
                                {r.status !== "approved" && (
                                    <button onClick={() => approve(r._id)} className="tb-btn-primary !text-xs inline-flex items-center gap-1.5" data-testid={`review-approve-${r._id}`}>
                                        <Check className="w-3.5 h-3.5" /> Aprobar y publicar
                                    </button>
                                )}
                                {r.status !== "rejected" && (
                                    <button onClick={() => reject(r._id)} className="tb-btn-secondary !text-xs inline-flex items-center gap-1.5" data-testid={`review-reject-${r._id}`}>
                                        <X className="w-3.5 h-3.5" /> Rechazar
                                    </button>
                                )}
                                <button onClick={() => remove(r._id)} className="tb-link !text-xs text-rose-400 inline-flex items-center gap-1 ml-auto" data-testid={`review-delete-${r._id}`}>
                                    <Trash2 className="w-3.5 h-3.5" /> Eliminar
                                </button>
                            </div>
                        </article>
                    ))}
                </div>
            )}
        </div>
    );
}



// ---------- Suggestion Campaign (monthly credit program) ----------
function SuggestionCampaignAdmin() {
    const [cfg, setCfg] = useState({
        suggestion_campaign_active: false,
        suggestion_credit_amount: 1.0,
        suggestion_campaign_title: "",
        suggestion_campaign_message: "",
        suggestion_campaign_button_text: "",
        suggestion_campaign_month: "",
    });
    const [original, setOriginal] = useState("");
    const [saving, setSaving] = useState(false);
    const [items, setItems] = useState([]);
    const [filterMonth, setFilterMonth] = useState("");
    const [loadingList, setLoadingList] = useState(false);

    const currentMonth = new Date().toISOString().slice(0, 7);

    useEffect(() => {
        api.get("/admin/settings").then(({ data }) => {
            const next = {
                suggestion_campaign_active: !!data.suggestion_campaign_active,
                suggestion_credit_amount: data.suggestion_credit_amount ?? 1.0,
                suggestion_campaign_title: data.suggestion_campaign_title || "",
                suggestion_campaign_message: data.suggestion_campaign_message || "",
                suggestion_campaign_button_text: data.suggestion_campaign_button_text || "Enviar sugerencia",
                suggestion_campaign_month: data.suggestion_campaign_month || currentMonth,
            };
            setCfg(next);
            setOriginal(JSON.stringify(next));
        }).catch(() => toast.error("No se pudo cargar la configuración de la campaña"));
    }, [currentMonth]);

    const loadSuggestions = async (month = "") => {
        setLoadingList(true);
        try {
            const q = month ? `?month=${encodeURIComponent(month)}` : "";
            const { data } = await api.get(`/admin/suggestions${q}`);
            setItems(data || []);
        } catch (e) {
            toast.error("Error al cargar sugerencias");
        } finally { setLoadingList(false); }
    };
    useEffect(() => { loadSuggestions(filterMonth); }, [filterMonth]);

    const isDirty = JSON.stringify(cfg) !== original;
    const save = async () => {
        setSaving(true);
        try {
            await api.put("/settings", cfg);
            setOriginal(JSON.stringify(cfg));
            toast.success("Campaña actualizada");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al guardar");
        } finally { setSaving(false); }
    };

    const removeSuggestion = async (sid) => {
        if (!window.confirm("¿Eliminar esta sugerencia? Si el usuario recibió crédito, se revertirá de su saldo.")) return;
        try {
            await api.delete(`/admin/suggestions/${sid}`);
            toast.success("Sugerencia eliminada");
            loadSuggestions(filterMonth);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al eliminar");
        }
    };

    return (
        <div className="space-y-6" data-testid="suggestion-campaign-admin">
            <div className="tb-card p-6 space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <h3 className="font-display text-2xl mb-1 flex items-center gap-2"><ThumbsUp className="w-5 h-5 text-[var(--tb-gold)]" /> Campaña de Sugerencias</h3>
                        <p className="text-xs text-[var(--tb-text-2)]">Una vez al mes, tus usuarios pueden dejar una sugerencia y reciben un crédito acumulable en su saldo. Aplicable a cualquier producto y combinable con cupones.</p>
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={cfg.suggestion_campaign_active}
                            onChange={(e) => setCfg({ ...cfg, suggestion_campaign_active: e.target.checked })}
                            data-testid="campaign-active-toggle" />
                        <span className={`text-sm ${cfg.suggestion_campaign_active ? "text-[var(--tb-gold)]" : "text-[var(--tb-text-2)]"}`}>
                            {cfg.suggestion_campaign_active ? "Campaña activa" : "Desactivada"}
                        </span>
                    </label>
                </div>
                <div className="grid sm:grid-cols-3 gap-3">
                    <div>
                        <label className="tb-overline">Crédito por sugerencia (USD)</label>
                        <input type="number" step="0.01" min="0" className="tb-input" value={cfg.suggestion_credit_amount}
                            onChange={(e) => setCfg({ ...cfg, suggestion_credit_amount: parseFloat(e.target.value || 0) })}
                            data-testid="campaign-credit-amount" />
                    </div>
                    <div>
                        <label className="tb-overline">Mes de la campaña (YYYY-MM)</label>
                        <input type="text" className="tb-input" placeholder={currentMonth} value={cfg.suggestion_campaign_month}
                            onChange={(e) => setCfg({ ...cfg, suggestion_campaign_month: e.target.value })}
                            data-testid="campaign-month" />
                        <div className="text-[10px] text-[var(--tb-muted)] mt-1">Cambia el valor para iniciar un nuevo ciclo (los usuarios que ya participaron del mes anterior podrán volver a hacerlo).</div>
                    </div>
                    <div>
                        <label className="tb-overline">Texto del botón</label>
                        <input type="text" className="tb-input" value={cfg.suggestion_campaign_button_text}
                            onChange={(e) => setCfg({ ...cfg, suggestion_campaign_button_text: e.target.value })}
                            data-testid="campaign-button-text" />
                    </div>
                </div>
                <div>
                    <label className="tb-overline">Título (banner + popup)</label>
                    <input type="text" className="tb-input" placeholder="💡 Comparte tu sugerencia y recibe $1 de crédito"
                        value={cfg.suggestion_campaign_title}
                        onChange={(e) => setCfg({ ...cfg, suggestion_campaign_title: e.target.value })}
                        data-testid="campaign-title" />
                </div>
                <div>
                    <label className="tb-overline">Mensaje descriptivo</label>
                    <textarea className="tb-input min-h-[80px]" placeholder="Una vez al mes queremos escuchar cómo mejorar Terra Boutique..."
                        value={cfg.suggestion_campaign_message}
                        onChange={(e) => setCfg({ ...cfg, suggestion_campaign_message: e.target.value })}
                        data-testid="campaign-message" />
                </div>
                <div className="flex items-center gap-3">
                    <button type="button" onClick={save} disabled={!isDirty || saving}
                        className="tb-btn-primary inline-flex items-center gap-1 disabled:opacity-50"
                        data-testid="campaign-save">
                        <Save className="w-4 h-4" /> {saving ? "Guardando..." : (isDirty ? "Guardar cambios" : "Sin cambios")}
                    </button>
                </div>
            </div>

            <div className="tb-card p-6 space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <h3 className="font-display text-xl">Sugerencias recibidas ({items.length})</h3>
                    <div className="flex items-center gap-2">
                        <input type="text" className="tb-input text-xs" placeholder="Filtrar por mes (YYYY-MM)"
                            value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)}
                            data-testid="suggestions-filter-month" />
                        <button type="button" onClick={() => loadSuggestions(filterMonth)}
                            className="tb-btn-secondary text-xs inline-flex items-center gap-1" data-testid="suggestions-refresh">
                            <RefreshCw className="w-3 h-3" /> Recargar
                        </button>
                    </div>
                </div>
                {loadingList ? (
                    <div className="text-center py-6 text-sm text-[var(--tb-text-2)]">Cargando...</div>
                ) : items.length === 0 ? (
                    <div className="text-center py-6 text-sm text-[var(--tb-muted)]">Sin sugerencias para este filtro</div>
                ) : (
                    <div className="space-y-2">
                        {items.map((s) => (
                            <div key={s._id} className="border border-[var(--tb-border)] p-3 rounded space-y-1" data-testid={`suggestion-row-${s._id}`}>
                                <div className="flex items-center justify-between gap-3 text-xs">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-medium">{s.user_name}</span>
                                        <span className="text-[var(--tb-muted)]">{s.user_email}</span>
                                        <span className="tb-badge tb-badge-muted">{s.country}</span>
                                        <span className="tb-badge tb-badge-muted">{s.campaign_month}</span>
                                        {s.credited && <span className="tb-badge tb-badge-success">+${(s.credit_amount || 0).toFixed(2)} acreditado</span>}
                                    </div>
                                    <button type="button" onClick={() => removeSuggestion(s._id)}
                                        className="text-[var(--tb-text-2)] hover:text-red-400" data-testid={`suggestion-delete-${s._id}`}>
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                                <div className="text-sm">{s.message}</div>
                                <div className="text-[10px] text-[var(--tb-muted)]">{new Date(s.created_at).toLocaleString()}</div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

// ---------- Launch / Maintenance control ----------
function LaunchControl() {
    const [cfg, setCfg] = useState({
        site_mode: "live",
        launch_date: "",
        launch_title: "",
        launch_message: "",
        maintenance_message: "",
    });
    const [original, setOriginal] = useState("");
    const [saving, setSaving] = useState(false);
    const [pwModal, setPwModal] = useState(null); // { targetMode }
    const [pwInput, setPwInput] = useState("");
    const [pwBusy, setPwBusy] = useState(false);

    useEffect(() => {
        api.get("/admin/settings").then(({ data }) => {
            const next = {
                site_mode: (data.site_mode || "live").toLowerCase(),
                launch_date: data.launch_date || "",
                launch_title: data.launch_title || "🚀 Próximo lanzamiento de Terra Boutique",
                launch_message: data.launch_message || "Estamos preparando algo especial. Vuelve pronto.",
                maintenance_message: data.maintenance_message || "Estamos actualizando el sitio. Volvemos en unos minutos.",
            };
            setCfg(next);
            setOriginal(JSON.stringify(next));
        }).catch(() => toast.error("No se pudo cargar la configuración"));
    }, []);

    const isDirty = JSON.stringify(cfg) !== original;
    const saveTexts = async () => {
        setSaving(true);
        try {
            await api.put("/settings", {
                launch_date: cfg.launch_date,
                launch_title: cfg.launch_title,
                launch_message: cfg.launch_message,
                maintenance_message: cfg.maintenance_message,
            });
            setOriginal(JSON.stringify(cfg));
            toast.success("Textos guardados");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al guardar");
        } finally { setSaving(false); }
    };

    const requestModeChange = (targetMode) => {
        if (targetMode === cfg.site_mode) return;
        setPwInput("");
        setPwModal({ targetMode });
    };

    const confirmModeChange = async () => {
        if (!pwModal) return;
        if (!pwInput) { toast.error("Ingresa tu contraseña"); return; }
        setPwBusy(true);
        try {
            await api.post("/admin/site-mode", { mode: pwModal.targetMode, password: pwInput });
            setCfg((c) => ({ ...c, site_mode: pwModal.targetMode }));
            const next = { ...cfg, site_mode: pwModal.targetMode };
            setOriginal(JSON.stringify(next));
            toast.success(
                pwModal.targetMode === "live" ? "🚀 Sitio LIVE — compras habilitadas" :
                pwModal.targetMode === "prelaunch" ? "Modo pre-lanzamiento activado (compras bloqueadas)" :
                "Modo mantenimiento activado (sitio bloqueado)"
            );
            setPwModal(null);
            setPwInput("");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al cambiar modo");
        } finally { setPwBusy(false); }
    };

    const modeStyle = {
        live: { label: "🚀 LIVE", color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/40" },
        prelaunch: { label: "⏳ Pre-lanzamiento", color: "text-[var(--tb-gold)]", bg: "bg-[var(--tb-border-gold)]/15 border-[var(--tb-border-gold)]/40" },
        maintenance: { label: "🔧 Mantenimiento", color: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/40" },
    };
    const m = modeStyle[cfg.site_mode] || modeStyle.live;

    return (
        <div className="space-y-6" data-testid="launch-control">
            <div className={`tb-card p-6 space-y-4 border ${m.bg}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                        <h3 className="font-display text-2xl flex items-center gap-2"><Rocket className="w-5 h-5 text-[var(--tb-gold)]" /> Control de Lanzamiento</h3>
                        <p className="text-xs text-[var(--tb-text-2)] mt-1">Modo actual: <strong className={m.color}>{m.label}</strong>. Solo admins pueden comprar mientras el sitio no esté LIVE.</p>
                    </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <button onClick={() => requestModeChange("live")}
                        disabled={cfg.site_mode === "live"}
                        className={`tb-card p-4 text-left border-2 transition ${cfg.site_mode === "live" ? "border-emerald-400 bg-emerald-500/10 cursor-default" : "border-[var(--tb-border)] hover:border-emerald-400/60"}`}
                        data-testid="mode-live-btn">
                        <Unlock className="w-5 h-5 text-emerald-400 mb-2" />
                        <div className="font-semibold text-sm">🚀 LIVE</div>
                        <div className="text-[10px] text-[var(--tb-text-2)] mt-1">Sitio abierto al público. Compras habilitadas.</div>
                    </button>
                    <button onClick={() => requestModeChange("prelaunch")}
                        disabled={cfg.site_mode === "prelaunch"}
                        className={`tb-card p-4 text-left border-2 transition ${cfg.site_mode === "prelaunch" ? "border-[var(--tb-gold)] bg-[var(--tb-border-gold)]/15 cursor-default" : "border-[var(--tb-border)] hover:border-[var(--tb-gold)]/60"}`}
                        data-testid="mode-prelaunch-btn">
                        <Clock className="w-5 h-5 text-[var(--tb-gold)] mb-2" />
                        <div className="font-semibold text-sm">⏳ Pre-lanzamiento</div>
                        <div className="text-[10px] text-[var(--tb-text-2)] mt-1">Visitantes navegan + cronómetro en home. Compras bloqueadas.</div>
                    </button>
                    <button onClick={() => requestModeChange("maintenance")}
                        disabled={cfg.site_mode === "maintenance"}
                        className={`tb-card p-4 text-left border-2 transition ${cfg.site_mode === "maintenance" ? "border-amber-400 bg-amber-500/10 cursor-default" : "border-[var(--tb-border)] hover:border-amber-400/60"}`}
                        data-testid="mode-maintenance-btn">
                        <Wrench className="w-5 h-5 text-amber-400 mb-2" />
                        <div className="font-semibold text-sm">🔧 Mantenimiento</div>
                        <div className="text-[10px] text-[var(--tb-text-2)] mt-1">Sitio bloqueado completo (excepto admins). Útil para actualizaciones.</div>
                    </button>
                </div>
                <p className="text-[11px] text-[var(--tb-muted)]">Cada cambio de modo requiere tu contraseña por seguridad.</p>
            </div>

            <div className="tb-card p-6 space-y-4">
                <h3 className="font-display text-xl flex items-center gap-2"><Clock className="w-5 h-5 text-[var(--tb-gold)]" /> Cronómetro de Lanzamiento</h3>
                <div>
                    <label className="tb-overline">Fecha y hora exacta del lanzamiento</label>
                    <input type="datetime-local" className="tb-input" value={cfg.launch_date ? cfg.launch_date.slice(0, 16) : ""}
                        onChange={(e) => setCfg({ ...cfg, launch_date: e.target.value ? new Date(e.target.value).toISOString() : "" })}
                        data-testid="launch-date" />
                    <div className="text-[10px] text-[var(--tb-muted)] mt-1">Se muestra como cuenta atrás en el banner del home cuando el modo es Pre-lanzamiento.</div>
                </div>
                <div>
                    <label className="tb-overline">Título del banner</label>
                    <input className="tb-input" placeholder="🚀 Próximo lanzamiento de Terra Boutique"
                        value={cfg.launch_title}
                        onChange={(e) => setCfg({ ...cfg, launch_title: e.target.value })}
                        data-testid="launch-title" />
                </div>
                <div>
                    <label className="tb-overline">Mensaje del banner</label>
                    <textarea className="tb-input min-h-[70px]" placeholder="Estamos preparando algo especial..."
                        value={cfg.launch_message}
                        onChange={(e) => setCfg({ ...cfg, launch_message: e.target.value })}
                        data-testid="launch-message" />
                </div>
                <div className="pt-3 border-t border-[var(--tb-border)]">
                    <h3 className="font-display text-xl flex items-center gap-2 mb-3"><Wrench className="w-5 h-5 text-amber-400" /> Mensaje de mantenimiento</h3>
                    <textarea className="tb-input min-h-[70px]" placeholder="Estamos actualizando el sitio. Volvemos en unos minutos."
                        value={cfg.maintenance_message}
                        onChange={(e) => setCfg({ ...cfg, maintenance_message: e.target.value })}
                        data-testid="maintenance-message" />
                    <div className="text-[10px] text-[var(--tb-muted)] mt-1">Aparece en pantalla completa cuando el modo es Mantenimiento.</div>
                </div>
                <button onClick={saveTexts} disabled={!isDirty || saving}
                    className="tb-btn-primary inline-flex items-center gap-2 disabled:opacity-50" data-testid="launch-save">
                    <Save className="w-4 h-4" /> {saving ? "Guardando..." : (isDirty ? "Guardar textos" : "Sin cambios")}
                </button>
            </div>

            {/* Password confirmation modal */}
            {pwModal && (
                <div className="fixed inset-0 bg-black/80 z-50 flex items-start md:items-center justify-center p-4 overflow-y-auto" data-testid="mode-pw-modal">
                    <div className="tb-card max-w-md w-full p-6 space-y-4">
                        <div className="flex items-center gap-3">
                            <Lock className="w-7 h-7 text-[var(--tb-gold)] flex-shrink-0" />
                            <div>
                                <h3 className="font-display text-xl">Confirmar cambio de modo</h3>
                                <p className="text-xs text-[var(--tb-text-2)] mt-1">
                                    Cambiarás a <strong className={modeStyle[pwModal.targetMode].color}>{modeStyle[pwModal.targetMode].label}</strong>. Ingresa tu contraseña de administrador para continuar.
                                </p>
                            </div>
                        </div>
                        <input type="password" className="tb-input" placeholder="Contraseña de admin"
                            value={pwInput} onChange={(e) => setPwInput(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && confirmModeChange()}
                            autoFocus data-testid="mode-pw-input" />
                        <div className="flex gap-2 justify-end">
                            <button onClick={() => setPwModal(null)} className="tb-btn-secondary !text-xs" data-testid="mode-pw-cancel">Cancelar</button>
                            <button onClick={confirmModeChange} disabled={pwBusy || !pwInput} className="tb-btn-primary !text-xs disabled:opacity-50" data-testid="mode-pw-confirm">
                                {pwBusy ? "Verificando..." : "Confirmar"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}


// ---------- Product gallery (extra photos) ----------
function ProductGalleryEditor({ editing, setEditing }) {
    const images = Array.isArray(editing.images) ? editing.images : [];
    const addImages = (files) => {
        if (!files || !files.length) return;
        const arr = Array.from(files);
        let pending = arr.length;
        arr.forEach((file) => {
            if (!file.type.startsWith("image/") || file.size > 5 * 1024 * 1024) {
                toast.error(`${file.name}: solo imágenes < 5MB`);
                pending--;
                return;
            }
            const reader = new FileReader();
            const img = new Image();
            reader.onload = (ev) => {
                img.onload = () => {
                    const canvas = document.createElement("canvas");
                    const max = 1200;
                    const scale = Math.min(1, max / Math.max(img.width, img.height));
                    canvas.width = Math.round(img.width * scale);
                    canvas.height = Math.round(img.height * scale);
                    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
                    setEditing((cur) => ({ ...cur, images: [...(cur.images || []), canvas.toDataURL("image/jpeg", 0.85)] }));
                };
                img.src = ev.target.result;
            };
            reader.readAsDataURL(file);
        });
    };
    const removeAt = (idx) => setEditing({ ...editing, images: images.filter((_, i) => i !== idx) });
    return (
        <div className="col-span-2">
            <span className="tb-overline text-[var(--tb-gold)]">Galería adicional ({images.length})</span>
            <p className="text-[10px] text-[var(--tb-muted)] mt-0.5 mb-2">Sube fotos extras opcionales. La &ldquo;Foto principal&rdquo; se mantiene como miniatura en el catálogo.</p>
            <div className="flex flex-wrap gap-2 items-start">
                {images.map((src, idx) => (
                    <div key={idx} className="relative w-20 h-20 rounded border border-[var(--tb-border)] overflow-hidden" data-testid={`gallery-img-${idx}`}>
                        <img src={src} alt="" className="w-full h-full object-cover" />
                        <button type="button" onClick={() => removeAt(idx)}
                            className="absolute top-0.5 right-0.5 bg-rose-500/90 hover:bg-rose-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px]"
                            data-testid={`gallery-remove-${idx}`}>×</button>
                    </div>
                ))}
                <label className="w-20 h-20 rounded border border-dashed border-[var(--tb-border-gold)]/40 flex items-center justify-center cursor-pointer hover:bg-[var(--tb-border-gold)]/10 transition" data-testid="gallery-add">
                    <Plus className="w-4 h-4 text-[var(--tb-gold)]" />
                    <input type="file" accept="image/*" multiple className="hidden"
                        onChange={(e) => { addImages(e.target.files); e.target.value = ""; }} />
                </label>
            </div>
        </div>
    );
}

// ---------- Product videos ----------
function ProductVideosEditor({ editing, setEditing }) {
    const videos = Array.isArray(editing.videos) ? editing.videos : [];
    const [uploading, setUploading] = useState(false);
    const addVideos = async (files) => {
        if (!files || !files.length) return;
        const arr = Array.from(files);
        setUploading(true);
        for (const file of arr) {
            if (!file.type.startsWith("video/")) { toast.error(`${file.name}: solo videos`); continue; }
            if (file.size > 50 * 1024 * 1024) { toast.error(`${file.name}: máx 50MB`); continue; }
            try {
                const fd = new FormData();
                fd.append("file", file);
                const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                const url = data.url || data.path;
                setEditing((cur) => ({ ...cur, videos: [...(cur.videos || []), url] }));
            } catch (e) {
                toast.error(`Error subiendo ${file.name}`);
            }
        }
        setUploading(false);
    };
    const removeAt = (idx) => setEditing({ ...editing, videos: videos.filter((_, i) => i !== idx) });
    return (
        <div className="col-span-2">
            <span className="tb-overline text-[var(--tb-gold)]">Videos ({videos.length})</span>
            <p className="text-[10px] text-[var(--tb-muted)] mt-0.5 mb-2">Opcional. Sube videos cortos del producto (MP4/WebM/MOV, máx 50MB cada uno).</p>
            <div className="flex flex-wrap gap-2 items-start">
                {videos.map((src, idx) => (
                    <div key={idx} className="relative w-32 h-20 rounded border border-[var(--tb-border)] overflow-hidden bg-black flex items-center justify-center" data-testid={`video-${idx}`}>
                        <video src={src.startsWith("/api/") ? src : src} className="w-full h-full object-cover" muted playsInline />
                        <button type="button" onClick={() => removeAt(idx)}
                            className="absolute top-0.5 right-0.5 bg-rose-500/90 hover:bg-rose-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px]"
                            data-testid={`video-remove-${idx}`}>×</button>
                    </div>
                ))}
                <label className={`w-32 h-20 rounded border border-dashed border-[var(--tb-border-gold)]/40 flex items-center justify-center cursor-pointer transition ${uploading ? "opacity-50" : "hover:bg-[var(--tb-border-gold)]/10"}`} data-testid="video-add">
                    {uploading ? (
                        <RefreshCw className="w-4 h-4 text-[var(--tb-gold)] animate-spin" />
                    ) : (
                        <>
                            <Plus className="w-4 h-4 text-[var(--tb-gold)] mr-1" />
                            <span className="text-[10px] text-[var(--tb-gold)]">Video</span>
                        </>
                    )}
                    <input type="file" accept="video/*" multiple className="hidden" disabled={uploading}
                        onChange={(e) => { addVideos(e.target.files); e.target.value = ""; }} />
                </label>
            </div>
        </div>
    );
}


// ---------- Admin Returns Panel (used in both Envios SV + Etiquetas USA) ----------
function AdminReturnsPanel({ country }) {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [loading, setLoading] = useState(false);
    const [pwModal, setPwModal] = useState(null); // {return}
    const [pwInput, setPwInput] = useState("");
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/returns?country=${country}`);
            setItems(data || []);
        } catch { /* ignore */ }
        finally { setLoading(false); }
    }, [country]);
    useEffect(() => { load(); }, [load]);

    const save = async () => {
        if (!editing) return;
        try {
            await api.patch(`/admin/returns/${editing._id}`, {
                reason: editing.reason, items_description: editing.items_description,
                customer_phone: editing.customer_phone, address_street: editing.address_street,
                address_apt: editing.address_apt, address_city: editing.address_city,
                address_state: editing.address_state, address_zip: editing.address_zip,
                departamento_id: editing.departamento_id ? Number(editing.departamento_id) : null,
                municipio_id: editing.municipio_id ? Number(editing.municipio_id) : null,
                address_reference: editing.address_reference, admin_notes: editing.admin_notes,
                status: editing.status,
            });
            toast.success("Guardado");
            setEditing(null); load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error"); }
    };

    const generateLabel = async () => {
        if (!pwModal) return;
        if (!pwInput) { toast.error("Ingresa tu contraseña"); return; }
        setBusy(true);
        try {
            const { data } = await api.post(`/admin/returns/${pwModal._id}/generate-label`, { password: pwInput });
            toast.success(`Etiqueta generada — Tracking ${data.tracking_number}`);
            setPwModal(null); setPwInput(""); load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error generando etiqueta"); }
        finally { setBusy(false); }
    };

    const emailLabel = async (r) => {
        try {
            const { data } = await api.post(`/admin/returns/${r._id}/email-label`);
            toast.success(`Email enviado a ${data.sent_to}`);
            load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error enviando email"); }
    };

    return (
        <div className="tb-card p-5 space-y-4" data-testid={`admin-returns-${country}`}>
            <div className="flex items-center justify-between flex-wrap gap-2">
                <h3 className="font-display text-xl flex items-center gap-2"><Package className="w-5 h-5 text-[var(--tb-gold)]" /> Devoluciones {country === "SV" ? "🇸🇻" : "🇺🇸"} ({items.length})</h3>
                <button onClick={load} className="tb-btn-secondary text-xs inline-flex items-center gap-1">
                    <RefreshCw className="w-3 h-3" /> Recargar
                </button>
            </div>
            {loading ? <div className="text-xs text-[var(--tb-muted)]">Cargando...</div> :
                items.length === 0 ? <div className="text-xs text-[var(--tb-muted)] py-4 text-center">Sin solicitudes de devolución</div> : (
                    <div className="space-y-2">
                        {items.map((r) => (
                            <div key={r._id} className="border border-[var(--tb-border)] rounded p-3 space-y-1" data-testid={`ret-row-${r._id}`}>
                                <div className="flex items-center gap-2 flex-wrap text-xs">
                                    <span className="font-mono">{r._id}</span>
                                    <span className="tb-badge tb-badge-muted">{r.status}</span>
                                    <span className="text-[var(--tb-muted)]">Pedido #{(r.order_id || "").slice(-8)}</span>
                                    <span className="text-[var(--tb-muted)]">{r.user_email}</span>
                                    {r.tracking_number && <span className="text-emerald-400">Tracking: {r.tracking_number}</span>}
                                </div>
                                <div className="text-sm line-clamp-2">{r.reason}</div>
                                <div className="text-[10px] text-[var(--tb-text-2)]">
                                    {r.address_street}{r.address_apt ? `, ${r.address_apt}` : ""}
                                    {country === "US" ? `, ${r.address_city || ""}, ${r.address_state || ""} ${r.address_zip || ""}` : ""}
                                </div>
                                <div className="flex gap-2 flex-wrap pt-1">
                                    <button onClick={() => setEditing({ ...r })} className="text-xs tb-link inline-flex items-center gap-1" data-testid={`ret-edit-${r._id}`}>
                                        <Edit3 className="w-3 h-3" /> Editar
                                    </button>
                                    {!r.tracking_number && (
                                        <button onClick={() => { setPwInput(""); setPwModal(r); }}
                                            className="text-xs bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25 rounded px-2 py-0.5 inline-flex items-center gap-1"
                                            data-testid={`ret-genlabel-${r._id}`}>
                                            <Printer className="w-3 h-3" /> Generar etiqueta
                                        </button>
                                    )}
                                    {r.tracking_number && r.status !== "enviada_cliente" && r.status !== "completada" && (
                                        <button onClick={() => emailLabel(r)}
                                            className="text-xs bg-blue-500/15 border border-blue-500/40 text-blue-300 hover:bg-blue-500/25 rounded px-2 py-0.5 inline-flex items-center gap-1"
                                            data-testid={`ret-email-${r._id}`}>
                                            <Mail className="w-3 h-3" /> Enviar al cliente
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

            {editing && (
                <div className="fixed inset-0 bg-black/80 z-50 flex items-start md:items-center justify-center p-4 overflow-y-auto">
                    <div className="tb-card max-w-xl w-full p-5 space-y-3 my-4">
                        <h3 className="font-display text-xl">Editar devolución {editing._id}</h3>
                        <div>
                            <label className="tb-overline">Razón</label>
                            <textarea className="tb-input min-h-[60px]" value={editing.reason || ""} onChange={(e) => setEditing({ ...editing, reason: e.target.value })} />
                        </div>
                        <div>
                            <label className="tb-overline">Teléfono del cliente</label>
                            <input className="tb-input" value={editing.customer_phone || ""} onChange={(e) => setEditing({ ...editing, customer_phone: e.target.value })} />
                        </div>
                        <div>
                            <label className="tb-overline">Calle</label>
                            <input className="tb-input" value={editing.address_street || ""} onChange={(e) => setEditing({ ...editing, address_street: e.target.value })} />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="tb-overline">{country === "US" ? "Apt/Suite" : "Referencia"}</label>
                                <input className="tb-input" value={country === "US" ? (editing.address_apt || "") : (editing.address_reference || "")} onChange={(e) => setEditing(country === "US" ? { ...editing, address_apt: e.target.value } : { ...editing, address_reference: e.target.value })} />
                            </div>
                            <div>
                                <label className="tb-overline">Ciudad</label>
                                <input className="tb-input" value={editing.address_city || ""} onChange={(e) => setEditing({ ...editing, address_city: e.target.value })} />
                            </div>
                        </div>
                        {country === "US" && (
                            <div className="grid grid-cols-2 gap-2">
                                <div><label className="tb-overline">Estado (2 letras)</label><input className="tb-input uppercase" maxLength={2} value={editing.address_state || ""} onChange={(e) => setEditing({ ...editing, address_state: e.target.value.toUpperCase() })} /></div>
                                <div><label className="tb-overline">ZIP</label><input className="tb-input" value={editing.address_zip || ""} onChange={(e) => setEditing({ ...editing, address_zip: e.target.value })} /></div>
                            </div>
                        )}
                        {country === "SV" && (
                            <div className="grid grid-cols-2 gap-2">
                                <div><label className="tb-overline">Departamento ID</label><input type="number" className="tb-input" value={editing.departamento_id || ""} onChange={(e) => setEditing({ ...editing, departamento_id: e.target.value })} /></div>
                                <div><label className="tb-overline">Municipio ID</label><input type="number" className="tb-input" value={editing.municipio_id || ""} onChange={(e) => setEditing({ ...editing, municipio_id: e.target.value })} /></div>
                            </div>
                        )}
                        <div>
                            <label className="tb-overline">Notas internas</label>
                            <textarea className="tb-input min-h-[50px]" value={editing.admin_notes || ""} onChange={(e) => setEditing({ ...editing, admin_notes: e.target.value })} />
                        </div>
                        <div>
                            <label className="tb-overline">Estado</label>
                            <select className="tb-input" value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })}>
                                <option value="pendiente_revision">Pendiente de revisión</option>
                                <option value="aprobada">Aprobada</option>
                                <option value="etiqueta_generada">Etiqueta generada</option>
                                <option value="enviada_cliente">Enviada al cliente</option>
                                <option value="completada">Completada</option>
                                <option value="rechazada">Rechazada</option>
                            </select>
                        </div>
                        <div className="flex gap-2 justify-end">
                            <button onClick={() => setEditing(null)} className="tb-btn-secondary !text-xs">Cancelar</button>
                            <button onClick={save} className="tb-btn-primary !text-xs">Guardar</button>
                        </div>
                    </div>
                </div>
            )}

            {pwModal && (
                <div className="fixed inset-0 bg-black/80 z-50 flex items-start md:items-center justify-center p-4 overflow-y-auto">
                    <div className="tb-card max-w-md w-full p-5 space-y-3 my-4">
                        <h3 className="font-display text-xl flex items-center gap-2"><Printer className="w-5 h-5 text-[var(--tb-gold)]" /> Generar etiqueta de devolución</h3>
                        <p className="text-xs text-[var(--tb-text-2)]">Devolución <strong>{pwModal._id}</strong>. {country === "US" ? "ShipStation" : "C807"} generará la etiqueta del cliente hacia tu dirección de origen configurada.</p>
                        <p className="text-[10px] text-amber-400">Ingresa tu contraseña de admin para confirmar.</p>
                        <input type="password" className="tb-input" autoFocus placeholder="Contraseña"
                            value={pwInput} onChange={(e) => setPwInput(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && generateLabel()} />
                        <div className="flex gap-2 justify-end">
                            <button onClick={() => setPwModal(null)} className="tb-btn-secondary !text-xs">Cancelar</button>
                            <button onClick={generateLabel} disabled={busy || !pwInput} className="tb-btn-primary !text-xs disabled:opacity-50">
                                {busy ? "Generando..." : "Confirmar y generar"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}







